import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { FaSearch, FaCopy, FaChevronLeft, FaChevronRight, FaChevronDown, FaChevronUp, FaFilter, FaTimes, FaSort, FaSortUp, FaSortDown, FaClock, FaChartLine, FaUserClock, FaExclamationTriangle, FaFire, FaUsers, FaSyncAlt, FaStar, FaShoppingCart, FaBolt, FaArrowRight, FaStore, FaTrophy, FaWallet, FaBell, FaHourglassHalf, FaMobileAlt, FaEye, FaTag, FaGift, FaHeart, FaUserFriends, FaCrown, FaUserPlus, FaUndo, FaBoxOpen } from 'react-icons/fa';
import { getCustomers, getAllCustomerMobileNumbers, getAbandonedCarts, getCustomerBehaviorInsights, getLiveCustomerPresence, getTodayCustomerSessionHistory, getBranchPerformanceInsights, getFreeCashAnalytics, getFreeCashNotifiedNoUsage, getInstoreSalesAnalytics, getInstoreCustomerAnalytics, searchInstorePosCustomers, getInstorePosCustomerProfile, getDeadProductsNoPurchase, getBranchNamesForUser, getCustomerBehaviorTimeline, getAddressById } from '../services/ClientService';
import socketService from '../services/socketService';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  FunnelChart,
  Funnel,
  LabelList,
} from 'recharts';

// Format date as '27 Nov 2025 09:12:23 AM' (DD MMM YYYY hh:mm:ss AM/PM)
const formatDate = (date) => {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();

  // Time (12-hour format with AM/PM)
  const hours24 = d.getHours();
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const hh = String(hours12).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const ampm = hours24 >= 12 ? 'PM' : 'AM';

  return `${day} ${month} ${year} ${hh}:${mm}:${ss} ${ampm}`;
};

// Format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹0';
  return `₹${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const deadOptionAccordionKey = (productId, optionId) => `${productId}::${optionId}`;

/** Option labels can arrive as a string or as a { weightLabel, weightValue, metricLabel } object. */
const safeOptionLabel = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object') {
    const label = value.weightLabel || value.label;
    if (label) return String(label).trim();
    if (value.weightValue != null) {
      return `${value.weightValue}${value.metricLabel ? ` ${value.metricLabel}` : ''}`.trim();
    }
    return '';
  }
  return String(value);
};

/** Initial load 10, then +20, +30, +40… per "Load more" click. */
const getDeadProductsBatchSize = (loadPage) => {
  const page = Math.max(0, loadPage);
  // First paint: 9 cards; then progressive 20, 30, 40…
  if (page === 0) return 9;
  return 10 * (page + 1);
};

const DeadProductCard = ({
  product,
  deadProductsAllTime,
  accordionOpen,
  onToggleAccordion,
}) => {
  const deadOptions = product.deadOptions || [];
  const categoryName = product.categoryName || product.category || 'Uncategorized';
  const subCategoryName = product.subCategoryName || null;

  return (
    <div className="h-full flex flex-col rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50/80 p-4 hover:shadow-md hover:border-violet-100 transition">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2" title={product.productName}>
            {product.productName}
          </p>
          {product.sku && (
            <p className="text-[11px] text-gray-500 mt-0.5">SKU {product.sku}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-1 justify-end shrink-0 max-w-[45%]">
          {product.isCommonInventory && (
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
              Common
            </span>
          )}
          {product.deadChannels?.includes('online') && (
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-200">
              Online
            </span>
          )}
          {product.deadChannels?.includes('offline') && (
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
              Offline
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1 mb-3 text-[11px] text-gray-600 min-h-[18px]">
        <FaTag className="text-violet-400 text-[10px] shrink-0" />
        <span className="truncate">{categoryName}</span>
        {subCategoryName && (
          <>
            <FaChevronRight className="text-[8px] text-gray-300 shrink-0" />
            <span className="text-violet-700 font-medium truncate">{subCategoryName}</span>
          </>
        )}
      </div>

      <div className="flex-1 space-y-1.5">
        {deadOptions.length === 0 ? (
          <p className="text-xs text-gray-500 text-center py-3">No dead options</p>
        ) : (
          deadOptions.map((opt, index) => {
            const accKey = deadOptionAccordionKey(product.productId, opt.optionId);
            const isOpen = accordionOpen[accKey] !== undefined ? accordionOpen[accKey] : index === 0;
            return (
              <div
                key={opt.optionId}
                className={`rounded-lg border overflow-hidden ${
                  isOpen ? 'border-violet-100 bg-white' : 'border-gray-100 bg-white/70'
                }`}
              >
                <button
                  type="button"
                  onClick={() => onToggleAccordion(accKey, isOpen)}
                  className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-violet-50/40 transition"
                >
                  <p className="text-xs font-bold text-violet-900 truncate flex-1" title={opt.optionLabel}>
                    {opt.optionLabel}
                  </p>
                  <div className="flex items-center gap-1 shrink-0">
                    {opt.deadChannels?.includes('online') && (
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800">On</span>
                    )}
                    {opt.deadChannels?.includes('offline') && (
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Off</span>
                    )}
                    {isOpen ? (
                      <FaChevronUp className="text-gray-400 text-[10px]" />
                    ) : (
                      <FaChevronDown className="text-gray-400 text-[10px]" />
                    )}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-2.5 pb-2.5 pt-0 border-t border-gray-50">
                    <div className="grid grid-cols-2 gap-1.5 text-[11px] mt-2">
                      <div className="rounded-md bg-gray-50 border border-gray-100 px-2 py-1.5">
                        <p className="text-[9px] uppercase text-gray-400 font-semibold">Stock</p>
                        <p className="font-semibold text-gray-800">{opt.stockLabel || `${Number(opt.availableQuantity || 0).toLocaleString('en-IN')} units`}</p>
                      </div>
                      <div className="rounded-md bg-gray-50 border border-gray-100 px-2 py-1.5">
                        <p className="text-[9px] uppercase text-gray-400 font-semibold">Value</p>
                        <p className="font-semibold text-gray-800">{formatCurrency(opt.stockValue || 0)}</p>
                      </div>
                      <div className="rounded-md bg-gray-50 border border-gray-100 px-2 py-1.5">
                        <p className="text-[9px] uppercase text-gray-400 font-semibold">{deadProductsAllTime ? 'Online' : 'Online (win)'}</p>
                        <p className="font-semibold text-sky-700">{opt.windowOnlineUnits || 0} sold</p>
                      </div>
                      <div className="rounded-md bg-gray-50 border border-gray-100 px-2 py-1.5">
                        <p className="text-[9px] uppercase text-gray-400 font-semibold">{deadProductsAllTime ? 'Offline' : 'Offline (win)'}</p>
                        <p className="font-semibold text-emerald-700">{opt.windowOfflineUnits || 0} sold</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between text-[11px]">
        <span className="text-gray-500">{product.deadOptionCount || deadOptions.length} dead option(s)</span>
        <span className="font-semibold text-gray-800">{formatCurrency(product.stockValue || 0)} at risk</span>
      </div>
    </div>
  );
};

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined || Number.isNaN(Number(minutes))) return '—';
  const totalMinutes = Math.max(0, Math.round(Number(minutes)));
  if (totalMinutes < 60) return `${totalMinutes} Min`;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours} ${hours === 1 ? 'Hour' : 'Hours'}${mins > 0 ? ` ${mins} Min` : ''}`;
};

const formatAddress = (address) => {
  if (!address) return '—';
  if (typeof address === 'string') return address;
  const parts = [address.name, address.house, address.area, address.address, address.city, address.state, address.pincode].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

const CHART_COLORS = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
const DAY_LABELS = { 1: 'Sun', 2: 'Mon', 3: 'Tue', 4: 'Wed', 5: 'Thu', 6: 'Fri', 7: 'Sat' };
const ACTIVITY_TIMEZONE = 'Asia/Kolkata';

const getHourInTimezone = (date, timeZone = ACTIVITY_TIMEZONE) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const hourPart = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', hour12: false })
    .formatToParts(date)
    .find((part) => part.type === 'hour');
  const hour = Number(hourPart?.value);
  return Number.isFinite(hour) ? hour : null;
};

const formatHourLabel = (hour) => {
  const h = Number(hour);
  if (!Number.isFinite(h) || h < 0 || h > 23) return '—';
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(hour12).padStart(2, '0')} ${period}`;
};

const formatEventTypeLabel = (eventType) => (
  String(eventType || 'unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const PRODUCT_HEALTH_TIER_META = {
  star: { label: 'Star', chip: 'bg-emerald-100 text-emerald-800 border-emerald-200', bar: 'bg-emerald-500', icon: FaStar },
  rising: { label: 'Rising', chip: 'bg-sky-100 text-sky-800 border-sky-200', bar: 'bg-sky-500', icon: FaBolt },
  cart_stuck: { label: 'Cart Stuck', chip: 'bg-amber-100 text-amber-800 border-amber-200', bar: 'bg-amber-500', icon: FaShoppingCart },
  high_traffic_low_sale: { label: 'High Traffic · Low Sale', chip: 'bg-orange-100 text-orange-800 border-orange-200', bar: 'bg-orange-500', icon: FaFire },
  at_risk: { label: 'At Risk', chip: 'bg-rose-100 text-rose-800 border-rose-200', bar: 'bg-rose-500', icon: FaExclamationTriangle },
  needs_attention: { label: 'Needs Attention', chip: 'bg-gray-100 text-gray-700 border-gray-200', bar: 'bg-gray-400', icon: FaChartLine },
  low_data: { label: 'Low Data', chip: 'bg-slate-100 text-slate-600 border-slate-200', bar: 'bg-slate-400', icon: FaChartLine },
};

const POS_LOYALTY_SEGMENT_META = {
  vip: { label: 'VIP', chip: 'bg-amber-100 text-amber-900 border-amber-200', icon: FaCrown, hint: '5+ bills or ₹5k+ spend' },
  loyal: { label: 'Loyal', chip: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: FaHeart, hint: '3–4 repeat visits' },
  rising: { label: 'Rising', chip: 'bg-sky-100 text-sky-800 border-sky-200', icon: FaBolt, hint: '2 visits · warming up' },
  oneTimers: { label: 'One-timers', chip: 'bg-slate-100 text-slate-700 border-slate-200', icon: FaUserPlus, hint: 'Single bill — win them back' },
  atRisk: { label: 'At risk', chip: 'bg-rose-100 text-rose-800 border-rose-200', icon: FaExclamationTriangle, hint: '2+ visits, idle 15+ days' },
};

const PRODUCT_HEALTH_TIER_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'star', label: 'Stars' },
  { value: 'rising', label: 'Rising' },
  { value: 'cart_stuck', label: 'Cart Stuck' },
  { value: 'high_traffic_low_sale', label: 'High Traffic' },
  { value: 'at_risk', label: 'At Risk' },
];

const buildProductHealthProfile = (product) => {
  const views = Number(product.views || 0);
  const carts = Number(product.cartAdditions || 0);
  const orders = Number(product.purchases || 0);
  const viewToCartRate = views > 0 ? Number(((carts / views) * 100).toFixed(1)) : 0;
  const cartToPurchaseRate = carts > 0 ? Number(((orders / carts) * 100).toFixed(1)) : 0;
  const conversionScore = views > 0 ? Number(((orders / views) * 100).toFixed(1)) : 0;
  const dropOffs = Math.max(0, views - orders);
  const cartAbandonment = Math.max(0, carts - orders);
  const dropRisk = views > 0 ? Number(((dropOffs / views) * 100).toFixed(1)) : 0;
  const engagementScore = Math.min(100, Number((Math.sqrt(views) * 8 + carts * 3).toFixed(1)));
  const cartEfficiency = Number((viewToCartRate * 0.4 + cartToPurchaseRate * 0.6).toFixed(1));
  const performanceScore = Math.min(100, Math.max(0, Number((
    conversionScore * 0.35
    + cartEfficiency * 0.25
    + engagementScore * 0.2
    + (100 - dropRisk) * 0.2
  ).toFixed(1))));

  let tier = 'needs_attention';
  if (views < 3) tier = 'low_data';
  else if (performanceScore >= 70 && conversionScore >= 5) tier = 'star';
  else if (viewToCartRate >= 15 && cartToPurchaseRate < 25 && carts >= 2) tier = 'cart_stuck';
  else if (views >= 10 && conversionScore < 2) tier = 'high_traffic_low_sale';
  else if (dropRisk >= 85 && views >= 5) tier = 'at_risk';
  else if (performanceScore >= 45) tier = 'rising';

  const actionByTier = {
    star: 'Feature on homepage, bundles, and restock priority.',
    rising: 'Push in banners, notifications, and category highlights.',
    cart_stuck: 'Recover carts with urgency coupon + push reminder.',
    high_traffic_low_sale: 'Improve images, reviews, and pricing on product page.',
    at_risk: 'Fix PDP friction, compare pricing, reduce checkout drop-off.',
    needs_attention: 'Test small discount or reposition in high-traffic sections.',
    low_data: 'Promote via deals/banners to collect more intent signals.',
  };

  return {
    productId: product.productId,
    name: product.productName || 'Unknown Product',
    views,
    carts,
    orders,
    dropOffs,
    cartAbandonment,
    viewToCartRate,
    cartToPurchaseRate,
    conversionScore,
    engagementScore,
    cartEfficiency,
    dropRisk,
    performanceScore,
    tier,
    tierLabel: PRODUCT_HEALTH_TIER_META[tier]?.label || 'Needs Attention',
    action: actionByTier[tier],
    funnel: [
      { stage: 'Views', value: views, fill: '#6366f1' },
      { stage: 'Cart', value: carts, fill: '#0ea5e9' },
      { stage: 'Orders', value: orders, fill: '#10b981' },
    ],
  };
};

const ProductHealthDetailModal = ({ product, windowLabel, onClose }) => {
  if (!product) return null;
  const tierMeta = PRODUCT_HEALTH_TIER_META[product.tier] || PRODUCT_HEALTH_TIER_META.needs_attention;
  const TierIcon = tierMeta.icon;

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-indigo-50 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${tierMeta.chip}`}>
                <TierIcon className="text-[10px]" />
                {product.tierLabel}
              </span>
              <span className="text-[10px] font-semibold text-gray-500">{windowLabel}</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mt-1 line-clamp-2">{product.name}</h3>
            <p className="text-sm font-bold text-indigo-700 mt-1">Health Score {product.performanceScore}/100</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-gray-800 flex items-center justify-center shrink-0" aria-label="Close product health details">
            <FaTimes size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="rounded-xl border border-amber-100 bg-amber-50/70 px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Recommended Action</p>
            <p className="text-sm text-amber-900 mt-1">{product.action}</p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {product.funnel.map((step) => (
              <div key={step.stage} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-center">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{step.stage}</p>
                <p className="text-xl font-bold text-gray-900 mt-0.5">{step.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'View → Cart', value: `${product.viewToCartRate}%`, tone: 'text-sky-700' },
              { label: 'Cart → Order', value: `${product.cartToPurchaseRate}%`, tone: 'text-emerald-700' },
              { label: 'Conversion', value: `${product.conversionScore}%`, tone: 'text-indigo-700' },
              { label: 'Drop Risk', value: `${product.dropRisk}%`, tone: 'text-rose-700' },
              { label: 'Drop-offs', value: product.dropOffs, tone: 'text-rose-700' },
              { label: 'Cart Abandonment', value: product.cartAbandonment, tone: 'text-amber-700' },
            ].map((metric) => (
              <div key={metric.label} className="rounded-lg border border-gray-100 bg-white px-3 py-2">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{metric.label}</p>
                <p className={`text-lg font-bold mt-0.5 ${metric.tone}`}>{metric.value}</p>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            {[
              { label: 'Performance', value: product.performanceScore, color: 'bg-indigo-500' },
              { label: 'Engagement', value: product.engagementScore, color: 'bg-violet-500' },
              { label: 'Cart Efficiency', value: product.cartEfficiency, color: 'bg-sky-500' },
            ].map((bar) => (
              <div key={bar.label}>
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                  <span>{bar.label}</span>
                  <span className="font-semibold">{bar.value}</span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div className={`h-full rounded-full ${bar.color}`} style={{ width: `${Math.min(100, bar.value)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const HOURLY_EVENT_TYPE_COLORS = {
  product_view: 'bg-sky-50 text-sky-700 border-sky-100',
  cart_addition: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  cart_removal: 'bg-rose-50 text-rose-700 border-rose-100',
  purchase: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  search: 'bg-amber-50 text-amber-700 border-amber-100',
  favorite_add: 'bg-violet-50 text-violet-700 border-violet-100',
  favorite_remove: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100',
};

const formatWeightLabel = (weight) => {
  if (weight == null || weight === '') return '';
  if (typeof weight === 'string' || typeof weight === 'number') return String(weight);
  if (typeof weight === 'object') {
    if (weight.weightLabel) return String(weight.weightLabel);
    if (weight.label) return String(weight.label);
    if (weight.weightValue != null && weight.metricLabel) {
      return `${weight.weightValue} ${weight.metricLabel}`;
    }
    if (weight.weight != null) return String(weight.weight);
  }
  return '';
};

const getCartRemovalProductName = (event) => {
  const meta = event?.metadata || {};
  return String(event?.productName || meta.productName || meta.product_name || '').trim();
};

const getCartRemovalItemSummary = (event) => {
  const meta = event?.metadata || {};
  const productName = getCartRemovalProductName(event);
  const parts = [];
  if (productName) parts.push(productName);
  const qty = Number(meta.quantity);
  if (Number.isFinite(qty) && qty > 0) parts.push(`Qty ${qty}`);
  const weightLabel = formatWeightLabel(meta.selectedWeight);
  if (weightLabel) parts.push(weightLabel);
  return parts.join(' · ');
};

const getTimelineEventPresentation = (event) => {
  const eventType = String(event?.eventType || 'unknown');
  if (eventType === 'cart_removal') {
    const productName = getCartRemovalProductName(event);
    return {
      eventType,
      title: 'cart_removal',
      titleClass: 'text-rose-700',
      detail: productName ? `Removed from cart ${productName}` : 'Removed from cart',
      subDetail: getCartRemovalItemSummary(event),
      dotClass: 'bg-rose-500',
    };
  }
  return {
    eventType,
    title: eventType,
    titleClass: 'text-indigo-700',
    detail: event?.productName ? String(event.productName) : '',
    subDetail: '',
    dotClass: 'bg-indigo-500',
  };
};

const GLOBAL_TIMELINE_MAX_DAYS = 30;

const WINDOW_OPTIONS = [
  { value: 'today', label: 'Today', short: 'Today' },
  { value: 'yesterday', label: 'Yesterday', short: 'Yest.' },
  { value: '7', label: 'Last 7 days', short: '7D' },
  { value: '15', label: 'Last 15 days', short: '15D' },
  { value: '30', label: 'Last 30 days', short: '30D' },
];

const CUSTOMERS_TAB_BEHAVIOR_WINDOW = '30';

const buildWindowParams = (windowValue) => {
  if (windowValue === 'today') return { windowKey: 'today', days: 1 };
  if (windowValue === 'yesterday') return { windowKey: 'yesterday', days: 1 };
  const parsed = parseInt(windowValue, 10);
  const days = Number.isFinite(parsed) && parsed > 0
    ? Math.min(GLOBAL_TIMELINE_MAX_DAYS, parsed)
    : GLOBAL_TIMELINE_MAX_DAYS;
  return { days };
};

const getWindowLabel = (windowValue) => {
  const opt = WINDOW_OPTIONS.find((o) => o.value === windowValue);
  return opt ? opt.label : `Last ${windowValue} days`;
};

const formatRelativeTime = (date) => {
  if (!date) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return formatDate(date);
};

const formatTimeIST = (date) => {
  if (!date) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  // Force IST display without depending on moment-timezone in the client bundle.
  return d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });
};

const formatDeltaMMSS = (deltaSeconds) => {
  const sec = Math.max(0, Math.floor(Number(deltaSeconds) || 0));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const formatJourneyLabel = (step) => {
  const eventType = String(step?.eventType || 'unknown');
  if (eventType !== 'purchase') return formatEventTypeLabel(eventType);
  const orderId = step?.orderId ? String(step.orderId) : '';
  if (!orderId) return 'Order placed';
  const tail = orderId.length > 6 ? orderId.slice(-6) : orderId;
  return `Order placed · ${tail}`;
};

const buildDisplayJourney = (journeyDetailed = []) => {
  const raw = Array.isArray(journeyDetailed) ? journeyDetailed : [];
  const seenOrderIds = new Set();
  const out = [];
  for (const step of raw) {
    const eventType = String(step?.eventType || 'unknown');
    const ts = step?.timestamp ? new Date(step.timestamp) : null;
    if (!ts || Number.isNaN(ts.getTime())) continue;
    const orderId = step?.orderId ? String(step.orderId) : null;

    // One "Order placed" per unique orderId
    if (eventType === 'purchase' && orderId) {
      if (seenOrderIds.has(orderId)) continue;
      seenOrderIds.add(orderId);
    }

    // Collapse accidental rapid duplicates (same label within 15s)
    const prev = out[out.length - 1] || null;
    if (prev && String(prev.eventType || '') === eventType && String(prev.orderId || '') === String(orderId || '')) {
      const dt = ts.getTime() - new Date(prev.timestamp).getTime();
      if (dt >= 0 && dt <= 15000) continue;
    }

    out.push({ ...step, timestamp: ts, orderId });
  }
  return out;
};

const LiveActiveSessionsModal = ({
  open,
  sessions,
  onClose,
  onCopyMobile,
  onRefresh,
  loading,
  historyLoading,
  historySessions,
  historyLastUpdatedAt,
  onHistoryRefresh,
}) => {
  const [activeTab, setActiveTab] = useState('live');
  if (!open) return null;
  const authenticated = sessions.filter((s) => s.userId && !s.isGuest);
  const guests = sessions.filter((s) => s.isGuest || !s.userId);

  const normalizedHistory = Array.isArray(historySessions) ? historySessions : [];
  const historyLoggedIn = normalizedHistory.filter((s) => s.userId && !s.isGuest);
  const historyGuests = normalizedHistory.filter((s) => s.isGuest || !s.userId);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-[80%] max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50 shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              Customers Active In App
            </h3>
            {activeTab === 'live' ? (
              <p className="text-xs text-gray-600 mt-0.5">
                Live · last 2 min · profiles and stats load together · use Refresh to update while open
              </p>
            ) : (
              <p className="text-xs text-gray-600 mt-0.5">
                History (Today) · all sessions captured today · grouped by session · accurate from analytics DB
                {historyLastUpdatedAt ? ` · last updated ${formatRelativeTime(historyLastUpdatedAt)}` : ''}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {activeTab === 'live' ? (
              <button
                type="button"
                onClick={onRefresh}
                disabled={loading}
                className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-white px-2.5 py-1.5"
              >
                <FaSyncAlt className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
            ) : (
              <button
                type="button"
                onClick={onHistoryRefresh}
                disabled={historyLoading}
                className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-white px-2.5 py-1.5"
              >
                <FaSyncAlt className={historyLoading ? 'animate-spin' : ''} />
                Refresh history
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-gray-800 flex items-center justify-center"
              aria-label="Close live sessions"
            >
              <FaTimes size={14} />
            </button>
          </div>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 bg-white shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="inline-flex items-center gap-1 rounded-xl bg-gray-50 border border-gray-100 p-1">
              <button
                type="button"
                onClick={() => setActiveTab('live')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                  activeTab === 'live'
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow'
                    : 'text-gray-600 hover:bg-white'
                }`}
              >
                Live now
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('history')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                  activeTab === 'history'
                    ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow'
                    : 'text-gray-600 hover:bg-white'
                }`}
              >
                History (Today)
              </button>
            </div>

            {activeTab === 'live' ? (
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2 min-w-[110px]">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Active now</p>
                  <p className="text-xl font-bold text-emerald-900">{sessions.length}</p>
                </div>
                <div className="rounded-xl bg-indigo-50 border border-indigo-100 px-3 py-2 min-w-[110px]">
                  <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Logged in</p>
                  <p className="text-xl font-bold text-indigo-900">{authenticated.length}</p>
                </div>
                <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2 min-w-[110px]">
                  <p className="text-[10px] uppercase tracking-wider text-gray-600 font-bold">Guests</p>
                  <p className="text-xl font-bold text-gray-800">{guests.length}</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-xl bg-indigo-50 border border-indigo-100 px-3 py-2 min-w-[110px]">
                  <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Sessions today</p>
                  <p className="text-xl font-bold text-indigo-900">{normalizedHistory.length}</p>
                </div>
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2 min-w-[110px]">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Logged in</p>
                  <p className="text-xl font-bold text-emerald-900">{historyLoggedIn.length}</p>
                </div>
                <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2 min-w-[110px]">
                  <p className="text-[10px] uppercase tracking-wider text-gray-600 font-bold">Guests</p>
                  <p className="text-xl font-bold text-gray-800">{historyGuests.length}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {activeTab === 'live' ? (
            loading && sessions.length === 0 ? (
            <div className="space-y-3 py-2">
              {[1, 2].map((i) => (
                <div key={i} className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 animate-pulse">
                  <div className="h-4 w-40 bg-gray-200 rounded" />
                  <div className="h-3 w-28 bg-gray-200 rounded mt-3" />
                  <div className="grid grid-cols-4 gap-2 mt-4">
                    {[1, 2, 3, 4].map((j) => (
                      <div key={j} className="h-12 bg-gray-200 rounded-lg" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FaMobileAlt className="mx-auto text-3xl text-gray-300 mb-3" />
              <p className="font-medium text-gray-700">No customers active in the app right now</p>
              <p className="text-sm mt-1">Someone appears here when they use the customer app (browse, cart, checkout) within the last 2 minutes.</p>
            </div>
          ) : (
            sessions.map((session) => (
              <div
                key={session.presenceId || session.socketId || session.userId}
                className="rounded-xl border border-gray-100 bg-white shadow-sm p-4 hover:border-emerald-200 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900 truncate">
                        {session.customerName || 'Customer'}
                      </p>
                      {session.isGuest ? (
                        <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Guest</span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Logged in</span>
                      )}
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 capitalize">{session.platform || 'mobile'}</span>
                    </div>
                    {session.customerMobile ? (
                      <button
                        type="button"
                        onClick={() => onCopyMobile(session.customerMobile)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 mt-1 inline-flex items-center gap-1.5"
                      >
                        {session.customerMobile}
                        <FaCopy className="text-[10px]" />
                      </button>
                    ) : (
                      <p className="text-xs text-gray-400 mt-1">No phone on profile</p>
                    )}
                    {session.customerEmail ? (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{session.customerEmail}</p>
                    ) : null}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-semibold text-emerald-700">{session.durationMins || 0}m active</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Last seen {formatRelativeTime(session.lastSeenAt)}</p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                  <div className="rounded-lg bg-gray-50 px-2 py-1.5">
                    <p className="text-[10px] text-gray-500 uppercase font-bold">Activity</p>
                    <p className="text-xs font-semibold text-gray-800 truncate">{session.lastActivity || session.currentScreen || 'Active'}</p>
                  </div>
                  <div className="rounded-lg bg-violet-50 px-2 py-1.5">
                    <p className="text-[10px] text-violet-600 uppercase font-bold flex items-center justify-center gap-1"><FaEye className="text-[9px]" /> Recent views</p>
                    <p className="text-sm font-bold text-violet-900">{Number(session.todayProductViews || 0)}</p>
                  </div>
                  <div className="rounded-lg bg-cyan-50 px-2 py-1.5">
                    <p className="text-[10px] text-cyan-700 uppercase font-bold flex items-center justify-center gap-1"><FaShoppingCart className="text-[9px]" /> Cart adds</p>
                    <p className="text-sm font-bold text-cyan-900">{Number(session.todayCartAdditions || 0)}</p>
                  </div>
                  <div className="rounded-lg bg-indigo-50 px-2 py-1.5">
                    <p className="text-[10px] text-indigo-600 uppercase font-bold">Recent events</p>
                    <p className="text-sm font-bold text-indigo-900">{Number(session.todayEvents || 0)}</p>
                  </div>
                </div>
              </div>
            ))
          )
          ) : (
            historyLoading && normalizedHistory.length === 0 ? (
              <div className="space-y-3 py-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 animate-pulse">
                    <div className="h-4 w-52 bg-gray-200 rounded" />
                    <div className="h-3 w-40 bg-gray-200 rounded mt-3" />
                    <div className="h-10 bg-gray-200 rounded-lg mt-4" />
                  </div>
                ))}
              </div>
            ) : normalizedHistory.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FaClock className="mx-auto text-3xl text-gray-300 mb-3" />
                <p className="font-medium text-gray-700">No sessions captured today yet</p>
                <p className="text-sm mt-1">This tab shows today’s full session history from analytics events (not live presence).</p>
              </div>
            ) : (
              <div className="space-y-2">
                {normalizedHistory.map((session) => (
                  <details
                    key={session.sessionKey || session.sessionId || session.userId}
                    className="group rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden"
                  >
                    <summary className="cursor-pointer list-none px-4 py-3 hover:bg-indigo-50/30 transition flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900 truncate">
                            {session.customerName || (session.isGuest ? 'Guest Visitor' : 'Customer')}
                          </p>
                          {session.isGuest ? (
                            <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Guest</span>
                          ) : (
                            <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Logged in</span>
                          )}
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 capitalize">{session.platform || 'mobile'}</span>
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">
                            {Number(session.totalEvents || 0)} events
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {session.customerMobile ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onCopyMobile(session.customerMobile);
                              }}
                              className="text-indigo-600 hover:text-indigo-800 font-semibold inline-flex items-center gap-1.5"
                            >
                              {session.customerMobile}
                              <FaCopy className="text-[10px]" />
                            </button>
                          ) : (
                            <span className="text-gray-400">No phone</span>
                          )}
                          <span className="mx-2 text-gray-300">·</span>
                          First: <span className="font-medium text-gray-700">{formatDate(session.firstSeenAt)}</span>
                          <span className="mx-2 text-gray-300">·</span>
                          Last: <span className="font-medium text-gray-700">{formatDate(session.lastSeenAt)}</span>
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs font-semibold text-indigo-700">{Number(session.durationMins || 0).toFixed(0)}m</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">Last seen {formatRelativeTime(session.lastSeenAt)}</p>
                      </div>
                    </summary>
                    <div className="px-4 pb-4">
                      <div className="mt-2 rounded-xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-violet-50 px-3 py-3">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Journey (recent)</p>
                          {/* <p className="text-[10px] text-gray-500">
                            {session.sessionId ? `Session: ${String(session.sessionId).slice(0, 18)}${String(session.sessionId).length > 18 ? '…' : ''}` : 'Session: —'}
                          </p> */}
                        </div>
                        {Array.isArray(session.journeyDetailed) && session.journeyDetailed.length ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            {buildDisplayJourney(session.journeyDetailed).map((step, idx, list) => {
                              const prev = idx > 0 ? list[idx - 1] : null;
                              const prevTs = prev?.timestamp ? new Date(prev.timestamp) : null;
                              const ts = step?.timestamp ? new Date(step.timestamp) : null;
                              const deltaSeconds = prevTs && ts
                                ? Math.max(0, Math.round((ts.getTime() - prevTs.getTime()) / 1000))
                                : null;
                              return (
                              <React.Fragment key={`${session.sessionKey}-j-${idx}`}>
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/60 bg-white/70 px-2.5 py-1 text-[11px] font-semibold text-indigo-900 shadow-sm">
                                  <span className="text-[10px] font-bold text-gray-500">{formatTimeIST(step.timestamp)}</span>
                                  {deltaSeconds != null ? (
                                    <>
                                      <span className="text-gray-300">·</span>
                                      <span className="text-[10px] font-bold text-emerald-700">+{formatDeltaMMSS(deltaSeconds)}</span>
                                    </>
                                  ) : null}
                                  <span className="text-gray-300">·</span>
                                  <span>{formatJourneyLabel(step)}</span>
                                </span>
                                {idx < list.length - 1 ? (
                                  <span className="text-indigo-400 text-xs font-bold">→</span>
                                ) : null}
                              </React.Fragment>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No journey events available.</p>
                        )}
                      </div>

                      <div className="mt-3">
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Event breakdown</p>
                        {Array.isArray(session.eventBreakdown) && session.eventBreakdown.length ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {session.eventBreakdown.map((item) => (
                              <div
                                key={`${session.sessionKey}-${item.eventType}`}
                                className={`rounded-lg border px-3 py-2.5 flex items-center justify-between gap-2 ${HOURLY_EVENT_TYPE_COLORS[item.eventType] || 'bg-gray-50 text-gray-700 border-gray-100'}`}
                              >
                                <span className="text-sm font-semibold leading-tight">{formatEventTypeLabel(item.eventType)}</span>
                                <span className="text-base font-bold tabular-nums">{Number(item.count || 0)}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No breakdown available.</p>
                        )}
                      </div>
                    </div>
                  </details>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

const HourlyPulseDetailModal = ({
  open,
  detail,
  windowLabel,
  onClose,
  onCopyMobile,
  onCopyAllMobiles,
}) => {
  if (!open || !detail) return null;
  const eventBreakdown = Array.isArray(detail.eventBreakdown) ? detail.eventBreakdown : [];
  const customers = Array.isArray(detail.customers) ? detail.customers : [];

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-violet-50 shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{detail.hourLabel} Activity Pulse</h3>
            <p className="text-xs text-gray-600 mt-0.5">{windowLabel} · IST · Hourly engagement breakdown</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-gray-800 flex items-center justify-center shrink-0"
            aria-label="Close hourly pulse details"
          >
            <FaTimes size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-5 py-4 grid grid-cols-2 gap-3 border-b border-indigo-50">
            <div className="rounded-xl bg-indigo-50/60 border border-indigo-100 px-4 py-3">
              <p className="text-[10px] uppercase tracking-wider text-indigo-600 font-bold">Users</p>
              <p className="text-2xl font-bold text-indigo-900 mt-0.5">{Number(detail.uniqueUsers || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="rounded-xl bg-violet-50/60 border border-violet-100 px-4 py-3">
              <p className="text-[10px] uppercase tracking-wider text-violet-600 font-bold">Events</p>
              <p className="text-2xl font-bold text-violet-900 mt-0.5">{Number(detail.events || 0).toLocaleString('en-IN')}</p>
            </div>
          </div>

          <div className="px-5 py-4 border-b border-indigo-50">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-3">Individual Event Counts</p>
            {eventBreakdown.length === 0 ? (
              <p className="text-sm text-gray-500">No events recorded in this hour.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {eventBreakdown.map((item) => (
                  <div
                    key={`${detail.hour}-${item.eventType}`}
                    className={`rounded-lg border px-3 py-2.5 flex items-center justify-between gap-2 ${HOURLY_EVENT_TYPE_COLORS[item.eventType] || 'bg-gray-50 text-gray-700 border-gray-100'}`}
                  >
                    <span className="text-sm font-semibold leading-tight">{formatEventTypeLabel(item.eventType)}</span>
                    <span className="text-base font-bold tabular-nums">{Number(item.count || 0)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-5 py-4">
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Active Customers</p>
              {customers.some((customer) => customer.mobile) ? (
                <button
                  type="button"
                  onClick={() => onCopyAllMobiles(customers)}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1.5 rounded-lg border border-indigo-100 bg-indigo-50 px-2.5 py-1.5 transition hover:bg-indigo-100"
                >
                  <FaCopy className="text-[10px]" />
                  Copy all mobiles
                </button>
              ) : null}
            </div>
            {customers.length === 0 ? (
              <p className="text-sm text-gray-500">No identified customers in this hour.</p>
            ) : (
              <div className="rounded-xl border border-gray-100 bg-gray-50/50 divide-y divide-gray-100 overflow-hidden">
                {customers.map((customer) => (
                  <div
                    key={`${detail.hour}-${customer.userId}`}
                    className="flex items-center justify-between gap-3 px-4 py-3 bg-white hover:bg-indigo-50/40 transition"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-800 truncate">{customer.name || 'Guest Customer'}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {customer.mobile || 'No mobile'}
                        <span className="mx-1.5 text-gray-300">·</span>
                        <span className="text-indigo-600 font-medium">{Number(customer.eventCount || 0)} events</span>
                      </p>
                    </div>
                    {customer.mobile ? (
                      <button
                        type="button"
                        onClick={() => onCopyMobile(customer.mobile)}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 transition"
                        title={`Copy ${customer.mobile}`}
                      >
                        <FaCopy className="text-[10px]" />
                        Copy
                      </button>
                    ) : (
                      <span className="shrink-0 text-xs text-gray-400">No mobile</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 flex items-center justify-between shrink-0">
          <span>
            {Number(detail.uniqueUsers || 0).toLocaleString('en-IN')} user{Number(detail.uniqueUsers || 0) === 1 ? '' : 's'}
            {' · '}
            {Number(detail.events || 0).toLocaleString('en-IN')} event{Number(detail.events || 0) === 1 ? '' : 's'}
          </span>
          <span>{customers.length} customer{customers.length === 1 ? '' : 's'} listed</span>
        </div>
      </div>
    </div>
  );
};

const EventIntensityTooltip = ({ active, payload, label, labelPrefix = '' }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload || {};
  const totalEvents = Number(point.events || 0);
  const eventBreakdown = Array.isArray(point.eventBreakdown) ? point.eventBreakdown : [];
  const displayLabel = point.hourLabel || point.dayLabel || label;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[260px]">
      <p className="text-xs text-gray-500">{labelPrefix}{displayLabel}</p>
      <p className="text-sm font-semibold text-gray-800 mb-2">Events: {totalEvents}</p>
      {eventBreakdown.length === 0 ? (
        <p className="text-xs text-gray-500">No event-level breakdown.</p>
      ) : (
        <div className="space-y-2">
          {eventBreakdown.slice(0, 6).map((item) => (
            <div key={item.eventType}>
              <div className="flex items-center justify-between text-xs text-gray-700">
                <span className="line-clamp-1 mr-2">{item.eventType}</span>
                <span>{item.count} ({item.percentage}%)</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded overflow-hidden mt-1">
                <div className={`h-full rounded ${item.percentage >= 45 ? 'bg-rose-500' : item.percentage >= 25 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.max(6, item.percentage)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const NOTIFIED_NO_USAGE_PAGE_SIZE = 5;

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 10, totalCount: 0, totalPages: 0 });
  const [expandedRow, setExpandedRow] = useState(null);
  const [toast, setToast] = useState({ visible: false, message: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const [abandonedMeta, setAbandonedMeta] = useState({ thresholdMinutes: 30, totalCount: 0 });
  const [abandonedSkip, setAbandonedSkip] = useState(0);
  const [abandonedHasMore, setAbandonedHasMore] = useState(false);
  const [loadingAbandonedMore, setLoadingAbandonedMore] = useState(false);
  const [abandonedPriorityFilter, setAbandonedPriorityFilter] = useState('all');
  const [expandedAbandonedCartItems, setExpandedAbandonedCartItems] = useState({});
  const [behaviorInsights, setBehaviorInsights] = useState(null);
  const [customerBehaviorMap, setCustomerBehaviorMap] = useState({});
  const [addressByIdMap, setAddressByIdMap] = useState({});
  const [loadingCustomerBehaviorId, setLoadingCustomerBehaviorId] = useState(null);
  const [activeTab, setActiveTab] = useState('customers');
  const [selectedWindow, setSelectedWindow] = useState('today');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [behaviorCustomerInput, setBehaviorCustomerInput] = useState('');
  const [loadingBehaviorCustomerSearch, setLoadingBehaviorCustomerSearch] = useState(false);
  const [behaviorCustomerSearchOptions, setBehaviorCustomerSearchOptions] = useState([]);
  const behaviorCustomerSearchTimer = useRef(null);
  const [fullTimelineModalOpen, setFullTimelineModalOpen] = useState(false);
  const [fullTimelineEvents, setFullTimelineEvents] = useState([]);
  const [loadingFullTimeline, setLoadingFullTimeline] = useState(false);
  const [fullDropOffsModalOpen, setFullDropOffsModalOpen] = useState(false);
  const [fullDropOffItems, setFullDropOffItems] = useState([]);
  const [loadingFullDropOffs, setLoadingFullDropOffs] = useState(false);
  const [deadStockModalOpen, setDeadStockModalOpen] = useState(false);
  const [deadStockSearch, setDeadStockSearch] = useState('');
  const [selectedHealthProductIds, setSelectedHealthProductIds] = useState([]);
  const [productHealthTierFilter, setProductHealthTierFilter] = useState('all');
  const [selectedHealthProductDetail, setSelectedHealthProductDetail] = useState(null);
  const [selectedHourlyPulseHour, setSelectedHourlyPulseHour] = useState(null);
  const [liveInAppCount, setLiveInAppCount] = useState(0);
  const [modalPresenceSessions, setModalPresenceSessions] = useState([]);
  const [livePresenceModalOpen, setLivePresenceModalOpen] = useState(false);
  const [loadingLivePresence, setLoadingLivePresence] = useState(false);
  const livePresenceModalOpenRef = useRef(false);
  const [todaySessionHistory, setTodaySessionHistory] = useState([]);
  const [loadingTodaySessionHistory, setLoadingTodaySessionHistory] = useState(false);
  const [todaySessionHistoryUpdatedAt, setTodaySessionHistoryUpdatedAt] = useState(null);
  const [branchPerformanceOptions, setBranchPerformanceOptions] = useState([]);
  const [selectedBranchPerformanceId, setSelectedBranchPerformanceId] = useState('all');
  const [branchPerformanceInsights, setBranchPerformanceInsights] = useState(null);
  const [loadingBranchPerformance, setLoadingBranchPerformance] = useState(false);
  const [freeCashAnalytics, setFreeCashAnalytics] = useState(null);
  const [loadingFreeCashAnalytics, setLoadingFreeCashAnalytics] = useState(false);
  const [notifiedNoUsageRows, setNotifiedNoUsageRows] = useState([]);
  const [notifiedNoUsageMeta, setNotifiedNoUsageMeta] = useState({ totalCount: 0, summary: null, notificationWindow: null });
  const [notifiedNoUsagePage, setNotifiedNoUsagePage] = useState(0);
  const [notifiedNoUsageSearch, setNotifiedNoUsageSearch] = useState('');
  const [debouncedNotifiedSearch, setDebouncedNotifiedSearch] = useState('');
  const [loadingNotifiedNoUsage, setLoadingNotifiedNoUsage] = useState(false);
  const [notifiedDetailCustomer, setNotifiedDetailCustomer] = useState(null);
  const [instoreSalesAnalytics, setInstoreSalesAnalytics] = useState(null);
  const [loadingInstoreSalesAnalytics, setLoadingInstoreSalesAnalytics] = useState(false);
  const [instoreBranchOptions, setInstoreBranchOptions] = useState([{ value: 'all', label: 'All POS Branches' }]);
  const [selectedInstoreBranchId, setSelectedInstoreBranchId] = useState('all');
  const [instoreCustomerAnalytics, setInstoreCustomerAnalytics] = useState(null);
  const [loadingInstoreCustomerAnalytics, setLoadingInstoreCustomerAnalytics] = useState(false);
  const [instorePosSearch, setInstorePosSearch] = useState('');
  const [debouncedInstorePosSearch, setDebouncedInstorePosSearch] = useState('');
  const [instorePosSearchResults, setInstorePosSearchResults] = useState([]);
  const [loadingInstorePosSearch, setLoadingInstorePosSearch] = useState(false);
  const [selectedPosLoyaltySegment, setSelectedPosLoyaltySegment] = useState('vip');
  const [posCustomerProfile, setPosCustomerProfile] = useState(null);
  const [loadingPosCustomerProfile, setLoadingPosCustomerProfile] = useState(false);
  const [posCustomerProfileOpen, setPosCustomerProfileOpen] = useState(false);
  const [deadProducts, setDeadProducts] = useState([]);
  const [deadProductsMeta, setDeadProductsMeta] = useState(null);
  const [deadProductsSkip, setDeadProductsSkip] = useState(0);
  const [deadProductsLoadPage, setDeadProductsLoadPage] = useState(0);
  const [deadProductsHasMore, setDeadProductsHasMore] = useState(false);
  const [loadingDeadProducts, setLoadingDeadProducts] = useState(false);
  const [loadingDeadProductsMore, setLoadingDeadProductsMore] = useState(false);
  const [deadProductsBranchId, setDeadProductsBranchId] = useState('all');
  const [deadProductsOnline, setDeadProductsOnline] = useState(true);
  const [deadProductsOffline, setDeadProductsOffline] = useState(true);
  const [deadProductsAllTime, setDeadProductsAllTime] = useState(false);
  const [deadProductsSearch, setDeadProductsSearch] = useState('');
  const [deadProductsAccordionOpen, setDeadProductsAccordionOpen] = useState({});

  // Filter states
  const [filters, setFilters] = useState({
    gender: 'all',
    joinedFrom: '',
    joinedTo: '',
    minRevenue: '',
    maxRevenue: '',
    minOrderCount: '',
    maxOrderCount: '',
    hasOrders: '', // '', 'yes', 'no'
    deliveredOrderBucket: '', // '', 'none', 'first', 'second', 'twoPlus'
    openCart: '', // '', 'yes', 'no'
  });
  
  // Sorting state
  const [sortBy, setSortBy] = useState('joinedTime');
  const [sortOrder, setSortOrder] = useState(-1);

  const showToast = (message) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message: '' }), 2000);
  };

  const handleNavigateOrderOverview = useCallback((orderId) => {
    if (orderId) navigate(`/orderOverview/${orderId}`);
  }, [navigate]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchCustomers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const skip = (page - 1) * pagination.limit;
      const params = {
        searchTerm: debouncedSearch,
        skip,
        limit: pagination.limit,
        sortBy,
        sortOrder,
        // Include filter params
        gender: filters.gender !== 'all' ? filters.gender : undefined,
        joinedFrom: filters.joinedFrom || undefined,
        joinedTo: filters.joinedTo || undefined,
        minRevenue: filters.minRevenue || undefined,
        maxRevenue: filters.maxRevenue || undefined,
        minOrderCount: filters.minOrderCount || undefined,
        maxOrderCount: filters.maxOrderCount || undefined,
        hasOrders: filters.hasOrders || undefined,
        deliveredOrderBucket: filters.deliveredOrderBucket || undefined,
        openCart: filters.openCart || undefined,
      };
      const res = await getCustomers(params);
      if (res && res.code === 200 && res.success) {
        setCustomers(res.data || []);
        setPagination(prev => ({
          ...prev,
          currentPage: page,
          totalCount: res.pagination?.totalCount || 0,
          totalPages: res.pagination?.totalPages || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      showToast('Failed to fetch customers.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, pagination.limit, sortBy, sortOrder, filters]);

  const fetchAbandonedCartsPage = useCallback(async ({ reset = false, skip: requestedSkip = 0 } = {}) => {
    const pageSize = 12;
    const nextSkip = reset ? 0 : Math.max(0, Number(requestedSkip || 0));
    if (!reset) {
      setLoadingAbandonedMore(true);
    }
    try {
      const abandonedRes = await getAbandonedCarts({
        thresholdMinutes: 30,
        limit: pageSize,
        skip: nextSkip,
        searchTerm: debouncedSearch || undefined,
        todayOnly: true,
      });

      if (abandonedRes?.success) {
        const rows = Array.isArray(abandonedRes.data) ? abandonedRes.data : [];
        const totalCount = Number(abandonedRes.pagination?.totalCount || 0);
        const loadedCount = nextSkip + rows.length;
        setAbandonedCarts((prev) => (reset ? rows : [...prev, ...rows]));
        setAbandonedMeta({
          thresholdMinutes: abandonedRes.meta?.thresholdMinutes || 30,
          totalCount,
        });
        setAbandonedSkip(loadedCount);
        setAbandonedHasMore(loadedCount < totalCount);
      } else if (reset) {
        setAbandonedCarts([]);
        setAbandonedSkip(0);
        setAbandonedHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching abandoned carts:', error);
      if (reset) {
        setAbandonedCarts([]);
        setAbandonedSkip(0);
        setAbandonedHasMore(false);
      }
    } finally {
      if (!reset) {
        setLoadingAbandonedMore(false);
      }
    }
  }, [debouncedSearch]);

  const fetchInsights = useCallback(async () => {
    setLoadingInsights(true);
    try {
      const [, behaviorRes] = await Promise.all([
        fetchAbandonedCartsPage({ reset: true }),
        getCustomerBehaviorInsights({
          ...buildWindowParams(selectedWindow),
          limit: 20,
        }),
      ]);

      if (behaviorRes?.success) {
        setBehaviorInsights(behaviorRes.data || null);
        if (selectedWindow === 'today' && behaviorRes.data?.livePresence) {
          setLiveInAppCount(Number(behaviorRes.data.livePresence.count || 0));
        }
      } else {
        setBehaviorInsights(null);
      }
    } catch (error) {
      console.error('Error fetching customer insights:', error);
      setBehaviorInsights(null);
    } finally {
      setLoadingInsights(false);
    }
  }, [selectedWindow, fetchAbandonedCartsPage]);

  useEffect(() => {
    livePresenceModalOpenRef.current = livePresenceModalOpen;
  }, [livePresenceModalOpen]);

  const fetchModalPresence = useCallback(async () => {
    if (selectedWindow !== 'today') return;
    setLoadingLivePresence(true);
    try {
      const res = await getLiveCustomerPresence({});
      if (res?.success) {
        setModalPresenceSessions(res.data?.sessions || []);
        if (typeof res.data?.count === 'number') {
          setLiveInAppCount(res.data.count);
        }
      }
    } catch (error) {
      console.error('Error fetching live presence:', error);
    } finally {
      setLoadingLivePresence(false);
    }
  }, [selectedWindow]);

  const fetchTodaySessionHistory = useCallback(async () => {
    setLoadingTodaySessionHistory(true);
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const masterId = userData.role === 1 ? userData.selectedBranch : userData.masterId;
      const res = await getTodayCustomerSessionHistory({
        limit: 120,
        selectedMaster: userData.role === 1 ? masterId : undefined,
      });
      if (res?.success) {
        setTodaySessionHistory(Array.isArray(res.data?.sessions) ? res.data.sessions : []);
        setTodaySessionHistoryUpdatedAt(res.data?.dayEnd || new Date().toISOString());
      } else {
        setTodaySessionHistory([]);
        if (res?.message) showToast(res.message);
      }
    } catch (error) {
      console.error('Error fetching today session history:', error);
      showToast('Failed to load session history');
    } finally {
      setLoadingTodaySessionHistory(false);
    }
  }, []);

  const openLivePresenceModal = useCallback(async () => {
    setLivePresenceModalOpen(true);
    setModalPresenceSessions([]);
    await fetchModalPresence();
    fetchTodaySessionHistory();
  }, [fetchModalPresence, fetchTodaySessionHistory]);

  useEffect(() => {
    if (activeTab !== 'micro' || selectedWindow !== 'today') {
      socketService.leaveCustomerPresenceAnalytics();
      return undefined;
    }

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const masterId = userData.role === 1 ? userData.selectedBranch : userData.masterId;
    if (!masterId) return undefined;

    const unsubscribe = socketService.addListener('customerPresenceUpdate', (data) => {
      if (typeof data?.count === 'number') {
        setLiveInAppCount(data.count);
      }
    });

    const tryJoin = () => {
      if (socketService.isSocketConnected()) {
        socketService.joinCustomerPresenceAnalytics(masterId);
      }
    };
    tryJoin();
    const joinInterval = setInterval(tryJoin, 4000);

    return () => {
      unsubscribe();
      clearInterval(joinInterval);
      socketService.leaveCustomerPresenceAnalytics();
    };
  }, [activeTab, selectedWindow]);

  useEffect(() => {
    fetchCustomers(1);
  }, [fetchCustomers]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  useEffect(() => {
    const loadBranchOptions = async () => {
      try {
        const response = await getBranchNamesForUser();
        if (response?.success && Array.isArray(response.data)) {
          const options = [
            { value: 'all', label: 'All Branches (Compare)' },
            ...response.data
              .filter((branch) => !branch.masterAccount)
              .map((branch) => ({
                value: String(branch._id),
                label: branch.branchName || 'Branch',
              })),
          ];
          // POS-enabled, non-master / non-warehouse branches only (for Instore Sales section)
          const posBranches = response.data.filter((branch) => (
            !branch.masterAccount
            && branch.isWarehouse !== true
            && (branch.enableOfflineStore === true || branch.enablePOS === true)
          ));
          const posOptions = [
            { value: 'all', label: 'All POS Branches' },
            ...posBranches.map((branch) => ({
              value: String(branch._id),
              label: branch.branchName || 'Branch',
            })),
          ];

          if (response.canSwitchBranch === false && response.data.length === 1) {
            const onlyBranch = response.data[0];
            setBranchPerformanceOptions([{
              value: String(onlyBranch._id),
              label: onlyBranch.branchName || 'Branch',
            }]);
            setSelectedBranchPerformanceId(String(onlyBranch._id));
            setDeadProductsBranchId(String(onlyBranch._id));
            const singlePos = [{ value: String(onlyBranch._id), label: onlyBranch.branchName || 'Branch' }];
            setInstoreBranchOptions(singlePos);
            setSelectedInstoreBranchId(String(onlyBranch._id));
          } else {
            setBranchPerformanceOptions(options);
            if (!options.some((option) => option.value === selectedBranchPerformanceId)) {
              setSelectedBranchPerformanceId('all');
            }
            if (!options.some((option) => option.value === deadProductsBranchId)) {
              setDeadProductsBranchId('all');
            }
            setInstoreBranchOptions(posOptions);
            setSelectedInstoreBranchId((prev) => (
              posOptions.some((option) => option.value === prev) ? prev : 'all'
            ));
          }
        }
      } catch (error) {
        console.error('Error fetching branch options:', error);
      }
    };
    loadBranchOptions();
    // Mount-only: branch option lists are loaded once; selected ids are updated inside this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBranchPerformance = useCallback(async () => {
    if (!selectedBranchPerformanceId) return;
    setLoadingBranchPerformance(true);
    try {
      const res = await getBranchPerformanceInsights({
        branchId: selectedBranchPerformanceId,
        ...buildWindowParams(selectedWindow),
        limit: 20,
      });
      if (res?.success) {
        setBranchPerformanceInsights(res.data || null);
      } else {
        setBranchPerformanceInsights(null);
      }
    } catch (error) {
      console.error('Error fetching branch performance insights:', error);
      setBranchPerformanceInsights(null);
    } finally {
      setLoadingBranchPerformance(false);
    }
  }, [selectedBranchPerformanceId, selectedWindow]);

  const fetchFreeCashAnalyticsData = useCallback(async () => {
    if (!selectedBranchPerformanceId) return;
    setLoadingFreeCashAnalytics(true);
    try {
      const res = await getFreeCashAnalytics({
        branchId: selectedBranchPerformanceId,
        ...buildWindowParams(selectedWindow),
        limit: 15,
      });
      if (res?.success) {
        setFreeCashAnalytics(res.data || null);
      } else {
        setFreeCashAnalytics(null);
      }
    } catch (error) {
      console.error('Error fetching Free Cash analytics:', error);
      setFreeCashAnalytics(null);
    } finally {
      setLoadingFreeCashAnalytics(false);
    }
  }, [selectedBranchPerformanceId, selectedWindow]);

  const fetchNotifiedNoUsage = useCallback(async ({ page = 0, search = '' } = {}) => {
    setLoadingNotifiedNoUsage(true);
    try {
      const res = await getFreeCashNotifiedNoUsage({
        ...buildWindowParams(selectedWindow),
        skip: page * NOTIFIED_NO_USAGE_PAGE_SIZE,
        limit: NOTIFIED_NO_USAGE_PAGE_SIZE,
        searchTerm: search || undefined,
      });
      if (res?.success) {
        setNotifiedNoUsageRows(Array.isArray(res.data) ? res.data : []);
        setNotifiedNoUsageMeta({
          totalCount: Number(res.pagination?.totalCount || 0),
          summary: res.summary || null,
          notificationWindow: res.notificationWindow || null,
        });
      } else {
        setNotifiedNoUsageRows([]);
        setNotifiedNoUsageMeta({ totalCount: 0, summary: null, notificationWindow: null });
      }
    } catch (error) {
      console.error('Error fetching Free Cash notified-no-usage customers:', error);
      setNotifiedNoUsageRows([]);
      setNotifiedNoUsageMeta({ totalCount: 0, summary: null, notificationWindow: null });
    } finally {
      setLoadingNotifiedNoUsage(false);
    }
  }, [selectedWindow]);

  const fetchInstoreSalesAnalyticsData = useCallback(async () => {
    if (!selectedInstoreBranchId) return;
    setLoadingInstoreSalesAnalytics(true);
    try {
      const res = await getInstoreSalesAnalytics({
        branchId: selectedInstoreBranchId,
        ...buildWindowParams(selectedWindow),
        limit: 12,
      });
      if (res?.success) {
        setInstoreSalesAnalytics(res.data || null);
      } else {
        setInstoreSalesAnalytics(null);
      }
    } catch (error) {
      console.error('Error fetching Instore Sales analytics:', error);
      setInstoreSalesAnalytics(null);
    } finally {
      setLoadingInstoreSalesAnalytics(false);
    }
  }, [selectedInstoreBranchId, selectedWindow]);

  const fetchInstoreCustomerAnalyticsData = useCallback(async () => {
    if (!selectedInstoreBranchId) return;
    setLoadingInstoreCustomerAnalytics(true);
    try {
      const res = await getInstoreCustomerAnalytics({
        branchId: selectedInstoreBranchId,
        ...buildWindowParams(selectedWindow),
        limit: 12,
      });
      if (res?.success) {
        setInstoreCustomerAnalytics(res.data || null);
      } else {
        setInstoreCustomerAnalytics(null);
      }
    } catch (error) {
      console.error('Error fetching Instore Customer analytics:', error);
      setInstoreCustomerAnalytics(null);
    } finally {
      setLoadingInstoreCustomerAnalytics(false);
    }
  }, [selectedInstoreBranchId, selectedWindow]);

  const openPosCustomerProfile = useCallback(async (mobile) => {
    const phone = String(mobile || '').replace(/\D/g, '').slice(-10);
    if (phone.length !== 10) {
      showToast('Valid 10-digit mobile required');
      return;
    }
    setPosCustomerProfileOpen(true);
    setLoadingPosCustomerProfile(true);
    setPosCustomerProfile(null);
    try {
      const res = await getInstorePosCustomerProfile({
        mobile: phone,
        branchId: selectedInstoreBranchId,
        allTime: true,
        billLimit: 40,
      });
      if (res?.success) {
        setPosCustomerProfile(res.data || null);
      } else {
        showToast(res?.message || 'Could not load POS customer');
      }
    } catch (error) {
      console.error('Error fetching POS customer profile:', error);
      showToast('Failed to load POS customer profile');
    } finally {
      setLoadingPosCustomerProfile(false);
    }
  }, [selectedInstoreBranchId]);

  const fetchDeadProductsData = useCallback(async ({ reset = false, skip: requestedSkip = 0, loadPage: requestedLoadPage } = {}) => {
    if (!deadProductsOnline && !deadProductsOffline) {
      setDeadProducts([]);
      setDeadProductsMeta(null);
      setDeadProductsSkip(0);
      setDeadProductsLoadPage(0);
      setDeadProductsHasMore(false);
      return;
    }
    if (reset) {
      setLoadingDeadProducts(true);
    } else {
      setLoadingDeadProductsMore(true);
    }
    const loadPage = reset ? 0 : (requestedLoadPage ?? 0);
    const batchLimit = getDeadProductsBatchSize(loadPage);
    try {
      const res = await getDeadProductsNoPurchase({
        branchId: deadProductsBranchId,
        includeOnline: deadProductsOnline,
        includeOffline: deadProductsOffline,
        allTime: deadProductsAllTime,
        ...(deadProductsAllTime ? {} : buildWindowParams(selectedWindow)),
        limit: batchLimit,
        skip: reset ? 0 : requestedSkip,
      });
      if (res?.success) {
        const rows = Array.isArray(res.data?.products) ? res.data.products : [];
        const totalCount = Number(res.data?.pagination?.totalCount || 0);
        const loadedCount = reset ? rows.length : requestedSkip + rows.length;
        setDeadProducts((prev) => (reset ? rows : [...prev, ...rows]));
        setDeadProductsMeta(res.data || null);
        setDeadProductsSkip(loadedCount);
        setDeadProductsLoadPage(loadPage + 1);
        setDeadProductsHasMore(loadedCount < totalCount);
        if (reset) setDeadProductsAccordionOpen({});
      } else {
        setDeadProducts([]);
        setDeadProductsMeta(null);
        setDeadProductsSkip(0);
        setDeadProductsLoadPage(0);
        setDeadProductsHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching dead products:', error);
      if (reset) {
        setDeadProducts([]);
        setDeadProductsMeta(null);
        setDeadProductsSkip(0);
        setDeadProductsLoadPage(0);
        setDeadProductsHasMore(false);
      }
    } finally {
      setLoadingDeadProducts(false);
      setLoadingDeadProductsMore(false);
    }
  }, [deadProductsBranchId, deadProductsOnline, deadProductsOffline, deadProductsAllTime, selectedWindow]);

  useEffect(() => {
    if (activeTab === 'micro') {
      fetchBranchPerformance();
      fetchFreeCashAnalyticsData();
      fetchInstoreSalesAnalyticsData();
      fetchInstoreCustomerAnalyticsData();
      fetchDeadProductsData({ reset: true });
    }
  }, [activeTab, fetchBranchPerformance, fetchFreeCashAnalyticsData, fetchInstoreSalesAnalyticsData, fetchInstoreCustomerAnalyticsData, fetchDeadProductsData]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedInstorePosSearch(instorePosSearch), 350);
    return () => clearTimeout(handler);
  }, [instorePosSearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedNotifiedSearch(notifiedNoUsageSearch);
      setNotifiedNoUsagePage(0);
    }, 350);
    return () => clearTimeout(handler);
  }, [notifiedNoUsageSearch]);

  useEffect(() => {
    if (activeTab !== 'micro') return;
    fetchNotifiedNoUsage({ page: notifiedNoUsagePage, search: debouncedNotifiedSearch });
  }, [activeTab, fetchNotifiedNoUsage, notifiedNoUsagePage, debouncedNotifiedSearch]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(Number(notifiedNoUsageMeta.totalCount || 0) / NOTIFIED_NO_USAGE_PAGE_SIZE));
    if (notifiedNoUsagePage > totalPages - 1) {
      setNotifiedNoUsagePage(totalPages - 1);
    }
  }, [notifiedNoUsageMeta.totalCount, notifiedNoUsagePage]);

  useEffect(() => {
    const q = String(debouncedInstorePosSearch || '').trim();
    const digits = q.replace(/\D/g, '');
    if (digits.length < 3 && q.length < 2) {
      setInstorePosSearchResults([]);
      return undefined;
    }
    let cancelled = false;
    const run = async () => {
      setLoadingInstorePosSearch(true);
      try {
        const res = await searchInstorePosCustomers({
          query: q,
          branchId: selectedInstoreBranchId,
          limit: 10,
        });
        if (!cancelled) {
          setInstorePosSearchResults(Array.isArray(res?.data) ? res.data : []);
        }
      } catch (error) {
        console.error('POS customer search failed:', error);
        if (!cancelled) setInstorePosSearchResults([]);
      } finally {
        if (!cancelled) setLoadingInstorePosSearch(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [debouncedInstorePosSearch, selectedInstoreBranchId]);

  useEffect(() => {
    const allowedWindows = new Set(WINDOW_OPTIONS.map((opt) => opt.value));
    if (!allowedWindows.has(selectedWindow)) {
      setSelectedWindow('today');
    }
  }, [selectedWindow]);

  useEffect(() => {
    setSelectedHourlyPulseHour(null);
    setSelectedHealthProductDetail(null);
    setNotifiedNoUsagePage(0);
    setNotifiedDetailCustomer(null);
  }, [selectedWindow]);

  const resolveBehaviorWindow = useCallback((tab = activeTab) => (
    tab === 'customers' ? CUSTOMERS_TAB_BEHAVIOR_WINDOW : selectedWindow
  ), [activeTab, selectedWindow]);

  const fetchCustomerBehavior = useCallback(async (customerId, options = {}) => {
    const { force = false, tab = activeTab } = options;
    if (!customerId) return;
    const behaviorWindow = resolveBehaviorWindow(tab);
    const mapKey = `${customerId}_${behaviorWindow}`;
    if (!force && customerBehaviorMap[mapKey]) return;
    setLoadingCustomerBehaviorId(customerId);
    try {
      const res = await getCustomerBehaviorTimeline({
        customerId,
        ...buildWindowParams(behaviorWindow),
        limit: 30,
      });
      if (res?.success) {
        setCustomerBehaviorMap((prev) => ({ ...prev, [mapKey]: res.data || null }));
      }
    } catch (error) {
      console.error('Failed to fetch customer behavior timeline:', error);
    } finally {
      setLoadingCustomerBehaviorId(null);
    }
  }, [customerBehaviorMap, activeTab, resolveBehaviorWindow]);

  const handleMicroAnalysisRefresh = useCallback(async () => {
    setSelectedHourlyPulseHour(null);
    try {
      await Promise.all([
        fetchInsights(),
        fetchBranchPerformance(),
        fetchFreeCashAnalyticsData(),
        fetchNotifiedNoUsage({ page: notifiedNoUsagePage, search: debouncedNotifiedSearch }),
        fetchInstoreSalesAnalyticsData(),
        fetchInstoreCustomerAnalyticsData(),
        fetchDeadProductsData({ reset: true }),
      ]);
      if (selectedCustomerId) {
        await fetchCustomerBehavior(selectedCustomerId, { force: true });
      }
      showToast('Micro Analysis refreshed');
    } catch (error) {
      console.error('Failed to refresh micro analysis:', error);
      showToast('Failed to refresh Micro Analysis');
    }
  }, [fetchInsights, fetchBranchPerformance, fetchFreeCashAnalyticsData, fetchNotifiedNoUsage, notifiedNoUsagePage, debouncedNotifiedSearch, fetchInstoreSalesAnalyticsData, fetchInstoreCustomerAnalyticsData, fetchDeadProductsData, fetchCustomerBehavior, selectedCustomerId]);

  const openFullTimelineModal = useCallback(async () => {
    if (!selectedCustomerId) return;
    setFullTimelineModalOpen(true);
    setLoadingFullTimeline(true);
    setFullTimelineEvents([]);
    try {
      const res = await getCustomerBehaviorTimeline({
        customerId: selectedCustomerId,
        ...buildWindowParams(selectedWindow),
        limit: 200,
      });
      if (res?.success) {
        setFullTimelineEvents(Array.isArray(res.data?.timeline) ? res.data.timeline : []);
      }
    } catch (error) {
      console.error('Failed to fetch full customer timeline:', error);
    } finally {
      setLoadingFullTimeline(false);
    }
  }, [selectedCustomerId, selectedWindow]);

  const openFullDropOffsModal = useCallback(async () => {
    if (!selectedCustomerId) return;
    setFullDropOffsModalOpen(true);
    setLoadingFullDropOffs(true);
    setFullDropOffItems([]);
    try {
      const res = await getCustomerBehaviorTimeline({
        customerId: selectedCustomerId,
        ...buildWindowParams(selectedWindow),
        limit: 10,
        productBehaviorLimit: 200,
        dropOffLimit: 200,
      });
      if (res?.success) {
        const items = res.data?.productBehavior?.viewedNotPurchased;
        setFullDropOffItems(Array.isArray(items) ? items : []);
      }
    } catch (error) {
      console.error('Failed to fetch full customer drop-offs:', error);
    } finally {
      setLoadingFullDropOffs(false);
    }
  }, [selectedCustomerId, selectedWindow]);

  const fetchAddressDetailsById = useCallback(async (customerId, addressId) => {
    if (!addressId) return;
    const cacheKey = `${customerId || 'unknown'}_${addressId}`;
    if (addressByIdMap[cacheKey]) return;
    try {
      const res = await getAddressById({ customerId, addressId });
      if (res?.success && res?.data) {
        setAddressByIdMap((prev) => ({ ...prev, [cacheKey]: res.data }));
      }
    } catch (error) {
      console.error('Failed to resolve address by ID:', error);
    }
  }, [addressByIdMap]);

  useEffect(() => {
    const expandedKey = `${expandedRow}_${resolveBehaviorWindow('customers')}`;
    if (expandedRow && activeTab === 'customers' && !customerBehaviorMap[expandedKey]) {
      fetchCustomerBehavior(expandedRow, { tab: 'customers' });
    }
  }, [expandedRow, customerBehaviorMap, fetchCustomerBehavior, activeTab, resolveBehaviorWindow]);

  const highIntentNoOrderToday = useMemo(() => {
    return (behaviorInsights?.watchlists?.highIntentNoOrderToday || []).slice(0, 10);
  }, [behaviorInsights]);

  const mapCustomerToSelectOption = useCallback((customer, meta = {}) => {
    const id = String(customer?.customerId || customer?._id || '');
    const name = customer?.customerName || customer?.name || 'Customer';
    const mobile = customer?.customerMobile || customer?.mobile || '';
    const email = customer?.customerEmail || customer?.email || '';
    const intentScore = Number(customer?.intentScore || meta.intentScore || 0);
    const label = [name, mobile, email].filter(Boolean).join(' · ');
    return {
      value: id,
      label: label || id,
      name,
      mobile,
      email,
      intentScore,
      isSuggested: Boolean(meta.isSuggested),
    };
  }, []);

  const suggestedBehaviorCustomerOptions = useMemo(() => {
    return highIntentNoOrderToday.map((entry) => mapCustomerToSelectOption(entry, { isSuggested: true, intentScore: entry.intentScore }));
  }, [highIntentNoOrderToday, mapCustomerToSelectOption]);

  const selectedBehaviorCustomerOption = useMemo(() => {
    if (!selectedCustomerId) return null;
    const fromSuggested = suggestedBehaviorCustomerOptions.find((opt) => opt.value === String(selectedCustomerId));
    if (fromSuggested) return fromSuggested;
    const fromSearch = behaviorCustomerSearchOptions.find((opt) => opt.value === String(selectedCustomerId));
    if (fromSearch) return fromSearch;
    const fromPage = customers.find((c) => String(c._id) === String(selectedCustomerId));
    if (fromPage) return mapCustomerToSelectOption(fromPage);
    return { value: String(selectedCustomerId), label: String(selectedCustomerId) };
  }, [selectedCustomerId, suggestedBehaviorCustomerOptions, behaviorCustomerSearchOptions, customers, mapCustomerToSelectOption]);

  const loadBehaviorCustomerOptions = useCallback(async (inputValue = '') => {
    const query = String(inputValue || '').trim();
    if (!query) {
      setBehaviorCustomerSearchOptions([]);
      return suggestedBehaviorCustomerOptions;
    }
    setLoadingBehaviorCustomerSearch(true);
    try {
      const res = await getCustomers({
        searchTerm: query,
        skip: 0,
        limit: 20,
        sortBy: 'joinedTime',
        sortOrder: -1,
      });
      const rows = Array.isArray(res?.data) ? res.data : [];
      const options = rows.map((row) => mapCustomerToSelectOption(row));
      setBehaviorCustomerSearchOptions(options);
      return options;
    } catch (error) {
      console.error('Failed to search customers for behavior insights:', error);
      return [];
    } finally {
      setLoadingBehaviorCustomerSearch(false);
    }
  }, [mapCustomerToSelectOption, suggestedBehaviorCustomerOptions]);

  useEffect(() => {
    if (activeTab !== 'micro') return;
    if (selectedCustomerId) return;
    if (highIntentNoOrderToday.length > 0) {
      setSelectedCustomerId(String(highIntentNoOrderToday[0].customerId));
    }
  }, [activeTab, selectedCustomerId, highIntentNoOrderToday]);

  useEffect(() => {
    if (selectedCustomerId) {
      fetchCustomerBehavior(selectedCustomerId);
    }
  }, [selectedCustomerId, selectedWindow, fetchCustomerBehavior]);

  useEffect(() => {
    const expandedKey = `${expandedRow}_${CUSTOMERS_TAB_BEHAVIOR_WINDOW}`;
    const expandedBehavior = customerBehaviorMap[expandedKey];
    const addresses = expandedBehavior?.preferredAddresses || [];
    addresses.forEach((addr) => {
      if (addr?.addressId) {
        fetchAddressDetailsById(expandedRow, addr.addressId);
      }
    });
  }, [expandedRow, customerBehaviorMap, fetchAddressDetailsById]);

  useEffect(() => {
    if (!selectedCustomerId) return;
    const selectedKey = `${selectedCustomerId}_${selectedWindow}`;
    const selectedBehavior = customerBehaviorMap[selectedKey];
    const addresses = selectedBehavior?.preferredAddresses || [];
    addresses.forEach((addr) => {
      if (addr?.addressId) {
        fetchAddressDetailsById(selectedCustomerId, addr.addressId);
      }
    });
  }, [selectedCustomerId, selectedWindow, customerBehaviorMap, fetchAddressDetailsById]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      showToast(`Copied: ${text}`);
    } catch (err) {
      console.error('Clipboard error:', err);
      showToast('Unable to copy.');
    }
  }; 

  const handleCopyAll = async () => {
    try {
      const res = await getAllCustomerMobileNumbers({ searchTerm: debouncedSearch });
      if (res && res.code === 200 && res.success && Array.isArray(res.data) && res.data.length > 0) {
        const joined = res.data.join(', ');
        await navigator.clipboard.writeText(joined);
        showToast('All mobile numbers copied to clipboard');
      } else {
        showToast('No mobile numbers found');
      }
    } catch (error) {
      console.error('Error copying all mobiles:', error);
      showToast('Failed to copy mobile numbers');
    }
  }; 

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) fetchCustomers(newPage);
  };

  const handleLoadMoreAbandoned = async () => {
    if (!abandonedHasMore || loadingAbandonedMore) return;
    await fetchAbandonedCartsPage({ reset: false, skip: abandonedSkip });
  };

  const handleLoadMoreDeadProducts = async () => {
    if (!deadProductsHasMore || loadingDeadProductsMore) return;
    await fetchDeadProductsData({
      reset: false,
      skip: deadProductsSkip,
      loadPage: deadProductsLoadPage,
    });
  };

  const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));

  const toggleAbandonedCartItems = (customerId) => {
    setExpandedAbandonedCartItems((prev) => ({
      ...prev,
      [customerId]: !prev[customerId],
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      gender: 'all',
      joinedFrom: '',
      joinedTo: '',
      minRevenue: '',
      maxRevenue: '',
      minOrderCount: '',
      maxOrderCount: '',
      hasOrders: '',
      deliveredOrderBucket: '',
      openCart: '',
    });
  };

  const hasActiveFilters = () => {
    return filters.gender !== 'all' ||
      filters.joinedFrom || filters.joinedTo ||
      filters.minRevenue || filters.maxRevenue ||
      filters.minOrderCount || filters.maxOrderCount ||
      filters.hasOrders ||
      filters.deliveredOrderBucket ||
      filters.openCart;
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 1 ? -1 : 1);
    } else {
      setSortBy(field);
      setSortOrder(-1);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <FaSort className="text-gray-300 ml-1" />;
    return sortOrder === 1 ? <FaSortUp className="text-white ml-1" /> : <FaSortDown className="text-white ml-1" />;
  };

  const summaryCards = useMemo(() => {
    const customerCount = pagination.totalCount || customers.length;
    const totalRevenue = customers.reduce((sum, customer) => sum + Number(customer.onlineOrderRevenue || 0), 0);
    const totalOrders = customers.reduce((sum, customer) => sum + Number(customer.onlineOrderCount || 0), 0);
    const avgRevenue = customerCount > 0 ? totalRevenue / customerCount : 0;
    const behaviorSummary = behaviorInsights?.summary || {};
    const journey = behaviorInsights?.journeyMetrics || {};
    const isTodayLive = selectedWindow === 'today';
    const liveCount = isTodayLive
      ? liveInAppCount
      : Number(behaviorSummary.activeSessions || 0);
    const sessionsNote = isTodayLive
      ? `${liveCount.toLocaleString('en-IN')} active in app · ${Number(behaviorSummary.activeCustomers || 0).toLocaleString('en-IN')} active today`
      : `${Number(behaviorSummary.activeCustomers || 0).toLocaleString('en-IN')} active customers · ${Number(behaviorSummary.activeSessions || 0).toLocaleString('en-IN')} sessions`;

    return [
      { title: 'Customers', value: customerCount.toLocaleString('en-IN'), note: 'Active customer records' },
      { title: 'Abandoned Carts', value: abandonedMeta.totalCount.toLocaleString('en-IN'), note: `Idle > ${formatDuration(abandonedMeta.thresholdMinutes)}` },
      { title: 'Customer Events', value: Number(behaviorSummary.totalEvents || 0).toLocaleString('en-IN'), note: sessionsNote },
      { title: 'Avg Revenue / Customer', value: formatCurrency(avgRevenue), note: `${totalOrders.toLocaleString('en-IN')} delivered orders · ${formatDuration(journey.avgViewToPurchaseMins || 0)} avg view-to-order` },
    ];
  }, [pagination.totalCount, customers, behaviorInsights, abandonedMeta, selectedWindow, liveInAppCount]);

  const selectedCustomerBehavior = useMemo(() => {
    if (!selectedCustomerId) return null;
    return customerBehaviorMap[`${selectedCustomerId}_${selectedWindow}`] || null;
  }, [customerBehaviorMap, selectedCustomerId, selectedWindow]);

  const salesOpportunityCards = useMemo(() => {
    return [
      {
        title: 'High Intent, No Purchase',
        value: Number(behaviorInsights?.watchlists?.highIntentNoPurchase?.length || 0),
        tone: 'rose',
        note: 'Target with urgency offers',
      },
      {
        title: 'Churn Risk',
        value: Number(behaviorInsights?.watchlists?.churnRiskCustomers?.length || 0),
        tone: 'amber',
        note: 'Reactivation campaigns',
      },
      {
        title: 'High Value Active',
        value: Number(behaviorInsights?.watchlists?.highValueActiveCustomers?.length || 0),
        tone: 'emerald',
        note: 'Upsell and loyalty',
      },
      {
        title: 'Drop-off Products',
        value: Number(behaviorInsights?.topDropOffProducts?.length || 0),
        tone: 'indigo',
        note: 'Fix conversion blockers',
      },
    ];
  }, [behaviorInsights]);

  const funnelData = useMemo(() => {
    const summary = behaviorInsights?.summary || {};
    const views = Number(summary.productViews || 0);
    const carts = Number(summary.cartAdditions || 0);
    const purchases = Number(summary.purchases || 0);
    return [
      { value: Math.max(views, 0), name: 'Product Views', fill: '#6366f1' },
      { value: Math.max(carts, 0), name: 'Cart Adds', fill: '#0ea5e9' },
      { value: Math.max(purchases, 0), name: 'Purchases', fill: '#10b981' },
    ];
  }, [behaviorInsights]);

  const segmentChartData = useMemo(() => {
    const counts = behaviorInsights?.segments?.counts || {};
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value: Number(value || 0) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [behaviorInsights]);

  const productHealthData = useMemo(() => {
    const sourceMap = new Map();
    [...(behaviorInsights?.topProducts || []), ...(behaviorInsights?.topDropOffProducts || [])].forEach((product) => {
      const id = String(product.productId);
      if (!sourceMap.has(id)) sourceMap.set(id, product);
    });
    return Array.from(sourceMap.values())
      .map(buildProductHealthProfile)
      .sort((a, b) => b.performanceScore - a.performanceScore);
  }, [behaviorInsights]);

  const productHealthSelectOptions = useMemo(() => (
    productHealthData.map((product) => ({
      value: String(product.productId),
      label: product.name,
      performanceScore: product.performanceScore,
      tierLabel: product.tierLabel,
    }))
  ), [productHealthData]);

  const selectedHealthProductOptions = useMemo(() => {
    const selectedSet = new Set(selectedHealthProductIds.map(String));
    return productHealthSelectOptions.filter((option) => selectedSet.has(String(option.value)));
  }, [productHealthSelectOptions, selectedHealthProductIds]);

  useEffect(() => {
    if (!selectedHealthProductIds.length) return;
    const validIds = new Set(productHealthData.map((item) => String(item.productId)));
    const next = selectedHealthProductIds.filter((id) => validIds.has(String(id)));
    if (next.length !== selectedHealthProductIds.length) {
      setSelectedHealthProductIds(next);
    }
  }, [selectedHealthProductIds, productHealthData]);

  const productHealthFilteredData = useMemo(() => {
    let rows = productHealthData;
    if (productHealthTierFilter !== 'all') {
      rows = rows.filter((item) => item.tier === productHealthTierFilter);
    }
    if (selectedHealthProductIds.length > 0) {
      const selectedSet = new Set(selectedHealthProductIds.map(String));
      rows = rows.filter((item) => selectedSet.has(String(item.productId)));
    }
    return rows;
  }, [productHealthData, productHealthTierFilter, selectedHealthProductIds]);

  const productHealthSummary = useMemo(() => {
    const rows = productHealthData;
    const starCount = rows.filter((item) => item.tier === 'star').length;
    const atRiskCount = rows.filter((item) => item.tier === 'at_risk' || item.tier === 'high_traffic_low_sale').length;
    const cartStuckCount = rows.filter((item) => item.tier === 'cart_stuck').length;
    const avgScore = rows.length
      ? Number((rows.reduce((sum, item) => sum + item.performanceScore, 0) / rows.length).toFixed(1))
      : 0;
    const totalDropOffs = rows.reduce((sum, item) => sum + item.dropOffs, 0);
    return { starCount, atRiskCount, cartStuckCount, avgScore, totalDropOffs, totalProducts: rows.length };
  }, [productHealthData]);

  const productHealthChartData = useMemo(() => (
    productHealthFilteredData.slice(0, 8).map((product) => ({
      name: product.name.length > 18 ? `${product.name.slice(0, 18)}…` : product.name,
      Views: product.views,
      'Cart Adds': product.carts,
      Orders: product.orders,
    }))
  ), [productHealthFilteredData]);

  const selectedBranchPerformanceOption = useMemo(() => (
    branchPerformanceOptions.find((option) => option.value === selectedBranchPerformanceId) || null
  ), [branchPerformanceOptions, selectedBranchPerformanceId]);

  const selectedInstoreBranchOption = useMemo(() => (
    instoreBranchOptions.find((option) => option.value === selectedInstoreBranchId) || null
  ), [instoreBranchOptions, selectedInstoreBranchId]);

  const branchPerformanceSummaryCards = useMemo(() => {
    const summary = branchPerformanceInsights?.summary || {};
    const sales = branchPerformanceInsights?.windowSales || {};
    const peer = branchPerformanceInsights?.peerComparison || {};
    return [
      { label: 'Delivered Revenue', value: formatCurrency(sales.totalRevenue || 0), tone: 'emerald' },
      { label: 'Delivered Orders', value: Number(sales.totalOrders || 0).toLocaleString('en-IN'), tone: 'indigo' },
      { label: 'AOV', value: formatCurrency(sales.averageOrderValue || 0), tone: 'sky' },
      { label: 'Active Customers', value: Number(summary.activeCustomers || 0).toLocaleString('en-IN'), tone: 'violet' },
      { label: 'View → Cart', value: `${Number(summary.viewToCartRate || 0).toFixed(1)}%`, tone: 'amber' },
      { label: 'Revenue Rank', value: peer.selectedBranchRank ? `#${peer.selectedBranchRank}` : '—', tone: 'rose' },
    ];
  }, [branchPerformanceInsights]);

  const branchLeaderboardChartData = useMemo(() => (
    (branchPerformanceInsights?.branchLeaderboard || []).slice(0, 8).map((row) => ({
      name: (row.branchName || 'Branch').length > 14 ? `${row.branchName.slice(0, 14)}…` : row.branchName,
      Revenue: Number(row.revenue || 0),
      Orders: Number(row.orders || 0),
    }))
  ), [branchPerformanceInsights]);

  const branchSegmentChartData = useMemo(() => {
    const counts = branchPerformanceInsights?.segments?.counts || {};
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value: Number(value || 0) }))
      .sort((a, b) => b.value - a.value);
  }, [branchPerformanceInsights]);

  const branchProductHealthData = useMemo(() => (
    (branchPerformanceInsights?.topProducts || [])
      .map((product) => buildProductHealthProfile({
        productId: product.productId,
        productName: product.productName,
        views: product.views,
        cartAdditions: product.cartAdditions,
        purchases: product.purchases || product.unitsSold,
      }))
      .sort((a, b) => b.performanceScore - a.performanceScore)
  ), [branchPerformanceInsights]);

  const branchHourlyChartData = useMemo(() => (
    (branchPerformanceInsights?.hourlyHeatmap || []).map((row) => ({
      hourLabelCompact: formatHourLabel(row.hour),
      events: Number(row.events || 0),
      uniqueUsers: Number(row.uniqueUsers || 0),
    }))
  ), [branchPerformanceInsights]);

  const branchWeekdayChartData = useMemo(() => (
    (branchPerformanceInsights?.weekdayHeatmap || []).map((row) => ({
      dayLabel: row.day,
      events: Number(row.events || 0),
      uniqueUsers: Number(row.uniqueUsers || 0),
    }))
  ), [branchPerformanceInsights]);

  const freeCashSummaryCards = useMemo(() => {
    const s = freeCashAnalytics?.summary || {};
    const w = freeCashAnalytics?.windowActivity || {};
    const a = freeCashAnalytics?.adoption || {};
    return [
      { label: 'Outstanding Liability', value: formatCurrency(s.outstandingLiability || 0), tone: 'violet' },
      { label: 'Lifetime Credited', value: formatCurrency(s.totalCredited || 0), tone: 'indigo' },
      { label: 'Lifetime Redeemed', value: formatCurrency(s.totalRedeemed || 0), tone: 'emerald' },
      { label: 'Adoption Rate', value: `${Number(a.adoptionRate || 0).toFixed(1)}%`, tone: 'sky' },
      { label: 'Active Wallets', value: Number(a.activeWalletHolders || 0).toLocaleString('en-IN'), tone: 'amber' },
      { label: 'Window Redeemed', value: formatCurrency(w.redeemed || 0), tone: 'rose' },
      { label: 'FC Orders', value: Number(w.ordersWithFreeCash || 0).toLocaleString('en-IN'), tone: 'cyan' },
      { label: 'Never Used', value: Number(a.inactiveUsers || 0).toLocaleString('en-IN'), tone: 'gray' },
    ];
  }, [freeCashAnalytics]);

  const freeCashAdoptionChartData = useMemo(() => (
    (freeCashAnalytics?.adoptionChart || []).map((row) => ({
      name: row.name,
      value: Number(row.value || 0),
    }))
  ), [freeCashAnalytics]);

  const freeCashSourceChartData = useMemo(() => (
    (freeCashAnalytics?.windowSourceBreakdown || []).map((row) => ({
      name: row.label || row.source,
      amount: Number(row.amount || 0),
      count: Number(row.count || 0),
    }))
  ), [freeCashAnalytics]);

  const freeCashHourlyChartData = useMemo(() => (
    (freeCashAnalytics?.hourlyTrend || []).map((row) => ({
      hourLabel: row.hourLabel,
      Credits: Number(row.credits || 0),
      Redeemed: Number(row.debits || 0),
    }))
  ), [freeCashAnalytics]);

  const freeCashBranchChartData = useMemo(() => (
    (freeCashAnalytics?.branchFreeCash || []).slice(0, 8).map((row) => ({
      name: (row.branchName || 'Branch').length > 12 ? `${row.branchName.slice(0, 12)}…` : row.branchName,
      Discount: Number(row.discountTotal || 0),
      Orders: Number(row.orders || 0),
    }))
  ), [freeCashAnalytics]);

  const freeCashNotificationCards = useMemo(() => {
    const n = freeCashAnalytics?.notificationInsights || {};
    return [
      { label: 'Notifications Sent', value: Number(n.totalSent || 0).toLocaleString('en-IN') },
      { label: 'Customers Notified', value: Number(n.uniqueNotified || 0).toLocaleString('en-IN') },
      { label: 'Push Delivered', value: `${Number(n.pushDelivered || 0).toLocaleString('en-IN')} (${Number(n.pushDeliveryRate || 0).toFixed(0)}%)` },
      { label: 'Push Failed', value: Number(n.pushFailed || 0).toLocaleString('en-IN') },
      { label: 'Redeemed After Alert', value: `${Number(n.notifiedUsedAfter || 0).toLocaleString('en-IN')} (${Number(n.notificationToUsageRate || 0).toFixed(0)}%)` },
      { label: 'Notified · No Use', value: Number(n.notifiedNoUsageCount || 0).toLocaleString('en-IN') },
      { label: 'Wallet Value at Risk', value: formatCurrency(n.walletBalanceAtRisk || 0) },
      { label: 'Expiry Reminders', value: `${Number(n.expiryRemindersSent || 0).toLocaleString('en-IN')} sent · ${Number(n.expiryReminderPushSuccess || 0).toLocaleString('en-IN')} pushed` },
    ];
  }, [freeCashAnalytics]);

  const notifiedNoUsageTotalPages = useMemo(() => (
    Math.max(1, Math.ceil(Number(notifiedNoUsageMeta.totalCount || 0) / NOTIFIED_NO_USAGE_PAGE_SIZE))
  ), [notifiedNoUsageMeta.totalCount]);

  const instoreSummaryCards = useMemo(() => {
    const sm = instoreSalesAnalytics?.summary || {};
    const ret = instoreSalesAnalytics?.returns?.summary || {};
    return [
      { label: 'Net POS Revenue', value: formatCurrency(sm.totalRevenue || 0), hint: sm.refundedAmount > 0 ? `Refunded ${formatCurrency(sm.refundedAmount)}` : null },
      { label: 'Bills', value: Number(sm.totalOrders || 0).toLocaleString('en-IN') },
      { label: 'Units Sold', value: Number(sm.totalUnits || 0).toLocaleString('en-IN'), hint: 'Net of returns' },
      { label: 'Avg Bill', value: formatCurrency(sm.avgOrderValue || 0) },
      { label: 'Returns Refund', value: formatCurrency(sm.returnsRefund || ret.totalRefund || 0), tone: 'rose' },
      { label: 'Returned Units', value: Number(sm.returnsQty || ret.totalQty || 0).toLocaleString('en-IN'), tone: 'rose' },
      { label: 'Restocked', value: Number(sm.returnsRestockedQty || ret.restockedQty || 0).toLocaleString('en-IN'), tone: 'emerald' },
      { label: 'Damaged', value: Number(sm.returnsDamagedQty || ret.damagedQty || 0).toLocaleString('en-IN'), tone: 'amber' },
      { label: 'Avg Basket', value: `${Number(sm.avgBasketSize || 0).toFixed(1)} items` },
      { label: 'Golden Hour', value: sm.peakHourLabel || '—' },
      { label: 'Best Day', value: sm.peakDayLabel || '—' },
      { label: 'SKUs Sold', value: Number(sm.uniqueProducts || 0).toLocaleString('en-IN') },
      { label: '₹ / Unit', value: formatCurrency(sm.revenuePerUnit || 0) },
      { label: 'Coupon Bills', value: `${Number(sm.couponShare || 0).toFixed(0)}%` },
    ];
  }, [instoreSalesAnalytics]);

  const instoreLoyaltySummaryCards = useMemo(() => {
    const sm = instoreCustomerAnalytics?.summary || {};
    return [
      { label: 'Loyalty customers', value: Number(sm.loyaltyCustomers || 0).toLocaleString('en-IN') },
      { label: 'Repeat customers', value: Number(sm.repeatCustomers || 0).toLocaleString('en-IN') },
      { label: 'Mobile capture', value: `${Number(sm.captureRate || 0).toFixed(0)}%` },
      { label: 'Offer spend', value: formatCurrency(sm.offerCashSpend || 0) },
      { label: 'Free gifts worth', value: formatCurrency(sm.freeGiftWorth || 0) },
      { label: 'Offer attach', value: `${Number(sm.offerAttachRate || 0).toFixed(0)}%` },
      { label: 'Avg loyalty spend', value: formatCurrency(sm.avgLoyaltySpend || 0) },
      { label: 'Active offers', value: Number(sm.activeOffers || 0).toLocaleString('en-IN') },
    ];
  }, [instoreCustomerAnalytics]);

  const instoreLoyaltySplitChart = useMemo(() => (
    (instoreCustomerAnalytics?.behaviourSplit || []).map((row) => ({
      name: row.name,
      value: Number(row.revenue || 0),
      orders: Number(row.orders || 0),
    })).filter((row) => row.value > 0 || row.orders > 0)
  ), [instoreCustomerAnalytics]);

  const instoreOfferRuleChart = useMemo(() => (
    (instoreCustomerAnalytics?.ruleTypeBreakdown || []).slice(0, 8).map((row) => ({
      label: row.ruleLabel || row.ruleType,
      Redemptions: Number(row.redemptions || 0),
      Investment: Number(row.totalInvestment || 0),
    }))
  ), [instoreCustomerAnalytics]);

  const selectedPosSegmentRows = useMemo(() => {
    const key = selectedPosLoyaltySegment || 'vip';
    return instoreCustomerAnalytics?.segments?.[key] || [];
  }, [instoreCustomerAnalytics, selectedPosLoyaltySegment]);

  const instoreSubCategoryChartData = useMemo(() => (
    (instoreSalesAnalytics?.subCategoryPerformance || []).slice(0, 8).map((c) => ({
      label: c.label,
      Revenue: Number(c.revenue || 0),
      Units: Number(c.units || 0),
    }))
  ), [instoreSalesAnalytics]);

  const deadStockFiltered = useMemo(() => {
    const all = instoreSalesAnalytics?.deadStockFull || [];
    const q = deadStockSearch.trim().toLowerCase();
    if (!q) return all;
    return all.filter((p) => (
      (p.productName || '').toLowerCase().includes(q)
      || (p.sku || '').toLowerCase().includes(q)
      || (p.category || '').toLowerCase().includes(q)
    ));
  }, [instoreSalesAnalytics, deadStockSearch]);

  const toggleDeadProductAccordion = useCallback((key, isCurrentlyOpen) => {
    setDeadProductsAccordionOpen((prev) => ({ ...prev, [key]: !isCurrentlyOpen }));
  }, []);

  const deadProductsFiltered = useMemo(() => {
    const q = deadProductsSearch.trim().toLowerCase();
    if (!q) return deadProducts;
    return deadProducts.filter((p) => (
      (p.productName || '').toLowerCase().includes(q)
      || (p.sku || '').toLowerCase().includes(q)
      || (p.category || '').toLowerCase().includes(q)
      || (p.categoryName || '').toLowerCase().includes(q)
      || (p.subCategoryName || '').toLowerCase().includes(q)
      || (p.deadOptions || []).some((opt) => (opt.optionLabel || '').toLowerCase().includes(q))
    ));
  }, [deadProducts, deadProductsSearch]);

  const deadProductsSummary = deadProductsMeta?.summary || {};

  const instoreHourlyChartData = useMemo(() => (
    (instoreSalesAnalytics?.hourly || []).map((h) => ({
      label: h.hourLabel,
      Revenue: Number(h.revenue || 0),
      Units: Number(h.units || 0),
      Bills: Number(h.orders || 0),
    }))
  ), [instoreSalesAnalytics]);

  const instoreDowChartData = useMemo(() => (
    (instoreSalesAnalytics?.dayOfWeek || []).map((d) => ({
      label: d.label,
      Revenue: Number(d.revenue || 0),
      Units: Number(d.units || 0),
    }))
  ), [instoreSalesAnalytics]);

  const instoreDailyChartData = useMemo(() => (
    (instoreSalesAnalytics?.dailyTimeline || []).map((d) => ({
      label: d.label,
      Revenue: Number(d.revenue || 0),
      Bills: Number(d.orders || 0),
      Units: Number(d.units || 0),
    }))
  ), [instoreSalesAnalytics]);

  const instorePaymentChartData = useMemo(() => (
    (instoreSalesAnalytics?.paymentMix || []).map((p) => ({
      name: p.method,
      value: Number(p.revenue || 0),
      share: Number(p.share || 0),
    }))
  ), [instoreSalesAnalytics]);

  const instoreCategoryChartData = useMemo(() => (
    (instoreSalesAnalytics?.categoryPerformance || []).slice(0, 8).map((c) => ({
      label: c.category,
      Revenue: Number(c.revenue || 0),
      Units: Number(c.units || 0),
    }))
  ), [instoreSalesAnalytics]);

  const timeIntelligenceCards = useMemo(() => {
    const bestHour = (behaviorInsights?.peakHours || [])[0];
    const weekdayHeatmap = behaviorInsights?.weekdayHeatmap || [];
    const bestDay = [...weekdayHeatmap].sort((a, b) => Number(b.events || 0) - Number(a.events || 0))[0];
    return {
      bestHour: bestHour ? formatHourLabel(bestHour.hour) : '—',
      bestDay: bestDay?.day || '—',
      bestHourEvents: Number(bestHour?.events || 0),
      bestDayEvents: Number(bestDay?.events || 0),
    };
  }, [behaviorInsights]);

  const customerJourneySteps = useMemo(() => {
    const events = selectedCustomerBehavior?.timeline || [];
    const mapped = events
      .slice(0, 10)
      .map((event) => event.eventType)
      .filter(Boolean)
      .map((name) => String(name).replace(/_/g, ' ').toUpperCase());
    return mapped.length ? mapped : ['APP OPEN', 'BROWSE', 'PRODUCT VIEW', 'CART', 'CHECKOUT', 'PURCHASE'];
  }, [selectedCustomerBehavior]);

  const customerDna = useMemo(() => {
    const customerInfo = selectedCustomerBehavior?.customer || {};
    const summary = selectedCustomerBehavior?.summary || {};
    const timeline = selectedCustomerBehavior?.timeline || [];
    const productViews = timeline.filter((x) => x.eventType === 'product_view').length;
    const searches = timeline.filter((x) => x.eventType === 'search').length;
    const cartAdds = timeline.filter((x) => x.eventType === 'cart_addition').length;
    const purchases = timeline.filter((x) => x.eventType === 'purchase').length;
    const avgOrderValue = summary.orders > 0 ? Number(summary.revenue || 0) / Number(summary.orders || 1) : 0;
    const discountSensitivity = cartAdds > 0 ? Math.min(100, (searches / cartAdds) * 40 + (productViews / Math.max(cartAdds, 1)) * 10) : 0;
    const churnRisk = summary.orders > 0 && Number(summary.openCartItems || 0) === 0 && Number(summary.avgSessionMins || 0) < 1.5 ? 'High' : 'Medium';
    return {
      identity: {
        name: customerInfo.name || '—',
        mobile: customerInfo.mobile || '—',
        email: customerInfo.email || '—',
      },
      behavior: {
        productViews,
        searches,
        cartAdds,
        purchases,
        avgSessionMins: Number(summary.avgSessionMins || 0),
      },
      financial: {
        totalSpend: Number(summary.revenue || 0),
        averageOrderValue: Number(avgOrderValue || 0),
        orders: Number(summary.orders || 0),
      },
      retention: {
        churnRisk,
        inactivitySignal: Number(summary.avgSessionMins || 0) < 2 ? 'Low Engagement' : 'Active',
        loyaltySignal: Number(summary.orders || 0) >= 3 ? 'Loyal' : 'Developing',
        discountSensitivity: Number(discountSensitivity.toFixed(1)),
      },
    };
  }, [selectedCustomerBehavior]);

  const searchAnalytics = useMemo(() => {
    const totalEvents = Number(behaviorInsights?.summary?.totalEvents || 0);
    const searchEvents = Number(behaviorInsights?.summary?.searchEvents || 0);
    const searchToPurchaseRatio = Number(behaviorInsights?.summary?.purchases || 0) > 0
      ? Number((searchEvents / Number(behaviorInsights?.summary?.purchases || 1)).toFixed(2))
      : 0;
    const noResultProxy = Math.max(0, searchEvents - Number(behaviorInsights?.summary?.cartAdditions || 0));
    return {
      searchEvents,
      searchShare: totalEvents > 0 ? Number(((searchEvents / totalEvents) * 100).toFixed(1)) : 0,
      searchToPurchaseRatio,
      noResultProxy,
    };
  }, [behaviorInsights]);

  const operationalRiskSignals = useMemo(() => {
    const churnRisk = Number(behaviorInsights?.watchlists?.churnRiskCustomers?.length || 0);
    const highIntentNoPurchase = Number(behaviorInsights?.watchlists?.highIntentNoPurchase?.length || 0);
    const abandoned = Number(abandonedMeta?.totalCount || 0);
    return [
      { name: 'Churn Risk', value: churnRisk },
      { name: 'High Intent No Purchase', value: highIntentNoPurchase },
      { name: 'Abandoned Carts', value: abandoned },
    ];
  }, [behaviorInsights, abandonedMeta]);

  const predictiveInsights = useMemo(() => {
    const summary = behaviorInsights?.summary || {};
    const journey = behaviorInsights?.journeyMetrics || {};
    const highIntent = Number(behaviorInsights?.watchlists?.highIntentNoPurchase?.length || 0);
    const churn = Number(behaviorInsights?.watchlists?.churnRiskCustomers?.length || 0);
    const avgViewToPurchase = Number(journey.avgViewToPurchaseMins || 0);

    const nextHourPotentialOrders = Math.max(
      0,
      Math.round((highIntent * 0.25) + (Number(summary.cartToPurchaseRate || 0) / 100) * 2),
    );
    const churnRiskPressure = churn >= 12 ? 'High' : churn >= 5 ? 'Medium' : 'Low';
    const conversionVelocity = avgViewToPurchase > 0 && avgViewToPurchase <= 20 ? 'Fast' : avgViewToPurchase <= 45 ? 'Moderate' : 'Slow';

    return {
      nextHourPotentialOrders,
      churnRiskPressure,
      conversionVelocity,
      expectedRevenueLiftLabel: `${Math.max(3, Math.min(18, Math.round((Number(summary.viewToCartRate || 0) * 0.12) + (Number(summary.cartToPurchaseRate || 0) * 0.08))))}%`,
    };
  }, [behaviorInsights]);

  const actionQueue = useMemo(() => {
    const highIntent = behaviorInsights?.watchlists?.highIntentNoPurchase || [];
    const churn = behaviorInsights?.watchlists?.churnRiskCustomers || [];
    const dropOff = behaviorInsights?.topDropOffProducts || [];

    return [
      {
        priority: 'P1',
        title: 'Recover High Intent Carts',
        impact: `${highIntent.length} customers`,
        action: `Send ${formatDuration(30)} urgency offer + push reminder`,
      },
      {
        priority: 'P1',
        title: 'Prevent Churn',
        impact: `${churn.length} at-risk customers`,
        action: 'Launch win-back campaign with tailored coupon',
      },
      {
        priority: 'P2',
        title: 'Fix Product Drop-offs',
        impact: `${dropOff.slice(0, 3).length} products`,
        action: 'Improve image + pricing + first fold copy',
      },
      {
        priority: 'P3',
        title: 'Boost Peak-time Conversion',
        impact: `${timeIntelligenceCards.bestHour} peak hour`,
        action: 'Schedule offer and homepage placements before peak',
      },
    ];
  }, [behaviorInsights, timeIntelligenceCards]);

  const eventCoverage = useMemo(() => {
    const timeline = selectedCustomerBehavior?.timeline || [];
    const set = new Set(timeline.map((event) => String(event.eventType || 'unknown')));
    return Array.from(set).sort();
  }, [selectedCustomerBehavior]);

  const dailyNoOrderCustomers = useMemo(() => {
    return behaviorInsights?.watchlists?.engagedNoOrderDaily || [];
  }, [behaviorInsights]);

  const pincodeAnalytics = useMemo(() => {
    const source = behaviorInsights?.pincodeAnalytics || {};
    return {
      top: Array.isArray(source.topPincodes) ? source.topPincodes : [],
      least: Array.isArray(source.leastPincodes) ? source.leastPincodes : [],
    };
  }, [behaviorInsights]);

  const livePulseMetrics = useMemo(() => {
    const summary = behaviorInsights?.summary || {};
    const journey = behaviorInsights?.journeyMetrics || {};
    const windowSales = behaviorInsights?.windowSales || {};
    const cancelled = behaviorInsights?.cancelledOrders || {};
    const isTodayLive = selectedWindow === 'today';
    const totalEvents = Number(summary.totalEvents || 0);
    const activeCustomers = Number(summary.activeCustomers || 0);
    const productViews = Number(summary.productViews || 0);
    const cartAdditions = Number(summary.cartAdditions || 0);
    const purchases = Number(summary.purchases || 0);
    const activeSessions = isTodayLive
      ? liveInAppCount
      : Number(summary.activeSessions || 0);
    const historicalTrackedSessions = isTodayLive
      ? Number(summary.historicalTrackedSessions || summary.activeSessions || 0)
      : null;
    const viewToCart = Number(summary.viewToCartRate || 0);
    const cartToOrder = Number(summary.cartToPurchaseRate || 0);
    const overall = productViews > 0 ? Number(((purchases / productViews) * 100).toFixed(2)) : 0;
    return {
      totalEvents,
      activeCustomers,
      activeSessions,
      isTodayLive,
      historicalTrackedSessions,
      productViews,
      cartAdditions,
      purchases,
      viewToCart,
      cartToOrder,
      overallConversion: overall,
      totalRevenue: Number(windowSales.totalRevenue || 0),
      orderCount: Number(windowSales.totalOrders || 0),
      uniqueBuyers: Number(windowSales.uniqueBuyers || 0),
      avgOrderValue: Number(windowSales.averageOrderValue || 0),
      ordersPerDay: Number(windowSales.ordersPerDay || 0),
      revenuePerDay: Number(windowSales.revenuePerDay || 0),
      avgViewToPurchase: Number(journey.avgViewToPurchaseMins || 0),
      cancelledOrderCount: Number(cancelled.totalOrders || 0),
      cancelledOrderValue: Number(cancelled.totalValue || 0),
      cancelledUniqueCustomers: Number(cancelled.uniqueCustomers || 0),
      cancelledAvgOrderValue: Number(cancelled.averageOrderValue || 0),
      cancelledCancelRate: Number(cancelled.cancelRate || 0),
      cancelledOrdersPerDay: Number(cancelled.ordersPerDay || 0),
    };
  }, [behaviorInsights, selectedWindow, liveInAppCount]);

  const aggregateHourlyHeatmap = useMemo(() => {
    const data = (behaviorInsights?.hourlyHeatmap || []).map((row) => ({
      ...row,
      hourLabel: formatHourLabel(row.hour),
      eventBreakdown: Array.isArray(row.eventBreakdown) ? row.eventBreakdown : [],
      customers: Array.isArray(row.customers) ? row.customers : [],
    }));
    const max = data.reduce((m, row) => Math.max(m, Number(row.events || 0)), 0);
    return { data, max };
  }, [behaviorInsights]);

  const selectedHourlyPulseDetail = useMemo(() => {
    if (selectedHourlyPulseHour == null) return null;
    return aggregateHourlyHeatmap.data.find((row) => Number(row.hour) === Number(selectedHourlyPulseHour)) || null;
  }, [aggregateHourlyHeatmap.data, selectedHourlyPulseHour]);

  const copyHourlyPulseMobiles = useCallback((customers = []) => {
    const mobiles = customers.map((customer) => customer.mobile).filter(Boolean);
    if (!mobiles.length) {
      showToast('No mobile numbers found for this hour');
      return;
    }
    navigator.clipboard.writeText(mobiles.join(', '))
      .then(() => showToast(`Copied ${mobiles.length} mobile number${mobiles.length === 1 ? '' : 's'}`))
      .catch(() => showToast('Failed to copy mobile numbers'));
  }, []);

  const aggregateWeekdayHeatmap = useMemo(() => {
    const data = behaviorInsights?.weekdayHeatmap || [];
    const max = data.reduce((m, row) => Math.max(m, Number(row.events || 0)), 0);
    return { data, max };
  }, [behaviorInsights]);

  const topPerformingProducts = useMemo(() => {
    const products = behaviorInsights?.topProducts || [];
    return products
      .map((p) => {
        const views = Number(p.views || 0);
        const carts = Number(p.cartAdditions || 0);
        const orders = Number(p.purchases || 0);
        const conversionRate = views > 0 ? Number(((orders / views) * 100).toFixed(1)) : 0;
        const cartConversionRate = carts > 0 ? Number(((orders / carts) * 100).toFixed(1)) : 0;
        return {
          productId: p.productId,
          productName: p.productName || 'Unknown Product',
          views,
          carts,
          orders,
          conversionRate,
          cartConversionRate,
          tag: orders > 0 && conversionRate >= 8 ? 'star' : carts > views * 0.4 ? 'rising' : views > 0 && orders === 0 ? 'risk' : 'normal',
        };
      })
      .sort((a, b) => b.views - a.views)
      .slice(0, 8);
  }, [behaviorInsights]);

  const conversionFunnelStages = useMemo(() => {
    const { productViews, cartAdditions, purchases } = livePulseMetrics;
    const base = Math.max(productViews, 1);
    return [
      { label: 'Product Views', value: productViews, pct: 100, color: 'from-indigo-500 to-violet-500' },
      { label: 'Cart Additions', value: cartAdditions, pct: Math.min(100, Math.round((cartAdditions / base) * 100)), color: 'from-sky-500 to-cyan-500' },
      { label: 'Purchases', value: purchases, pct: Math.min(100, Math.round((purchases / base) * 100)), color: 'from-emerald-500 to-teal-500' },
    ];
  }, [livePulseMetrics]);

  const eventTypeMix = useMemo(() => {
    const summary = behaviorInsights?.summary || {};
    const entries = [
      { name: 'Views', value: Number(summary.productViews || 0), color: '#6366f1' },
      { name: 'Cart Adds', value: Number(summary.cartAdditions || 0), color: '#0ea5e9' },
      { name: 'Purchases', value: Number(summary.purchases || 0), color: '#10b981' },
      { name: 'Searches', value: Number(summary.searchEvents || 0), color: '#f59e0b' },
      { name: 'Favorites', value: Number(summary.favoriteAdds || 0), color: '#ef4444' },
    ].filter((e) => e.value > 0);
    return entries;
  }, [behaviorInsights]);

  const abandonedAgingChart = useMemo(() => {
    const data = behaviorInsights?.abandonedCartAging || [];
    const order = ['<30m', '30-60m', '1-3h', '3-12h', '>12h'];
    return order
      .map((bucket) => {
        const row = data.find((d) => d.bucket === bucket);
        return {
          bucket,
          customers: Number(row?.customers || 0),
          cartValue: Number(row?.cartValue || 0),
        };
      })
      .filter((row) => row.customers > 0 || row.cartValue > 0);
  }, [behaviorInsights]);

  const journeySpeedSignals = useMemo(() => {
    const journey = behaviorInsights?.journeyMetrics || {};
    return [
      { label: 'View → Cart', value: Number(journey.avgViewToCartMins || 0), color: 'indigo' },
      { label: 'Cart → Order', value: Number(journey.avgCartToPurchaseMins || 0), color: 'sky' },
      { label: 'View → Order', value: Number(journey.avgViewToPurchaseMins || 0), color: 'emerald' },
    ];
  }, [behaviorInsights]);

  const customerHealthMix = useMemo(() => {
    const topCustomers = behaviorInsights?.topCustomers || [];
    const buckets = { hot: 0, warm: 0, cold: 0, dormant: 0 };
    topCustomers.forEach((c) => {
      const intent = Number(c.intentScore || 0);
      const purchases = Number(c.purchases || 0);
      if (intent >= 70 || purchases >= 2) buckets.hot += 1;
      else if (intent >= 40) buckets.warm += 1;
      else if (intent >= 15) buckets.cold += 1;
      else buckets.dormant += 1;
    });
    const total = topCustomers.length || 1;
    return [
      { label: 'Hot', count: buckets.hot, pct: Math.round((buckets.hot / total) * 100), color: 'bg-rose-500', textColor: 'text-rose-700', bgColor: 'bg-rose-50', borderColor: 'border-rose-200' },
      { label: 'Warm', count: buckets.warm, pct: Math.round((buckets.warm / total) * 100), color: 'bg-amber-500', textColor: 'text-amber-700', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
      { label: 'Cold', count: buckets.cold, pct: Math.round((buckets.cold / total) * 100), color: 'bg-sky-500', textColor: 'text-sky-700', bgColor: 'bg-sky-50', borderColor: 'border-sky-200' },
      { label: 'Dormant', count: buckets.dormant, pct: Math.round((buckets.dormant / total) * 100), color: 'bg-gray-400', textColor: 'text-gray-700', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' },
    ];
  }, [behaviorInsights]);

  const salesVelocityCard = useMemo(() => {
    return {
      ordersPerDay: livePulseMetrics.ordersPerDay || 0,
      revenuePerDay: livePulseMetrics.revenuePerDay || 0,
      avgOrderValue: livePulseMetrics.avgOrderValue || 0,
      uniqueBuyers: livePulseMetrics.uniqueBuyers || 0,
      windowDays: behaviorInsights?.activityWindow?.days || 1,
      windowLabel: behaviorInsights?.activityWindow?.label || getWindowLabel(selectedWindow),
    };
  }, [behaviorInsights, livePulseMetrics, selectedWindow]);

  const getResolvedPreferredAddress = useCallback((customerId, addressObj) => {
    if (!addressObj) return '—';
    if (addressObj.address && !/^[a-f\d]{24}$/i.test(String(addressObj.address))) {
      return addressObj.address;
    }
    const addressId = addressObj.addressId;
    if (!addressId) return addressObj.address || '—';
    const cacheKey = `${customerId || 'unknown'}_${addressId}`;
    const resolved = addressByIdMap[cacheKey];
    if (resolved?.address) return resolved.address;
    return `Resolving address (${String(addressId).slice(0, 8)}...)`;
  }, [addressByIdMap]);

  const abandonedCartsEnhanced = useMemo(() => {
    return (abandonedCarts || []).map((entry) => {
      const idleMinutes = Number(entry?.cart?.idleMinutes || 0);
      const cartValue = Number(entry?.cart?.cartValue || 0);
      const itemCount = Number(entry?.cart?.itemCount || 0);
      const urgencyScore = Math.min(100, (idleMinutes * 0.35) + (cartValue / 20) + (itemCount * 4));
      const priority = urgencyScore >= 85 ? 'critical' : urgencyScore >= 60 ? 'high' : urgencyScore >= 35 ? 'medium' : 'low';
      const potentialRecovery = Number((cartValue * (priority === 'critical' ? 0.75 : priority === 'high' ? 0.6 : priority === 'medium' ? 0.45 : 0.3)).toFixed(2));
      return {
        ...entry,
        abandonedMetrics: {
          urgencyScore: Number(urgencyScore.toFixed(1)),
          priority,
          potentialRecovery,
          recommendedAction: priority === 'critical'
            ? 'Send instant push + coupon'
            : priority === 'high'
              ? 'Send push reminder with urgency'
              : priority === 'medium'
                ? 'Show personalized recommendations'
                : 'Retarget via gentle reminder',
        },
      };
    });
  }, [abandonedCarts]);

  const filteredAbandonedCarts = useMemo(() => {
    if (abandonedPriorityFilter === 'all') return abandonedCartsEnhanced;
    return abandonedCartsEnhanced.filter((entry) => entry.abandonedMetrics?.priority === abandonedPriorityFilter);
  }, [abandonedCartsEnhanced, abandonedPriorityFilter]);

  const abandonedIntelligenceSummary = useMemo(() => {
    const rows = abandonedCartsEnhanced;
    const count = rows.length;
    const totalValue = rows.reduce((sum, row) => sum + Number(row?.cart?.cartValue || 0), 0);
    const avgIdle = count > 0 ? rows.reduce((sum, row) => sum + Number(row?.cart?.idleMinutes || 0), 0) / count : 0;
    const criticalCount = rows.filter((row) => row.abandonedMetrics?.priority === 'critical').length;
    const highCount = rows.filter((row) => row.abandonedMetrics?.priority === 'high').length;
    const potentialRecovery = rows.reduce((sum, row) => sum + Number(row?.abandonedMetrics?.potentialRecovery || 0), 0);
    return {
      totalValue,
      avgIdle,
      criticalCount,
      highCount,
      potentialRecovery,
      loadedCount: rows.length,
    };
  }, [abandonedCartsEnhanced]);

  const recoveryCandidateRanking = useMemo(() => {
    const abandonedCandidates = abandonedCartsEnhanced.map((entry) => {
      const mobileOrEmail = entry.customer?.mobile || entry.customer?.email || '';
      const score = Math.min(
        100,
        Number(entry.abandonedMetrics?.urgencyScore || 0) +
        (Number(entry.cart?.cartValue || 0) / 80) +
        (Number(entry.cart?.itemCount || 0) * 2),
      );
      return {
        key: `abandoned_${entry.customerId}`,
        customerId: entry.customerId,
        customerName: entry.customer?.name || 'Guest Customer',
        contact: mobileOrEmail,
        segment: 'Abandoned Cart',
        score: Number(score.toFixed(1)),
        valueAtRisk: Number(entry.cart?.cartValue || 0),
        recommendedAction: entry.abandonedMetrics?.recommendedAction || 'Send reminder',
      };
    });

    const highIntentCandidates = (behaviorInsights?.watchlists?.highIntentNoPurchase || []).map((entry) => {
      const score = Math.min(
        100,
        Number(entry.intentScore || 0) * 0.9 +
        (Number(entry.openCartValue || 0) / 120) +
        (Number(entry.cartAdditions || 0) * 2.5),
      );
      return {
        key: `high_intent_${entry.customerId}`,
        customerId: entry.customerId,
        customerName: entry.customerName || 'Customer',
        contact: entry.customerMobile || entry.customerEmail || '',
        segment: 'High Intent',
        score: Number(score.toFixed(1)),
        valueAtRisk: Number(entry.openCartValue || 0),
        recommendedAction: `Send personalized offer in <${formatDuration(30)}`,
      };
    });

    return [...abandonedCandidates, ...highIntentCandidates]
      .sort((a, b) => b.score - a.score)
      .slice(0, 12);
  }, [abandonedCartsEnhanced, behaviorInsights]);

  const revenueLeakRadar = useMemo(() => {
    const topDropOff = behaviorInsights?.topDropOffProducts || [];
    const highIntentNoPurchaseCount = Number(behaviorInsights?.watchlists?.highIntentNoPurchase?.length || 0);
    const churnRiskCount = Number(behaviorInsights?.watchlists?.churnRiskCustomers?.length || 0);
    const abandonedValue = Number(abandonedIntelligenceSummary.totalValue || 0);
    const recoveryPotential = Number(abandonedIntelligenceSummary.potentialRecovery || 0);
    const dropOffLeak = topDropOff.slice(0, 6).reduce((sum, item) => {
      const views = Number(item.views || 0);
      const conversionGap = Math.max(0, views - Number(item.purchases || 0));
      return sum + conversionGap;
    }, 0);

    const rows = [
      {
        leakType: 'Abandoned Cart Value',
        severity: abandonedValue >= 5000 ? 'Critical' : abandonedValue >= 2000 ? 'High' : 'Medium',
        impact: abandonedValue,
        metric: `${formatCurrency(abandonedValue)} at risk`,
      },
      {
        leakType: 'Recoverable Abandoned Value',
        severity: recoveryPotential >= 3000 ? 'High' : recoveryPotential >= 1200 ? 'Medium' : 'Low',
        impact: recoveryPotential,
        metric: `${formatCurrency(recoveryPotential)} recoverable`,
      },
      {
        leakType: 'High-Intent No Purchase',
        severity: highIntentNoPurchaseCount >= 12 ? 'Critical' : highIntentNoPurchaseCount >= 6 ? 'High' : 'Medium',
        impact: highIntentNoPurchaseCount * 200,
        metric: `${highIntentNoPurchaseCount} customers`,
      },
      {
        leakType: 'Churn Risk Pipeline',
        severity: churnRiskCount >= 10 ? 'High' : churnRiskCount >= 4 ? 'Medium' : 'Low',
        impact: churnRiskCount * 150,
        metric: `${churnRiskCount} customers`,
      },
      {
        leakType: 'Product Drop-off Leak',
        severity: dropOffLeak >= 300 ? 'High' : dropOffLeak >= 120 ? 'Medium' : 'Low',
        impact: dropOffLeak,
        metric: `${dropOffLeak} non-converted interactions`,
      },
    ];

    return rows.sort((a, b) => b.impact - a.impact);
  }, [behaviorInsights, abandonedIntelligenceSummary]);

  const selectedCustomerHourlyWithEvents = useMemo(() => {
    const timeline = selectedCustomerBehavior?.timeline || [];
    const hourMap = {};
    const dayMap = {};

    timeline.forEach((event) => {
      if (!event?.timestamp) return;
      const date = new Date(event.timestamp);
      if (Number.isNaN(date.getTime())) return;
      const hour = getHourInTimezone(date);
      if (hour === null) return;
      const dayOfWeek = date.getDay() + 1;
      const eventType = String(event.eventType || 'unknown');

      hourMap[hour] = hourMap[hour] || {};
      hourMap[hour][eventType] = (hourMap[hour][eventType] || 0) + 1;

      dayMap[dayOfWeek] = dayMap[dayOfWeek] || {};
      dayMap[dayOfWeek][eventType] = (dayMap[dayOfWeek][eventType] || 0) + 1;
    });

    const buildBreakdown = (eventObj = {}, total = 0) => Object.entries(eventObj)
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .map(([eventType, count]) => ({
        eventType,
        count: Number(count || 0),
        percentage: total > 0 ? Number(((Number(count || 0) / total) * 100).toFixed(1)) : 0,
      }));

    const hourEventsMap = new Map(
      (selectedCustomerBehavior?.activityByHour || []).map((entry) => [Number(entry.hour), Number(entry.events || 0)]),
    );

    const hourBreakdownMap = new Map(
      (selectedCustomerBehavior?.activityByHourBreakdown || []).map((entry) => ([
        Number(entry.hour),
        Array.isArray(entry.eventBreakdown) ? entry.eventBreakdown : [],
      ])),
    );

    const hourlyData = Array.from({ length: 24 }, (_, hour) => {
      const total = hourEventsMap.get(hour) || 0;
      const serverBreakdown = (hourBreakdownMap.get(hour) || [])
        .slice()
        .sort((a, b) => Number(b.count || 0) - Number(a.count || 0))
        .map((item) => ({
          eventType: String(item.eventType || 'unknown'),
          count: Number(item.count || 0),
          percentage: total > 0 ? Number(((Number(item.count || 0) / total) * 100).toFixed(1)) : 0,
        }));
      return {
        hour,
        events: total,
        hourLabel: formatHourLabel(hour),
        hourLabelCompact: formatHourLabel(hour),
        eventBreakdown: serverBreakdown.length ? serverBreakdown : buildBreakdown(hourMap[hour], total),
      };
    });

    const dayBreakdownMap = new Map(
      (selectedCustomerBehavior?.activityByDayBreakdown || []).map((entry) => ([
        Number(entry.dayOfWeek),
        Array.isArray(entry.eventBreakdown) ? entry.eventBreakdown : [],
      ])),
    );

    const weekdayData = (selectedCustomerBehavior?.activityByDay || []).map((entry) => {
      const dayOfWeek = Number(entry.dayOfWeek);
      const total = Number(entry.events || 0);
      const serverBreakdown = (dayBreakdownMap.get(dayOfWeek) || [])
        .slice()
        .sort((a, b) => Number(b.count || 0) - Number(a.count || 0))
        .map((item) => ({
          eventType: String(item.eventType || 'unknown'),
          count: Number(item.count || 0),
          percentage: total > 0 ? Number(((Number(item.count || 0) / total) * 100).toFixed(1)) : 0,
        }));
      return {
        ...entry,
        dayLabel: DAY_LABELS[dayOfWeek] || `D${dayOfWeek}`,
        eventBreakdown: serverBreakdown.length ? serverBreakdown : buildBreakdown(dayMap[dayOfWeek], total),
      };
    });

    return { hourlyData, weekdayData };
  }, [selectedCustomerBehavior]);

  return (
    <div className="p-6 bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 min-h-screen">
      <div className="mb-6 flex flex-col gap-4">
        {/* Premium hero header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 text-white shadow-xl">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl" />
          <div className="relative p-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              {activeTab === 'micro' ? (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-medium tracking-wide">
                  <span className="inline-block w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  LIVE • {getWindowLabel(selectedWindow)}
                </div>
              ) : null}
              <h1 className="text-3xl font-bold mt-2 tracking-tight">Customers Intelligence</h1>
              <p className="text-sm text-white/80 mt-1">Deep behavior monitoring, conversion patterns and revenue growth signals.</p>
            </div>
            <div className="flex items-center gap-2 bg-white/15 backdrop-blur border border-white/20 rounded-2xl p-1.5">
              <button
                onClick={() => setActiveTab('micro')}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${activeTab === 'micro' ? 'bg-white text-indigo-700 shadow-lg' : 'text-white/90 hover:bg-white/10'}`}
              >
                <span className="inline-flex items-center gap-2"><FaChartLine /> Micro Analysis</span>
              </button>
              <button
                onClick={() => setActiveTab('customers')}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${activeTab === 'customers' ? 'bg-white text-indigo-700 shadow-lg' : 'text-white/90 hover:bg-white/10'}`}
              >
                <span className="inline-flex items-center gap-2"><FaUsers /> Customers</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          {activeTab === 'customers' && (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email or mobile"
                  className="px-4 py-2 border rounded-md w-80"
                />
                <FaSearch className="absolute right-3 top-3 text-gray-400" />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-2 rounded-md flex items-center gap-2 ${hasActiveFilters() ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                <FaFilter /> Filters {hasActiveFilters() && <span className="bg-white text-orange-500 text-xs px-1.5 py-0.5 rounded-full">Active</span>}
              </button>
              <button onClick={handleCopyAll} className="px-3 py-2 bg-indigo-600 text-white rounded-md">Copy All Mobile Numbers</button>
            </div>
          )}

          {activeTab === 'micro' ? (
            <div className="flex items-center gap-3 ml-auto flex-wrap justify-end">
              <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Global Timeline</span>
              <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
                {WINDOW_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSelectedWindow(opt.value)}
                    title={opt.label}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${selectedWindow === opt.value ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
                  >
                    {opt.short}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={handleMicroAnalysisRefresh}
                disabled={loadingInsights || loadingCustomerBehaviorId === selectedCustomerId}
                title="Refresh all Micro Analysis data"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold bg-white border border-indigo-200 text-indigo-700 shadow-sm hover:bg-indigo-50 hover:border-indigo-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaSyncAlt className={`text-[11px] ${loadingInsights || loadingCustomerBehaviorId === selectedCustomerId ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          ) : null}
        </div>

        {/* Filter Panel */}
        {activeTab === 'customers' && showFilters && (
          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Filter Customers</h3>
              <div className="flex items-center gap-2">
                {hasActiveFilters() && (
                  <button onClick={clearFilters} className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1">
                    <FaTimes /> Clear Filters
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Gender Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Gender</label>
                <select 
                  value={filters.gender} 
                  onChange={(e) => handleFilterChange('gender', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="all">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Has Orders Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Has Online Orders</label>
                <select 
                  value={filters.hasOrders} 
                  onChange={(e) => handleFilterChange('hasOrders', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">All Customers</option>
                  <option value="yes">With Orders</option>
                  <option value="no">Without Orders</option>
                </select>
              </div>

              {/* Open Cart Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Open Cart</label>
                <select
                  value={filters.openCart}
                  onChange={(e) => handleFilterChange('openCart', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">All</option>
                  <option value="yes">Open Cart Users</option>
                  <option value="no">No Open Cart Users</option>
                </select>
              </div>

              {/* Delivered Orders Bucket Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Delivered Orders</label>
                <select
                  value={filters.deliveredOrderBucket}
                  onChange={(e) => handleFilterChange('deliveredOrderBucket', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">All</option>
                  <option value="none">No Order</option>
                  <option value="first">First Order</option>
                  <option value="second">Second Order</option>
                  <option value="twoPlus">2+ Orders</option>
                </select>
              </div>

              {/* Joined Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Joined From</label>
                <input 
                  type="date" 
                  value={filters.joinedFrom} 
                  onChange={(e) => handleFilterChange('joinedFrom', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Joined To</label>
                <input 
                  type="date" 
                  value={filters.joinedTo} 
                  onChange={(e) => handleFilterChange('joinedTo', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              {/* Revenue Range */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Min Revenue (₹)</label>
                <input 
                  type="number" 
                  value={filters.minRevenue} 
                  onChange={(e) => handleFilterChange('minRevenue', e.target.value)}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Max Revenue (₹)</label>
                <input 
                  type="number" 
                  value={filters.maxRevenue} 
                  onChange={(e) => handleFilterChange('maxRevenue', e.target.value)}
                  placeholder="No limit"
                  min="0"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              {/* Order Count Range */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Min Order Count</label>
                <input 
                  type="number" 
                  value={filters.minOrderCount} 
                  onChange={(e) => handleFilterChange('minOrderCount', e.target.value)}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Max Order Count</label>
                <input 
                  type="number" 
                  value={filters.maxOrderCount} 
                  onChange={(e) => handleFilterChange('maxOrderCount', e.target.value)}
                  placeholder="No limit"
                  min="0"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {activeTab === 'customers' ? (
      <>
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gradient-to-r from-blue-500 to-indigo-500">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-white">Name</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-white">Email</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-white">Phone</th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold text-white cursor-pointer hover:bg-indigo-600 select-none"
                  onClick={() => handleSort('onlineOrderRevenue')}
                >
                  <div className="flex items-center">
                    Online Revenue <SortIcon field="onlineOrderRevenue" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold text-white cursor-pointer hover:bg-indigo-600 select-none"
                  onClick={() => handleSort('onlineOrderCount')}
                >
                  <div className="flex items-center">
                    Delivered Orders <SortIcon field="onlineOrderCount" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold text-white cursor-pointer hover:bg-indigo-600 select-none"
                  onClick={() => handleSort('lastLogin')}
                >
                  <div className="flex items-center">
                    Last Login <SortIcon field="lastLogin" />
                  </div>
                </th>
              </tr> 
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="p-6 text-center">Loading...</td></tr>
              ) : customers.length === 0 ? (
                <tr><td colSpan="6" className="p-6 text-center">No customers found</td></tr>
              ) : (
                customers.map(c => (
                  <React.Fragment key={c._id}>
                    <tr className={`cursor-pointer hover:bg-gray-50`} onClick={() => toggleRow(c._id)}>
                      <td className="px-6 py-4 text-gray-700">{c.name || '—'}</td>
                      <td className="px-6 py-4 text-gray-700">{c.email || '—'}</td>
                      <td className="px-6 py-4 text-gray-700 flex items-center gap-3">
                        <span>{c.mobile || '—'}</span>
                        {c.mobile && (
                          <button onClick={(e) => { e.stopPropagation(); copyToClipboard(c.mobile); }} className="text-gray-500 hover:text-gray-700">
                            <FaCopy />
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`font-semibold ${c.onlineOrderRevenue > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                          {formatCurrency(c.onlineOrderRevenue)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full text-sm font-medium ${c.onlineOrderCount > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {c.onlineOrderCount || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-700">{formatDate(c.lastLogin)}</td>
                    </tr>

                    {expandedRow === c._id && (
                      <tr className="bg-blue-50">
                        <td colSpan="6" className="p-4">
                          <div className="text-gray-700 grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <p><strong>Gender:</strong> {c.gender ? String(c.gender).charAt(0).toUpperCase() + String(c.gender).slice(1) : '—'}</p>
                            <p><strong>Joined:</strong> {formatDate(c.joinedTime || c.createdAt)}</p>
                            <p><strong>Last Login:</strong> {formatDate(c.lastLogin)}</p>
                            <p><strong>Online Revenue:</strong> <span className="text-green-600 font-semibold">{formatCurrency(c.onlineOrderRevenue)}</span></p>
                            <p><strong>Delivered Orders:</strong> <span className="text-green-600 font-semibold">{c.onlineOrderCount || 0}</span></p>
                            {c.address && <p className="col-span-2"><strong>Address:</strong> {formatAddress(c.address)}</p>}
                          </div>
                          {loadingCustomerBehaviorId === c._id ? (
                            <p className="text-sm text-gray-500">Loading customer specific behavior timeline...</p>
                          ) : !customerBehaviorMap[`${c._id}_${CUSTOMERS_TAB_BEHAVIOR_WINDOW}`] ? (
                            <p className="text-sm text-gray-500">No behavior data available for this customer in the last 30 days.</p>
                          ) : (
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <div className="bg-white rounded-md p-3 border border-blue-100">
                                  <p className="text-xs text-gray-500">Avg Session</p>
                                  <p className="text-lg font-semibold text-gray-800">{formatDuration(customerBehaviorMap[`${c._id}_${CUSTOMERS_TAB_BEHAVIOR_WINDOW}`]?.summary?.avgSessionMins || 0)}</p>
                                </div>
                                <div className="bg-white rounded-md p-3 border border-blue-100">
                                  <p className="text-xs text-gray-500">Open Cart Value</p>
                                  <p className="text-lg font-semibold text-amber-700">{formatCurrency(customerBehaviorMap[`${c._id}_${CUSTOMERS_TAB_BEHAVIOR_WINDOW}`]?.summary?.openCartValue || 0)}</p>
                                </div>
                                <div className="bg-white rounded-md p-3 border border-blue-100">
                                  <p className="text-xs text-gray-500">Delivered Orders (30D)</p>
                                  <p className="text-lg font-semibold text-green-700">{Number(customerBehaviorMap[`${c._id}_${CUSTOMERS_TAB_BEHAVIOR_WINDOW}`]?.summary?.orders || 0)}</p>
                                </div>
                                <div className="bg-white rounded-md p-3 border border-blue-100">
                                  <p className="text-xs text-gray-500">Delivered Revenue (30D)</p>
                                  <p className="text-lg font-semibold text-green-700">{formatCurrency(customerBehaviorMap[`${c._id}_${CUSTOMERS_TAB_BEHAVIOR_WINDOW}`]?.summary?.revenue || 0)}</p>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white rounded-md p-3 border border-blue-100">
                                  <p className="text-xs uppercase text-gray-500 mb-2">Viewed But Not Purchased</p>
                                  <div className="space-y-2">
                                    {(customerBehaviorMap[`${c._id}_${CUSTOMERS_TAB_BEHAVIOR_WINDOW}`]?.productBehavior?.viewedNotPurchased || []).slice(0, 4).map((item) => (
                                      <p key={item.productId} className="text-sm text-gray-700">
                                        {item.productName} <span className="text-xs text-gray-500">({item.nonConvertedViews || 0} drop-offs)</span>
                                      </p>
                                    ))}
                                  </div>
                                </div>

                                <div className="bg-white rounded-md p-3 border border-blue-100">
                                  <p className="text-xs uppercase text-gray-500 mb-2">Recent Activity Timeline</p>
                                  <div className="space-y-2 max-h-44 overflow-auto pr-1">
                                    {(customerBehaviorMap[`${c._id}_${CUSTOMERS_TAB_BEHAVIOR_WINDOW}`]?.timeline || []).slice(0, 6).map((event, idx) => {
                                      const presentation = getTimelineEventPresentation(event);
                                      return (
                                        <p key={`${event.timestamp}-${idx}`} className="text-sm text-gray-700">
                                          <span className={`font-medium ${presentation.titleClass}`}>{presentation.title}</span>
                                          {presentation.detail ? ` · ${presentation.detail}` : ''}
                                          <span className="text-xs text-gray-500"> · {formatDate(event.timestamp)}</span>
                                        </p>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>

                              {(customerBehaviorMap[`${c._id}_${CUSTOMERS_TAB_BEHAVIOR_WINDOW}`]?.preferredAddresses || []).length > 0 && (
                                <div className="bg-white rounded-md p-3 border border-blue-100">
                                  <p className="text-xs uppercase text-gray-500 mb-2">Preferred Delivery Addresses</p>
                                  {(customerBehaviorMap[`${c._id}_${CUSTOMERS_TAB_BEHAVIOR_WINDOW}`]?.preferredAddresses || []).slice(0, 2).map((address, idx) => (
                                    <p key={`${address.addressId || address.address}-${idx}`} className="text-sm text-gray-700">
                                      {getResolvedPreferredAddress(c._id, address)} <span className="text-xs text-gray-500">({address.orders} orders)</span>
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">Showing page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalCount} customers)</div>
          <div className="flex items-center gap-3 mr-16">
            <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage <= 1} className="px-3 py-1 rounded border disabled:opacity-50">
              <FaChevronLeft />
            </button>
            <div className="text-sm">{pagination.currentPage}</div>
            <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage >= pagination.totalPages} className="px-3 py-1 rounded border disabled:opacity-50">
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>
      </>
      ) : (
        <div className="space-y-6">
          {/* Live Pulse Hero Strip */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-indigo-950 to-violet-900 text-white shadow-2xl">
            <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-fuchsia-500/15 rounded-full blur-3xl" />
            <div className="relative p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/60 font-semibold">Live Pulse · {getWindowLabel(selectedWindow)}</p>
                  <h2 className="text-2xl font-bold mt-1">Real-time Customer Intelligence</h2>
                  {livePulseMetrics.isTodayLive && livePulseMetrics.historicalTrackedSessions != null ? (
                    <p className="text-[11px] text-white/50 mt-1">
                      {livePulseMetrics.historicalTrackedSessions.toLocaleString('en-IN')} analytics sessions tracked today (historical)
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (livePulseMetrics.isTodayLive) openLivePresenceModal();
                  }}
                  title={livePulseMetrics.isTodayLive ? 'View customers active in the app (last 2 min)' : 'Historical session count for selected period'}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all ${
                    livePulseMetrics.isTodayLive
                      ? 'bg-emerald-500/20 border-emerald-400/30 hover:bg-emerald-500/30 cursor-pointer'
                      : 'bg-emerald-500/20 border-emerald-400/30 cursor-default'
                  }`}
                >
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs font-semibold text-emerald-200">
                    {livePulseMetrics.activeSessions.toLocaleString('en-IN')}{' '}
                    {livePulseMetrics.isTodayLive ? 'active in app' : 'active sessions'}
                  </span>
                </button>
              </div>
              <div className="overflow-x-auto pb-1 scrollbar-thin">
                <div className="flex flex-nowrap gap-3 min-w-full">
                <div className="flex-1 min-w-[132px] rounded-xl bg-white/10 backdrop-blur border border-white/10 p-3 shrink-0">
                  <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Events</p>
                  <p className="text-2xl font-bold mt-1">{livePulseMetrics.totalEvents.toLocaleString('en-IN')}</p>
                  <p className="text-[11px] text-white/60 mt-1">{livePulseMetrics.activeCustomers.toLocaleString('en-IN')} customers</p>
                </div>
                <div className="flex-1 min-w-[132px] rounded-xl bg-gradient-to-br from-gray-500/20 to-gray-300/15 backdrop-blur border border-gray-300/30 p-3 shrink-0">
                  <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Product Views</p>
                  <p className="text-2xl font-bold mt-1">{livePulseMetrics.productViews.toLocaleString('en-IN')}</p>
                  <p className="text-[11px] text-white/60 mt-1">discovery layer</p>
                </div>
                <div className="flex-1 min-w-[132px] rounded-xl bg-gradient-to-br from-sky-500/20 to-cyan-500/15 backdrop-blur border border-sky-300/30 p-3 shrink-0">
                  <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Cart Adds</p>
                  <p className="text-2xl font-bold mt-1">{livePulseMetrics.cartAdditions.toLocaleString('en-IN')}</p>
                  <p className="text-[11px] text-cyan-200 mt-1">V→C {livePulseMetrics.viewToCart.toFixed(1)}%</p>
                </div>
                <div className="flex-1 min-w-[132px] rounded-xl bg-gradient-to-br from-yellow-500/30 to-amber-500/20 backdrop-blur border border-yellow-300/30 p-3 shrink-0">
                  <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Purchases</p>
                  <p className="text-2xl font-bold mt-1">{livePulseMetrics.purchases.toLocaleString('en-IN')}</p>
                  <p className="text-[11px] text-emerald-300 mt-1">C→O {livePulseMetrics.cartToOrder.toFixed(1)}%</p>
                </div>
                <div className="flex-1 min-w-[132px] rounded-xl bg-gradient-to-br from-emerald-500/30 to-teal-500/20 backdrop-blur border border-emerald-300/30 p-3 shrink-0">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-100 font-semibold">Delivered Revenue</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(livePulseMetrics.totalRevenue)}</p>
                  <p className="text-[11px] text-emerald-100/80 mt-1">{livePulseMetrics.orderCount} delivered · AOV {formatCurrency(livePulseMetrics.avgOrderValue)}</p>
                </div>
                <div className="flex-1 min-w-[132px] rounded-xl bg-gradient-to-br from-orange-500/30 to-rose-500/20 backdrop-blur border border-orange-300/30 p-3 shrink-0">
                  <p className="text-[10px] uppercase tracking-wider text-orange-100 font-semibold">Cancelled Orders</p>
                  <p className="text-2xl font-bold mt-1">{livePulseMetrics.cancelledOrderCount.toLocaleString('en-IN')}</p>
                  <p className="text-[11px] text-orange-100/80 mt-1">
                    {formatCurrency(livePulseMetrics.cancelledOrderValue)} lost
                    {livePulseMetrics.cancelledCancelRate > 0 ? ` · ${livePulseMetrics.cancelledCancelRate.toFixed(1)}% rate` : ''}
                  </p>
                </div>
                <div className="flex-1 min-w-[132px] rounded-xl bg-gradient-to-br from-rose-500/25 to-fuchsia-500/15 backdrop-blur border border-rose-300/30 p-3 shrink-0">
                  <p className="text-[10px] uppercase tracking-wider text-rose-100 font-semibold">Conversion</p>
                  <p className="text-2xl font-bold mt-1">{livePulseMetrics.overallConversion.toFixed(2)}%</p>
                  <p className="text-[11px] text-rose-100/80 mt-1">view → order · {formatDuration(livePulseMetrics.avgViewToPurchase)}</p>
                </div>
                </div>
              </div>

              {/* Inline mini funnel */}
              <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-2">
                {conversionFunnelStages.map((stage) => (
                  <div key={stage.label} className="rounded-lg bg-white/5 border border-white/10 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] text-white/70 font-medium">{stage.label}</span>
                      <span className="text-[11px] font-semibold text-white">{stage.value.toLocaleString('en-IN')} · {stage.pct}%</span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className={`h-full bg-gradient-to-r ${stage.color} rounded-full transition-all`} style={{ width: `${Math.max(4, stage.pct)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto pb-1 scrollbar-thin">
            <div className="flex flex-nowrap gap-4 min-w-full">
            {summaryCards.map((card) => (
              <div key={card.title} className="flex-1 min-w-[200px] shrink-0 relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" />
                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">{card.title}</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">{card.value}</p>
                <p className="mt-1 text-xs text-gray-500">{card.note}</p>
              </div>
            ))}
            </div>
          </div>

          {/* Sales Velocity + Activity Heatmap row */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-teal-50 rounded-2xl shadow-sm border border-emerald-100 p-5">
              <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-200/40 rounded-full blur-3xl" />
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-emerald-900">Delivered Sales Velocity</p>
                  <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold px-2 py-1 rounded-full bg-emerald-100">{salesVelocityCard.windowLabel}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-emerald-700">Orders / Day</p>
                    <p className="text-3xl font-bold text-emerald-900">{salesVelocityCard.ordersPerDay.toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-emerald-700">Revenue / Day</p>
                    <p className="text-3xl font-bold text-emerald-900">{formatCurrency(salesVelocityCard.revenuePerDay)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-emerald-700">Avg Order Value</p>
                    <p className="text-lg font-semibold text-emerald-900">{formatCurrency(salesVelocityCard.avgOrderValue)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-emerald-700">Unique Buyers</p>
                    <p className="text-lg font-semibold text-emerald-900">{salesVelocityCard.uniqueBuyers.toLocaleString('en-IN')}</p>
                  </div>
                  <div className="col-span-2 border-t border-emerald-100/80 pt-3 mt-1">
                    <p className="text-xs text-rose-700">Cancelled in Window</p>
                    <p className="text-lg font-semibold text-rose-900">
                      {livePulseMetrics.cancelledOrderCount.toLocaleString('en-IN')} orders
                      {' · '}{formatCurrency(livePulseMetrics.cancelledOrderValue)} lost
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="xl:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Activity Heatmap · Hourly Pulse</p>
                  <p className="text-xs text-gray-500 mt-0.5">Darker = more events. Times shown in IST (12-hour). Click an hour chip to open details.</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold px-2 py-1 rounded-full bg-indigo-50">Peak {timeIntelligenceCards.bestHour}</span>
              </div>
              <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
                {aggregateHourlyHeatmap.data.map((row) => {
                  const intensity = aggregateHourlyHeatmap.max > 0 ? row.events / aggregateHourlyHeatmap.max : 0;
                  const opacity = Math.max(0.08, intensity);
                  const isSelected = Number(selectedHourlyPulseHour) === Number(row.hour);
                  return (
                    <button
                      type="button"
                      key={`hour-${row.hour}`}
                      title={`${row.hourLabel} — ${row.events} events · ${row.uniqueUsers || 0} users`}
                      onClick={() => setSelectedHourlyPulseHour(Number(row.hour))}
                      className={`h-11 rounded-md flex items-end justify-center text-[8px] font-semibold leading-tight relative px-0.5 transition-all hover:scale-[1.03] hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${isSelected ? 'ring-2 ring-indigo-500 ring-offset-1 shadow-md' : ''}`}
                      style={{ backgroundColor: `rgba(79, 70, 229, ${isSelected ? Math.min(1, opacity + 0.15) : opacity})` }}
                    >
                      <span className={`pb-1 whitespace-nowrap ${intensity > 0.5 || isSelected ? 'text-white' : 'text-indigo-900/70'}`}>{row.hourLabel}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 grid grid-cols-7 gap-1.5">
                {aggregateWeekdayHeatmap.data.map((row) => {
                  const intensity = aggregateWeekdayHeatmap.max > 0 ? row.events / aggregateWeekdayHeatmap.max : 0;
                  const opacity = Math.max(0.1, intensity);
                  return (
                    <div
                      key={`day-${row.day}`}
                      title={`${row.day} — ${row.events} events · ${row.uniqueUsers || 0} users`}
                      className="rounded-lg p-2 border border-gray-100"
                      style={{ backgroundColor: `rgba(16, 185, 129, ${opacity})` }}
                    >
                      <p className={`text-[10px] font-bold ${intensity > 0.5 ? 'text-white' : 'text-emerald-900'}`}>{row.day}</p>
                      <p className={`text-xs font-semibold mt-0.5 ${intensity > 0.5 ? 'text-white' : 'text-emerald-900/80'}`}>{row.events}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Top Performing Products in window + Event Type Mix */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800 flex items-center gap-2"><FaFire className="text-orange-500" /> Top Performing Products · {getWindowLabel(selectedWindow)}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Star = high conversion · Rising = strong cart intent · Risk = no purchases yet</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-2 pr-3">Product</th>
                      <th className="py-2 pr-3">Views</th>
                      <th className="py-2 pr-3">Cart Adds</th>
                      <th className="py-2 pr-3">Orders</th>
                      <th className="py-2 pr-3">View → Order</th>
                      <th className="py-2">Tag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingInsights ? (
                      <tr><td colSpan="6" className="py-5 text-center text-gray-500">Loading product performance...</td></tr>
                    ) : topPerformingProducts.length === 0 ? (
                      <tr><td colSpan="6" className="py-5 text-center text-gray-500">No product activity in this window.</td></tr>
                    ) : (
                      topPerformingProducts.map((p) => (
                        <tr key={p.productId} className="border-b last:border-b-0 hover:bg-indigo-50/30 transition-colors">
                          <td className="py-2 pr-3 font-medium text-gray-800 line-clamp-1 max-w-[260px]">{p.productName}</td>
                          <td className="py-2 pr-3 font-semibold text-indigo-700">{p.views.toLocaleString('en-IN')}</td>
                          <td className="py-2 pr-3 font-semibold text-sky-700">{p.carts.toLocaleString('en-IN')}</td>
                          <td className="py-2 pr-3 font-semibold text-emerald-700">{p.orders.toLocaleString('en-IN')}</td>
                          <td className="py-2 pr-3">
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${p.conversionRate >= 8 ? 'bg-emerald-500' : p.conversionRate >= 3 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${Math.min(100, p.conversionRate * 4)}%` }} />
                              </div>
                              <span className="text-xs font-semibold text-gray-700">{p.conversionRate.toFixed(1)}%</span>
                            </div>
                          </td>
                          <td className="py-2">
                            <span className={`text-[10px] px-2 py-1 rounded-full font-semibold ${
                              p.tag === 'star' ? 'bg-emerald-100 text-emerald-700'
                                : p.tag === 'rising' ? 'bg-sky-100 text-sky-700'
                                  : p.tag === 'risk' ? 'bg-rose-100 text-rose-700'
                                    : 'bg-gray-100 text-gray-700'
                            }`}>
                              {p.tag === 'star' ? 'STAR' : p.tag === 'rising' ? 'RISING' : p.tag === 'risk' ? 'RISK' : 'NORMAL'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <p className="text-sm font-semibold text-gray-800 mb-3">Event Type Mix</p>
              {eventTypeMix.length === 0 ? (
                <p className="text-xs text-gray-500">No events in this window.</p>
              ) : (
                <>
                  <div className="h-44">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={eventTypeMix} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={2}>
                          {eventTypeMix.map((entry, idx) => (
                            <Cell key={`${entry.name}-${idx}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {eventTypeMix.map((entry) => {
                      const total = eventTypeMix.reduce((s, e) => s + e.value, 0);
                      const pct = total > 0 ? Math.round((entry.value / total) * 100) : 0;
                      return (
                        <div key={entry.name} className="flex items-center gap-2 text-xs">
                          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                          <span className="text-gray-700 flex-1">{entry.name}</span>
                          <span className="font-semibold text-gray-800">{entry.value.toLocaleString('en-IN')} ({pct}%)</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Journey Speed + Customer Health Mix + Abandoned Aging */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <p className="text-sm font-semibold text-gray-800 mb-3">Journey Speed Tracker</p>
              <p className="text-xs text-gray-500 mb-3">Faster journeys = healthier funnel. Slow journeys signal friction in PDP, cart or checkout.</p>
              <div className="space-y-3">
                {journeySpeedSignals.map((signal) => {
                  const max = Math.max(1, ...journeySpeedSignals.map((s) => s.value));
                  const pct = Math.min(100, Math.max(6, (signal.value / max) * 100));
                  return (
                    <div key={signal.label}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-600 font-medium">{signal.label}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          signal.color === 'indigo' ? 'bg-indigo-50 text-indigo-700'
                            : signal.color === 'sky' ? 'bg-sky-50 text-sky-700'
                              : 'bg-emerald-50 text-emerald-700'
                        }`}>
                          {formatDuration(signal.value)}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full bg-gradient-to-r ${
                          signal.color === 'indigo' ? 'from-indigo-500 to-violet-500'
                            : signal.color === 'sky' ? 'from-sky-500 to-cyan-500'
                              : 'from-emerald-500 to-teal-500'
                        }`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 p-3 rounded-lg bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-100">
                <p className="text-[11px] text-violet-700 font-semibold uppercase tracking-wider">Velocity Verdict</p>
                <p className="text-xs text-violet-900 mt-1">
                  {predictiveInsights.conversionVelocity === 'Fast' && 'Customers convert quickly. Push more traffic to multiply orders.'}
                  {predictiveInsights.conversionVelocity === 'Moderate' && 'Normal funnel. Try urgency offers and trust signals to accelerate.'}
                  {predictiveInsights.conversionVelocity === 'Slow' && 'Friction detected. Simplify checkout, add cart reminders within 30 min.'}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <p className="text-sm font-semibold text-gray-800 mb-3">Customer Temperature</p>
              <p className="text-xs text-gray-500 mb-3">How engaged is your audience right now. Hot = high intent, ready to convert.</p>
              <div className="space-y-2">
                {customerHealthMix.map((bucket) => (
                  <div key={bucket.label} className={`rounded-lg ${bucket.bgColor} ${bucket.borderColor} border p-3`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${bucket.color}`} />
                        <span className={`text-sm font-semibold ${bucket.textColor}`}>{bucket.label}</span>
                      </div>
                      <div className="text-right">
                        <span className={`text-lg font-bold ${bucket.textColor}`}>{bucket.count}</span>
                        <span className="text-xs text-gray-500 ml-1">({bucket.pct}%)</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-white/80 rounded-full overflow-hidden mt-2">
                      <div className={`h-full ${bucket.color}`} style={{ width: `${Math.max(4, bucket.pct)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <p className="text-sm font-semibold text-gray-800 mb-3">Abandoned Cart Aging</p>
              <p className="text-xs text-gray-500 mb-3">Carts get colder the longer they sit. Recover the youngest first for best ROI.</p>
              {abandonedAgingChart.length === 0 ? (
                <p className="text-xs text-gray-500">No abandoned carts in selected window.</p>
              ) : (
                <>
                  <div className="h-44">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={abandonedAgingChart}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip
                          formatter={(value, name) => name === 'cartValue' ? formatCurrency(value) : value}
                          labelStyle={{ color: '#1f2937' }}
                        />
                        <Bar dataKey="customers" fill="#f59e0b" radius={[6, 6, 0, 0]} name="Customers" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 space-y-1">
                    {abandonedAgingChart.map((row) => (
                      <div key={row.bucket} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">{row.bucket}</span>
                        <span className="font-semibold text-gray-800">{row.customers} carts · {formatCurrency(row.cartValue)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500" />
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2"><FaClock className="text-rose-500" /> Abandoned Carts Command Center</h2>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold px-2 py-1 rounded-full bg-rose-50">Today only</span>
                  <span className="text-xs text-gray-500">
                    Loaded {abandonedIntelligenceSummary.loadedCount} / {abandonedMeta.totalCount} today&apos;s records
                  </span>
                  <select
                    value={abandonedPriorityFilter}
                    onChange={(e) => setAbandonedPriorityFilter(e.target.value)}
                    className="px-2 py-1 border rounded-md text-xs bg-white"
                  >
                    <option value="all">All priorities</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto pb-1 scrollbar-thin mb-4">
                <div className="flex flex-nowrap gap-2 min-w-full">
                <div className="flex-1 min-w-[140px] shrink-0 rounded-xl bg-gradient-to-br from-rose-50 to-white border border-rose-100 p-3 hover:shadow-sm transition">
                  <p className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">Critical</p>
                  <p className="text-lg font-bold text-rose-900 mt-0.5">{abandonedIntelligenceSummary.criticalCount}</p>
                </div>
                <div className="flex-1 min-w-[140px] shrink-0 rounded-xl bg-gradient-to-br from-amber-50 to-white border border-amber-100 p-3 hover:shadow-sm transition">
                  <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">High</p>
                  <p className="text-lg font-bold text-amber-900 mt-0.5">{abandonedIntelligenceSummary.highCount}</p>
                </div>
                <div className="flex-1 min-w-[140px] shrink-0 rounded-xl bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 p-3 hover:shadow-sm transition">
                  <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Total Cart Value</p>
                  <p className="text-lg font-bold text-indigo-900 mt-0.5">{formatCurrency(abandonedIntelligenceSummary.totalValue)}</p>
                </div>
                <div className="flex-1 min-w-[140px] shrink-0 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 p-3 hover:shadow-sm transition">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Recovery Potential</p>
                  <p className="text-lg font-bold text-emerald-900 mt-0.5">{formatCurrency(abandonedIntelligenceSummary.potentialRecovery)}</p>
                </div>
                <div className="flex-1 min-w-[140px] shrink-0 rounded-xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 p-3 hover:shadow-sm transition">
                  <p className="text-[10px] uppercase tracking-wider text-gray-700 font-bold">Avg Idle</p>
                  <p className="text-lg font-bold text-gray-900 mt-0.5">{formatDuration(abandonedIntelligenceSummary.avgIdle || 0)}</p>
                </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-2 pr-4">Customer</th>
                      <th className="py-2 pr-4">Idle Time</th>
                      <th className="py-2 pr-4">Cart Value</th>
                      <th className="py-2 pr-4">Items</th>
                      <th className="py-2 pr-4">Urgency</th>
                      <th className="py-2 pr-4">Recovery Potential</th>
                      <th className="py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingInsights ? (
                      <tr><td colSpan="7" className="py-6 text-center text-gray-500">Loading abandoned cart analytics...</td></tr>
                    ) : filteredAbandonedCarts.length === 0 ? (
                      <tr><td colSpan="7" className="py-6 text-center text-gray-500">No abandoned carts found for today with selected priority filter.</td></tr>
                    ) : (
                      filteredAbandonedCarts.map((entry) => (
                        <tr key={entry.customerId} className="border-b last:border-b-0 align-top">
                          <td className="py-3 pr-4">
                            <p className="font-medium text-gray-800">{entry.customer?.name || '—'}</p>
                            <p className="text-xs text-gray-500">{entry.customer?.mobile || entry.customer?.email || 'No contact'}</p>
                            <div className="mt-1 space-y-0.5">
                              {(() => {
                                const cartItems = entry.cart?.items || [];
                                const showAllItems = !!expandedAbandonedCartItems[entry.customerId];
                                const previewLimit = 3;
                                const itemsToShow = showAllItems ? cartItems : cartItems.slice(0, previewLimit);
                                const remainingCount = Math.max(0, cartItems.length - previewLimit);

                                return (
                                  <>
                                    {itemsToShow.map((item, idx) => {
                                      const optionLabel = item.optionLabel || (typeof item.selectedWeight === 'object' ? item.selectedWeight?.weightLabel : item.selectedWeight) || '';
                                      const unitPrice = Number(item.unitPrice || 0);
                                      const lineValue = Number(item.lineValue || 0);
                                      return (
                                        <p key={`${item.cartId || idx}`} className="text-[11px] text-gray-600 line-clamp-1">
                                          <span className="font-medium text-gray-700">{item.productName || 'Product'}</span>
                                          {optionLabel ? <span className="text-gray-500"> · {optionLabel}</span> : null}
                                          {unitPrice > 0 ? <span className="text-gray-500"> · {formatCurrency(unitPrice)}</span> : null}
                                          <span className="text-gray-500"> · Qty {Number(item.quantity || 0)}</span>
                                          {lineValue > 0 ? <span className="text-gray-700 font-medium"> = {formatCurrency(lineValue)}</span> : null}
                                        </p>
                                      );
                                    })}
                                    {remainingCount > 0 && !showAllItems && (
                                      <button
                                        type="button"
                                        onClick={() => toggleAbandonedCartItems(entry.customerId)}
                                        className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium"
                                      >
                                        +{remainingCount} more item{remainingCount === 1 ? '' : 's'}
                                      </button>
                                    )}
                                    {remainingCount > 0 && showAllItems && (
                                      <button
                                        type="button"
                                        onClick={() => toggleAbandonedCartItems(entry.customerId)}
                                        className="text-[11px] text-gray-500 hover:text-gray-700 font-medium"
                                      >
                                        Show less
                                      </button>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-amber-700 font-semibold">{formatDuration(entry.cart?.idleMinutes || 0)}</td>
                          <td className="py-3 pr-4 font-semibold text-gray-800">{formatCurrency(entry.cart?.cartValue || 0)}</td>
                          <td className="py-3 pr-4">{entry.cart?.itemCount || 0}</td>
                          <td className="py-3 pr-4">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              entry.abandonedMetrics?.priority === 'critical'
                                ? 'bg-rose-100 text-rose-700'
                                : entry.abandonedMetrics?.priority === 'high'
                                  ? 'bg-amber-100 text-amber-700'
                                  : entry.abandonedMetrics?.priority === 'medium'
                                    ? 'bg-indigo-100 text-indigo-700'
                                    : 'bg-gray-100 text-gray-700'
                            }`}>
                              {(entry.abandonedMetrics?.priority || 'low').toUpperCase()} · {Number(entry.abandonedMetrics?.urgencyScore || 0).toFixed(0)}
                            </span>
                          </td>
                          <td className="py-3 pr-4 font-semibold text-emerald-700">{formatCurrency(entry.abandonedMetrics?.potentialRecovery || 0)}</td>
                          <td className="py-3 text-xs text-gray-700">{entry.abandonedMetrics?.recommendedAction}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {abandonedHasMore && (
                <div className="mt-3 flex justify-center">
                  <button
                    onClick={handleLoadMoreAbandoned}
                    disabled={loadingAbandonedMore}
                    className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm disabled:opacity-50"
                  >
                    {loadingAbandonedMore ? 'Loading more...' : 'Load more abandoned carts'}
                  </button>
                </div>
              )}

              {/* Dead Products — No Purchase */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="mb-4">
                  <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                    <FaExclamationTriangle className="text-violet-500" />
                    Dead Products
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100">No Purchase</span>
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Products with zero sales for the selected channel(s) — only dead options are listed per product.
                    {deadProductsAllTime
                      ? ' All-time mode ignores the global timeline.'
                      : ` Window: ${getWindowLabel(selectedWindow).toLowerCase()}.`}
                    {deadProductsMeta?.scopeNote ? ` ${deadProductsMeta.scopeNote}` : ''}
                  </p>

                  <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap lg:flex-nowrap lg:items-center">
                    <button
                      type="button"
                      onClick={() => setDeadProductsAllTime((v) => !v)}
                      className={`shrink-0 inline-flex items-center justify-center px-3.5 py-2 text-xs font-semibold rounded-xl border transition-all ${
                        deadProductsAllTime
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white border-transparent shadow-md shadow-amber-200/60'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:text-amber-700 shadow-sm'
                      }`}
                      title="Show products that have never sold — not limited by the global timeline"
                    >
                      All Time Dead Stock
                    </button>

                    <select
                      value={deadProductsBranchId}
                      onChange={(e) => setDeadProductsBranchId(e.target.value)}
                      className="shrink-0 w-full sm:w-auto lg:min-w-[170px] px-3 py-2 border border-gray-200 rounded-xl text-xs bg-white font-medium text-gray-700 shadow-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-400"
                    >
                      {(branchPerformanceOptions.length > 0 ? branchPerformanceOptions : [{ value: 'all', label: 'All Branches' }]).map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>

                    <div className="inline-flex items-center rounded-full gap-1 border border-gray-200/90 bg-gradient-to-r from-slate-50 via-white to-slate-50 p-1 shadow-sm shrink-0 w-full sm:w-auto">
                      <button
                        type="button"
                        onClick={() => setDeadProductsOnline((v) => !v)}
                        className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-bold rounded-full transition-all ${
                          deadProductsOnline
                            ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-md shadow-sky-200/50 ring-1 ring-sky-400/30'
                            : 'text-gray-500 hover:text-sky-600 hover:bg-sky-50/60'
                        }`}
                      >
                        <FaMobileAlt className="text-[11px]" />
                        Online
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeadProductsOffline((v) => !v)}
                        className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-bold rounded-full transition-all ${
                          deadProductsOffline
                            ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md shadow-emerald-200/50 ring-1 ring-emerald-400/30'
                            : 'text-gray-500 hover:text-emerald-600 hover:bg-emerald-50/60'
                        }`}
                      >
                        <FaStore className="text-[11px]" />
                        Offline
                      </button>
                    </div>
                  </div>
                </div>

                {!deadProductsOnline && !deadProductsOffline ? (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                    Turn on at least one channel — Online or Offline — to list dead products.
                  </p>
                ) : (
                  <>
                    <div className="overflow-x-auto pb-1 scrollbar-thin mb-4">
                      <div className="flex flex-nowrap gap-2 min-w-full">
                        <div className="flex-1 min-w-[130px] shrink-0 rounded-xl bg-gradient-to-br from-violet-50 to-white border border-violet-100 p-3">
                          <p className="text-[10px] uppercase tracking-wider text-violet-700 font-bold">Dead Products</p>
                          <p className="text-lg font-bold text-violet-900 mt-0.5">{Number(deadProductsSummary.totalDead || 0).toLocaleString('en-IN')}</p>
                        </div>
                        {Number(deadProductsSummary.deadOptionCount || 0) > 0 && (
                          <div className="flex-1 min-w-[130px] shrink-0 rounded-xl bg-gradient-to-br from-fuchsia-50 to-white border border-fuchsia-100 p-3">
                            <p className="text-[10px] uppercase tracking-wider text-fuchsia-700 font-bold">Dead Options</p>
                            <p className="text-lg font-bold text-fuchsia-900 mt-0.5">{Number(deadProductsSummary.deadOptionCount || 0).toLocaleString('en-IN')}</p>
                          </div>
                        )}
                        {deadProductsOnline && (
                          <div className="flex-1 min-w-[130px] shrink-0 rounded-xl bg-gradient-to-br from-sky-50 to-white border border-sky-100 p-3">
                            <p className="text-[10px] uppercase tracking-wider text-sky-700 font-bold">No Online Sale</p>
                            <p className="text-lg font-bold text-sky-900 mt-0.5">{Number(deadProductsSummary.onlineDead || 0).toLocaleString('en-IN')}</p>
                          </div>
                        )}
                        {deadProductsOffline && (
                          <div className="flex-1 min-w-[130px] shrink-0 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 p-3">
                            <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">No Offline Sale</p>
                            <p className="text-lg font-bold text-emerald-900 mt-0.5">{Number(deadProductsSummary.offlineDead || 0).toLocaleString('en-IN')}</p>
                          </div>
                        )}
                        {deadProductsOnline && deadProductsOffline && (
                          <div className="flex-1 min-w-[130px] shrink-0 rounded-xl bg-gradient-to-br from-rose-50 to-white border border-rose-100 p-3">
                            <p className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">Both Channels</p>
                            <p className="text-lg font-bold text-rose-900 mt-0.5">{Number(deadProductsSummary.bothDead || 0).toLocaleString('en-IN')}</p>
                          </div>
                        )}
                        <div className="flex-1 min-w-[130px] shrink-0 rounded-xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 p-3">
                          <p className="text-[10px] uppercase tracking-wider text-gray-700 font-bold">Stock at Risk</p>
                          <p className="text-lg font-bold text-gray-900 mt-0.5">{formatCurrency(deadProductsSummary.stockValue || 0)}</p>
                        </div>
                        {Number(deadProductsSummary.commonInventoryCount || 0) > 0 && (
                          <div className="flex-1 min-w-[130px] shrink-0 rounded-xl bg-gradient-to-br from-amber-50 to-white border border-amber-100 p-3">
                            <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Common Inventory</p>
                            <p className="text-lg font-bold text-amber-900 mt-0.5">{Number(deadProductsSummary.commonInventoryCount || 0).toLocaleString('en-IN')}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <input
                        type="search"
                        value={deadProductsSearch}
                        onChange={(e) => setDeadProductsSearch(e.target.value)}
                        placeholder="Search by product, option, SKU or category…"
                        className="w-full sm:max-w-md px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>

                    {loadingDeadProducts ? (
                      <p className="text-sm text-gray-500 py-8 text-center">Loading dead products…</p>
                    ) : deadProductsFiltered.length === 0 ? (
                      <p className="text-sm text-gray-500 py-8 text-center rounded-xl bg-gray-50 border border-dashed border-gray-200">
                        {deadProductsAllTime
                          ? 'No all-time dead products for this branch and channel filter. Every product has sold at least once!'
                          : 'No dead products for this window and channel filter. Great — everything selected is moving!'}
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 xl:gap-4">
                        {deadProductsFiltered.map((product) => (
                          <DeadProductCard
                            key={product.productId}
                            product={product}
                            deadProductsAllTime={deadProductsAllTime}
                            accordionOpen={deadProductsAccordionOpen}
                            onToggleAccordion={toggleDeadProductAccordion}
                          />
                        ))}
                      </div>
                    )}

                    {deadProductsHasMore && !deadProductsSearch.trim() && (
                      <div className="mt-4 flex justify-center">
                        <button
                          type="button"
                          onClick={handleLoadMoreDeadProducts}
                          disabled={loadingDeadProductsMore}
                          className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
                        >
                          {loadingDeadProductsMore
                            ? 'Loading more…'
                            : `Load ${getDeadProductsBatchSize(deadProductsLoadPage)} more`}
                        </button>
                      </div>
                    )}
                    {!loadingDeadProducts && deadProductsMeta?.pagination?.totalCount > 0 && (
                      <p className="text-[11px] text-gray-400 text-center mt-3">
                        Showing {deadProductsFiltered.length.toLocaleString('en-IN')} loaded
                        {deadProductsSearch.trim() ? ' (filtered)' : ` of ${Number(deadProductsMeta.pagination.totalCount).toLocaleString('en-IN')}`}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" />
              <div className="mb-3 pb-3 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2"><FaChartLine className="text-indigo-500" /> Behavior Insights</h2>
                <p className="text-xs text-gray-500 mt-1">Conversion, drop-offs, and high-intent opportunities in one decision row.</p>
              </div>
              {loadingInsights ? (
                <p className="text-sm text-gray-500">Loading behavior insights...</p>
              ) : !behaviorInsights ? (
                <p className="text-sm text-gray-500">Insights unavailable.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-lg bg-indigo-50 p-4">
                    <p className="text-xs text-indigo-700 font-semibold">Conversion</p>
                    <p className="text-sm text-indigo-900 mt-1">
                      View to Cart: <strong>{Number(behaviorInsights.summary?.viewToCartRate || 0).toFixed(1)}%</strong>
                    </p>
                    <p className="text-sm text-indigo-900">
                      Cart to Order: <strong>{Number(behaviorInsights.summary?.cartToPurchaseRate || 0).toFixed(1)}%</strong>
                    </p>
                    <p className="text-sm text-indigo-900">
                      Avg Session: <strong>{formatDuration(behaviorInsights.topCustomers?.[0]?.avgSessionDurationMins || 0)}</strong>
                    </p>
                  </div>

                  <div className="rounded-lg bg-amber-50 p-4 border border-amber-100">
                    <p className="text-xs text-amber-700 font-semibold flex items-center gap-1"><FaExclamationTriangle /> Drop-off Watch</p>
                    {(behaviorInsights.topDropOffProducts || []).slice(0, 2).map((product) => (
                      <p key={product.productId} className="text-xs text-amber-900 mt-1">
                        <strong>{product.productName}</strong>: {product.nonConvertedViews || 0} non-converted views
                      </p>
                    ))}
                  </div>

                  <div className="rounded-lg bg-rose-50 p-4 border border-rose-100">
                    <p className="text-xs text-rose-700 font-semibold flex items-center gap-1"><FaUserClock /> High-intent No-purchase</p>
                    {(behaviorInsights.watchlists?.highIntentNoPurchase || []).slice(0, 2).map((customer) => (
                      <p key={customer.customerId} className="text-xs text-rose-900 mt-1">
                        <strong>{customer.customerName || customer.customerMobile || 'Customer'}</strong>: intent {customer.intentScore || 0}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 via-amber-500 to-orange-500" />
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2"><FaUserClock className="text-rose-500" /> High Engagement But No Order · {getWindowLabel(selectedWindow)}</h2>
                <p className="text-xs text-gray-500 mt-0.5">App time, sessions, and no-order status are scoped to the selected Global Timeline. Recover fast with personalized offers.</p>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold px-2 py-1 rounded-full bg-rose-50">Min session {formatDuration(behaviorInsights?.activityWindow?.dailyWindow?.minSessionMins || 10)}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600 border-b">
                    <th className="py-2 pr-4">Customer</th>
                    <th className="py-2 pr-4">App Time</th>
                    <th className="py-2 pr-4">Sessions</th>
                    <th className="py-2 pr-4">Events</th>
                    <th className="py-2 pr-4">Engagement</th>
                    <th className="py-2 pr-4">Last Active</th>
                    <th className="py-2">Recommended Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingInsights ? (
                    <tr><td colSpan="7" className="py-6 text-center text-gray-500">Loading high-engagement no-order list...</td></tr>
                  ) : dailyNoOrderCustomers.length === 0 ? (
                    <tr><td colSpan="7" className="py-6 text-center text-gray-500">No high-engagement no-order customers found for {getWindowLabel(selectedWindow).toLowerCase()}.</td></tr>
                  ) : (
                    dailyNoOrderCustomers.map((entry) => (
                      <tr key={String(entry.customerId)} className="border-b last:border-b-0">
                        <td className="py-3 pr-4">
                          <p className="font-medium text-gray-800">{entry.customerName || '—'}</p>
                          <p className="text-xs text-gray-500">{entry.customerMobile || entry.customerEmail || 'No contact'}</p>
                        </td>
                        <td className="py-3 pr-4 font-semibold text-indigo-700">{formatDuration(entry.totalSessionDurationMins || 0)}</td>
                        <td className="py-3 pr-4">{entry.sessions || 0}</td>
                        <td className="py-3 pr-4">{entry.totalEvents || 0}</td>
                        <td className="py-3 pr-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            entry.engagementLevel === 'Very High'
                              ? 'bg-rose-100 text-rose-700'
                              : entry.engagementLevel === 'High'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {entry.engagementLevel || 'Moderate'}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-xs text-gray-600">{formatDate(entry.lastActiveAt)}</td>
                        <td className="py-3 text-xs text-gray-700">{entry.recommendedAction}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Top Ordered Pincodes</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Highest demand areas · double-down with inventory & marketing</p>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">DEMAND</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-2 pr-3">Pincode</th>
                      <th className="py-2 pr-3">Orders</th>
                      <th className="py-2 pr-3">Delivered</th>
                      <th className="py-2 pr-3">Cancelled</th>
                      <th className="py-2 pr-3">In Progress</th>
                      <th className="py-2">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingInsights ? (
                      <tr><td colSpan="6" className="py-5 text-center text-gray-500">Loading pincode analytics...</td></tr>
                    ) : pincodeAnalytics.top.length === 0 ? (
                      <tr><td colSpan="6" className="py-5 text-center text-gray-500">No pincode data found.</td></tr>
                    ) : (
                      pincodeAnalytics.top.slice(0, 8).map((row) => (
                        <tr key={`top-${row.pincode}`} className="border-b last:border-b-0 hover:bg-emerald-50/30">
                          <td className="py-2 pr-3 font-semibold text-gray-800">{row.pincode}</td>
                          <td className="py-2 pr-3 font-semibold">{row.totalOrders || 0}</td>
                          <td className="py-2 pr-3 text-emerald-700 font-semibold">{row.deliveredOrders || 0}</td>
                          <td className="py-2 pr-3 text-rose-700">{row.cancelledOrders || 0}</td>
                          <td className="py-2 pr-3">{row.inProgressOrders || 0}</td>
                          <td className="py-2 font-semibold text-emerald-700">{formatCurrency(row.revenue || 0)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Least Ordered Pincodes</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Growth opportunity zones · target ads here for expansion</p>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 font-semibold">GROWTH</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-2 pr-3">Pincode</th>
                      <th className="py-2 pr-3">Orders</th>
                      <th className="py-2 pr-3">Delivered</th>
                      <th className="py-2 pr-3">Cancelled</th>
                      <th className="py-2">Delivered %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingInsights ? (
                      <tr><td colSpan="5" className="py-5 text-center text-gray-500">Loading pincode analytics...</td></tr>
                    ) : pincodeAnalytics.least.length === 0 ? (
                      <tr><td colSpan="5" className="py-5 text-center text-gray-500">No pincode data found.</td></tr>
                    ) : (
                      pincodeAnalytics.least.slice(0, 8).map((row) => (
                        <tr key={`least-${row.pincode}`} className="border-b last:border-b-0 hover:bg-amber-50/30">
                          <td className="py-2 pr-3 font-semibold text-gray-800">{row.pincode}</td>
                          <td className="py-2 pr-3">{row.totalOrders || 0}</td>
                          <td className="py-2 pr-3 text-emerald-700">{row.deliveredOrders || 0}</td>
                          <td className="py-2 pr-3 text-rose-700">{row.cancelledOrders || 0}</td>
                          <td className="py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, Number(row.deliveredRate || 0))}%` }} />
                              </div>
                              <span className="text-xs font-semibold text-gray-700">{Number(row.deliveredRate || 0).toFixed(1)}%</span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto pb-1 scrollbar-thin">
            <div className="flex flex-nowrap gap-4 min-w-full">
            {salesOpportunityCards.map((card) => {
              const tones = {
                rose: { gradient: 'from-rose-500 to-rose-600', border: 'border-rose-100', text: 'text-rose-700' },
                amber: { gradient: 'from-amber-500 to-orange-500', border: 'border-amber-100', text: 'text-amber-700' },
                emerald: { gradient: 'from-emerald-500 to-teal-500', border: 'border-emerald-100', text: 'text-emerald-700' },
                indigo: { gradient: 'from-indigo-500 to-violet-500', border: 'border-indigo-100', text: 'text-indigo-700' },
              };
              const tone = tones[card.tone] || tones.indigo;
              return (
                <div key={card.title} className={`flex-1 min-w-[200px] shrink-0 relative overflow-hidden bg-white rounded-2xl shadow-sm border ${tone.border} p-4 hover:shadow-md transition-shadow`}>
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${tone.gradient}`} />
                  <p className={`text-xs uppercase tracking-wide font-semibold ${tone.text}`}>{card.title}</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{card.value.toLocaleString('en-IN')}</p>
                  <p className="mt-1 text-xs text-gray-500">{card.note}</p>
                </div>
              );
            })}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-indigo-50/40 rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" />
              <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-violet-500 rounded-full animate-pulse" />
                AI Prediction Signals
              </p>
              <div className="space-y-3">
                <div className="rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 p-3 text-white shadow-md">
                  <p className="text-xs text-indigo-100">Potential Orders (Next Hour)</p>
                  <p className="text-3xl font-bold mt-1">{predictiveInsights.nextHourPotentialOrders}</p>
                </div>
                <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
                  <p className="text-xs text-amber-700 font-medium">Conversion Velocity</p>
                  <p className="text-lg font-bold text-amber-900">{predictiveInsights.conversionVelocity}</p>
                </div>
                <div className="rounded-xl bg-rose-50 border border-rose-100 p-3">
                  <p className="text-xs text-rose-700 font-medium">Churn Pressure</p>
                  <p className="text-lg font-bold text-rose-900">{predictiveInsights.churnRiskPressure}</p>
                </div>
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                  <p className="text-xs text-emerald-700 font-medium">Expected Revenue Lift</p>
                  <p className="text-lg font-bold text-emerald-900">{predictiveInsights.expectedRevenueLiftLabel}</p>
                </div>
              </div>
            </div>

            <div className="xl:col-span-2 relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 via-amber-500 to-indigo-500" />
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Priority Action Queue</p>
                  <p className="text-xs text-gray-500 mt-0.5">Mission-control recommendations · run top-down for maximum impact</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold px-2 py-1 rounded-full bg-indigo-50">{actionQueue.length} signals</span>
              </div>
              <div className="space-y-2">
                {actionQueue.map((item, idx) => (
                  <div key={`${item.title}-${idx}`} className={`rounded-xl border p-3 transition-all hover:shadow-md ${
                    item.priority === 'P1' ? 'border-rose-200 bg-gradient-to-r from-rose-50 to-white'
                      : item.priority === 'P2' ? 'border-amber-200 bg-gradient-to-r from-amber-50 to-white'
                        : 'border-indigo-100 bg-gradient-to-r from-indigo-50/60 to-white'
                  }`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-800">{item.title}</p>
                      <span className={`text-xs font-bold px-2 py-1 rounded-lg ${item.priority === 'P1' ? 'bg-rose-500 text-white' : item.priority === 'P2' ? 'bg-amber-500 text-white' : 'bg-indigo-500 text-white'}`}>{item.priority}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Impact: <span className="font-semibold text-gray-700">{item.impact}</span></p>
                    <p className="text-xs text-gray-700 mt-1">{item.action}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Recovery Candidate Ranking</p>
                  <p className="text-xs text-gray-500 mt-0.5">Top priority customers to recover now · sorted by score</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold px-2 py-1 rounded-full bg-emerald-50">REVENUE</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-2 pr-3">Customer</th>
                      <th className="py-2 pr-3">Segment</th>
                      <th className="py-2 pr-3">Recovery Score</th>
                      <th className="py-2 pr-3">Value at Risk</th>
                      <th className="py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recoveryCandidateRanking.length === 0 ? (
                      <tr><td colSpan="5" className="py-5 text-center text-gray-500">No recovery candidates yet.</td></tr>
                    ) : (
                      recoveryCandidateRanking.slice(0, 8).map((row) => (
                        <tr key={row.key} className="border-b last:border-b-0">
                          <td className="py-2 pr-3">
                            <p className="font-medium text-gray-800">{row.customerName}</p>
                            <p className="text-xs text-gray-500">{row.contact || 'No contact'}</p>
                          </td>
                          <td className="py-2 pr-3 text-xs text-gray-700">{row.segment}</td>
                          <td className="py-2 pr-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              row.score >= 85 ? 'bg-rose-100 text-rose-700' : row.score >= 65 ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'
                            }`}>
                              {row.score.toFixed(0)}
                            </span>
                          </td>
                          <td className="py-2 pr-3 font-semibold text-emerald-700">{formatCurrency(row.valueAtRisk)}</td>
                          <td className="py-2 text-xs text-gray-700">{row.recommendedAction}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 to-fuchsia-500" />
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Revenue-Leak Radar</p>
                  <p className="text-xs text-gray-500 mt-0.5">Leak severity & business impact · plug critical leaks first</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold px-2 py-1 rounded-full bg-rose-50">PLUG LEAKS</span>
              </div>
              <div className="space-y-2">
                {revenueLeakRadar.map((item) => (
                  <div key={item.leakType} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-800">{item.leakType}</p>
                      <span className={`text-[11px] px-2 py-1 rounded-full ${
                        item.severity === 'Critical'
                          ? 'bg-rose-100 text-rose-700'
                          : item.severity === 'High'
                            ? 'bg-amber-100 text-amber-700'
                            : item.severity === 'Medium'
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-gray-100 text-gray-700'
                      }`}>
                        {item.severity}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{item.metric}</p>
                    <div className="h-1.5 bg-gray-200 rounded mt-2 overflow-hidden">
                      <div
                        className={`h-full ${item.severity === 'Critical' ? 'bg-rose-500' : item.severity === 'High' ? 'bg-amber-500' : item.severity === 'Medium' ? 'bg-indigo-500' : 'bg-gray-400'}`}
                        style={{ width: `${Math.max(8, Math.min(100, item.impact / 80))}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2 relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-500" />
              <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2"><FaChartLine className="text-indigo-500" /> Product Funnel Intelligence</p>
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <FunnelChart>
                    <Tooltip />
                    <Funnel dataKey="value" data={funnelData} isAnimationActive>
                      <LabelList position="right" fill="#1f2937" stroke="none" dataKey="name" />
                    </Funnel>
                  </FunnelChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Tracks impression-to-order leakage and helps optimize conversion blockers.
              </p>
            </div>

            <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-emerald-500" />
              <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2"><FaClock className="text-indigo-500" /> Time Intelligence</p>
              <div className="space-y-3">
                <div className="rounded-lg bg-indigo-50 p-3">
                  <p className="text-xs text-indigo-700">Best Engagement Hour</p>
                  <p className="text-xl font-bold text-indigo-900">{timeIntelligenceCards.bestHour}</p>
                  <p className="text-xs text-indigo-800">{timeIntelligenceCards.bestHourEvents} events</p>
                </div>
                <div className="rounded-lg bg-emerald-50 p-3">
                  <p className="text-xs text-emerald-700">Best Performing Day</p>
                  <p className="text-xl font-bold text-emerald-900">{timeIntelligenceCards.bestDay}</p>
                  <p className="text-xs text-emerald-800">{timeIntelligenceCards.bestDayEvents} events</p>
                </div>
                <div className="rounded-lg bg-amber-50 p-3">
                  <p className="text-xs text-amber-700">Search-to-Purchase Ratio</p>
                  <p className="text-xl font-bold text-amber-900">{searchAnalytics.searchToPurchaseRatio}</p>
                  <p className="text-xs text-amber-800">Higher means search intent is strong</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 to-fuchsia-500" />
              <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2"><FaUsers className="text-violet-500" /> Segment Engine</p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={segmentChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                      {segmentChartData.map((entry, index) => (
                        <Cell key={`${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="xl:col-span-2 relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 via-amber-500 to-rose-500" />
              <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2"><FaExclamationTriangle className="text-rose-500" /> Operational Risk & Revenue Leak Detection</p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={operationalRiskSignals}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#ef4444" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-indigo-500 to-rose-500" />
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-sm font-semibold text-gray-800 flex items-center gap-2"><FaChartLine className="text-emerald-500" /> Product Health Score System</p>
                <p className="text-xs text-gray-500 mt-0.5">Spot stars, fix leaks, and get clear actions to grow revenue · {getWindowLabel(selectedWindow)}</p>
              </div>
              <div className="w-full max-w-[420px] min-w-[280px]">
                <Select
                  isMulti
                  value={selectedHealthProductOptions}
                  onChange={(options) => {
                    setSelectedHealthProductIds(
                      Array.isArray(options) ? options.map((option) => String(option.value)) : [],
                    );
                  }}
                  options={productHealthSelectOptions}
                  placeholder="Search & compare products..."
                  isClearable
                  isSearchable
                  closeMenuOnSelect={false}
                  formatOptionLabel={(option) => (
                    <div className="py-0.5">
                      <p className="text-sm font-medium text-gray-800 line-clamp-1">{option.label}</p>
                      <p className="text-xs text-gray-500">{option.tierLabel} · Score {option.performanceScore}</p>
                    </div>
                  )}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  styles={{
                    control: (base) => ({ ...base, borderRadius: '0.5rem', borderColor: '#d1d5db', minHeight: '42px' }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </div>

            <div className="overflow-x-auto pb-1 scrollbar-thin mb-4">
              <div className="flex flex-nowrap gap-2 min-w-full">
              <div className="flex-1 min-w-[140px] shrink-0 rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Star Products</p>
                <p className="text-xl font-bold text-emerald-900">{productHealthSummary.starCount}</p>
              </div>
              <div className="flex-1 min-w-[140px] shrink-0 rounded-xl bg-rose-50 border border-rose-100 px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">At Risk</p>
                <p className="text-xl font-bold text-rose-900">{productHealthSummary.atRiskCount}</p>
              </div>
              <div className="flex-1 min-w-[140px] shrink-0 rounded-xl bg-amber-50 border border-amber-100 px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Cart Stuck</p>
                <p className="text-xl font-bold text-amber-900">{productHealthSummary.cartStuckCount}</p>
              </div>
              <div className="flex-1 min-w-[140px] shrink-0 rounded-xl bg-indigo-50 border border-indigo-100 px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Avg Health</p>
                <p className="text-xl font-bold text-indigo-900">{productHealthSummary.avgScore}</p>
              </div>
              <div className="flex-1 min-w-[140px] shrink-0 rounded-xl bg-violet-50 border border-violet-100 px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wider text-violet-700 font-bold">Total Drop-offs</p>
                <p className="text-xl font-bold text-violet-900">{productHealthSummary.totalDropOffs}</p>
              </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-4">
              {PRODUCT_HEALTH_TIER_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setProductHealthTierFilter(filter.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${productHealthTierFilter === filter.value ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {productHealthChartData.length > 0 ? (
              <div className="h-64 mb-4 rounded-xl border border-gray-100 bg-gray-50/50 p-3">
                <p className="text-xs font-semibold text-gray-600 mb-2">Funnel Comparison · Views → Cart → Orders</p>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={productHealthChartData} margin={{ bottom: 8, left: 0, right: 8, top: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={56} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="Views" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Cart Adds" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Orders" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : null}

            {productHealthFilteredData.length === 0 ? (
              <div className="text-center text-sm text-gray-500 py-8 rounded-xl border border-dashed border-gray-200">
                No products match this filter. Try another tier or clear product selection.
              </div>
            ) : (
              <div className="overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
                <div className="grid grid-rows-2 grid-flow-col auto-cols-[minmax(320px,340px)] gap-x-4 gap-y-3 w-max min-w-full">
                  {productHealthFilteredData.map((product) => {
                    const tierMeta = PRODUCT_HEALTH_TIER_META[product.tier] || PRODUCT_HEALTH_TIER_META.needs_attention;
                    const TierIcon = tierMeta.icon;
                    return (
                      <button
                        key={product.productId}
                        type="button"
                        onClick={() => setSelectedHealthProductDetail(product)}
                        className="text-left rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50/80 p-4 hover:border-indigo-200 hover:shadow-md transition group h-full"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${tierMeta.chip}`}>
                            <TierIcon className="text-[9px]" />
                            {product.tierLabel}
                          </span>
                          <span className="text-lg font-bold text-indigo-700">{product.performanceScore}</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-800 line-clamp-2 min-h-[2.5rem]">{product.name}</p>

                        <div className="mt-3 flex flex-wrap items-center gap-1 text-[10px] font-semibold text-gray-500">
                          <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{product.views} views</span>
                          <FaArrowRight className="text-[8px] text-gray-300 shrink-0" />
                          <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">{product.carts} cart</span>
                          <FaArrowRight className="text-[8px] text-gray-300 shrink-0" />
                          <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{product.orders} orders</span>
                        </div>

                        <div className="mt-3 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div className={`h-full rounded-full ${tierMeta.bar}`} style={{ width: `${product.performanceScore}%` }} />
                        </div>

                        <p className="mt-3 text-xs text-gray-600 line-clamp-2 leading-relaxed">{product.action}</p>
                        <p className="mt-2 text-[11px] font-semibold text-indigo-600 group-hover:text-indigo-800">View full breakdown →</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" />
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FaUsers className="text-violet-500" /> Customer Specific Behavior Insights
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Top 10 high-intent customers with no order today are shown first — or search by name / mobile.
                </p>
              </div>
              <div className="flex items-center gap-2 min-w-[320px]">
                <div className="w-[320px]">
                  <Select
                    value={selectedBehaviorCustomerOption}
                    onChange={(option) => setSelectedCustomerId(option?.value ? String(option.value) : '')}
                    onInputChange={(inputValue, { action }) => {
                      if (action === 'input-change') {
                        setBehaviorCustomerInput(inputValue);
                        if (behaviorCustomerSearchTimer.current) {
                          clearTimeout(behaviorCustomerSearchTimer.current);
                        }
                        behaviorCustomerSearchTimer.current = setTimeout(() => {
                          loadBehaviorCustomerOptions(inputValue);
                        }, 350);
                      }
                      return inputValue;
                    }}
                    options={
                      behaviorCustomerInput.trim()
                        ? behaviorCustomerSearchOptions
                        : suggestedBehaviorCustomerOptions
                    }
                    placeholder="Search by name or mobile..."
                    isClearable
                    isSearchable
                    isLoading={loadingBehaviorCustomerSearch}
                    noOptionsMessage={() => (
                      behaviorCustomerInput.trim()
                        ? 'No customers found — try name or mobile'
                        : 'No high-intent customers today — search by name or mobile'
                    )}
                    formatOptionLabel={(option) => (
                      <div className="py-0.5">
                        <p className="text-sm font-medium text-gray-800">{option.name || option.label}</p>
                        <p className="text-xs text-gray-500">
                          {[option.mobile, option.email].filter(Boolean).join(' · ') || 'No contact'}
                          {option.isSuggested && option.intentScore > 0 ? (
                            <span className="ml-2 text-rose-600 font-semibold">Intent {option.intentScore}</span>
                          ) : null}
                        </p>
                      </div>
                    )}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: '0.5rem',
                        borderColor: '#d1d5db',
                        minHeight: '42px',
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
                <button
                  onClick={() => selectedCustomerId && fetchCustomerBehavior(selectedCustomerId, { force: true })}
                  className="px-3 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-md text-sm font-semibold shadow-sm hover:shadow-md disabled:opacity-50"
                  disabled={!selectedCustomerId}
                >
                  Refresh
                </button>
              </div>
            </div>

            {loadingCustomerBehaviorId === selectedCustomerId ? (
              <p className="text-sm text-gray-500">Loading micro behavior analytics...</p>
            ) : !selectedCustomerBehavior ? (
              <p className="text-sm text-gray-500">Select a customer to view micro behavior analysis.</p>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-1 xl:grid-cols-4 gap-3">
                  <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 p-3">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-indigo-100 rounded-full blur-2xl opacity-50" />
                    <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Identity Layer</p>
                    <p className="text-sm font-semibold text-gray-800 mt-1">{customerDna.identity.name}</p>
                    <p className="text-xs text-gray-600">{customerDna.identity.mobile}</p>
                    <p className="text-xs text-gray-600 line-clamp-1">{customerDna.identity.email}</p>
                  </div>
                  <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-sky-50 to-white border border-sky-100 p-3">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-sky-100 rounded-full blur-2xl opacity-50" />
                    <p className="text-[10px] uppercase tracking-wider text-sky-700 font-bold">Behavioral DNA</p>
                    <p className="text-xs text-gray-700 mt-1">Views: <span className="font-semibold">{customerDna.behavior.productViews}</span></p>
                    <p className="text-xs text-gray-700">Searches: <span className="font-semibold">{customerDna.behavior.searches}</span></p>
                    <p className="text-xs text-gray-700">Cart Adds: <span className="font-semibold">{customerDna.behavior.cartAdds}</span></p>
                  </div>
                  <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 p-3">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-100 rounded-full blur-2xl opacity-50" />
                    <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Financial DNA</p>
                    <p className="text-xs text-gray-700 mt-1">Total Spend: <span className="font-semibold">{formatCurrency(customerDna.financial.totalSpend)}</span></p>
                    <p className="text-xs text-gray-700">AOV: <span className="font-semibold">{formatCurrency(customerDna.financial.averageOrderValue)}</span></p>
                    <p className="text-xs text-gray-700">Orders: <span className="font-semibold">{customerDna.financial.orders}</span></p>
                  </div>
                  <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-rose-50 to-white border border-rose-100 p-3">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-rose-100 rounded-full blur-2xl opacity-50" />
                    <p className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">Retention DNA</p>
                    <p className="text-xs text-gray-700 mt-1">Churn Risk: <span className="font-semibold">{customerDna.retention.churnRisk}</span></p>
                    <p className="text-xs text-gray-700">Loyalty: <span className="font-semibold">{customerDna.retention.loyaltySignal}</span></p>
                    <p className="text-xs text-gray-700">Discount Sensitivity: <span className="font-semibold">{customerDna.retention.discountSensitivity}%</span></p>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-100 p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Event Coverage & Intensity Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {eventCoverage.slice(0, 14).map((eventType) => (
                      <span key={eventType} className="px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold">
                        {eventType}
                      </span>
                    ))}
                    {eventCoverage.length === 0 && <span className="text-xs text-gray-500">No event signals in selected window.</span>}
                  </div>
                </div>

                {(selectedCustomerBehavior?.preferredAddresses || []).length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-100 p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Preferred Delivery Addresses</p>
                    <div className="space-y-2">
                      {(selectedCustomerBehavior.preferredAddresses || []).slice(0, 3).map((address, idx) => (
                        <p key={`${address.addressId || address.address}-${idx}`} className="text-sm text-gray-700">
                          {getResolvedPreferredAddress(selectedCustomerId, address)} <span className="text-xs text-gray-500">({address.orders} orders)</span>
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="bg-indigo-50 rounded-lg p-3">
                    <p className="text-xs text-indigo-700">Avg Session Time</p>
                    <p className="text-xl font-bold text-indigo-900">{formatDuration(selectedCustomerBehavior.summary?.avgSessionMins || 0)}</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-xs text-amber-700">Open Cart Value</p>
                    <p className="text-xl font-bold text-amber-900">{formatCurrency(selectedCustomerBehavior.summary?.openCartValue || 0)}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-xs text-emerald-700">Delivered Orders in Window</p>
                    <p className="text-xl font-bold text-emerald-900">{Number(selectedCustomerBehavior.summary?.orders || 0)}</p>
                  </div>
                  <div className="bg-violet-50 rounded-lg p-3">
                    <p className="text-xs text-violet-700">Delivered Revenue in Window</p>
                    <p className="text-xl font-bold text-violet-900">{formatCurrency(selectedCustomerBehavior.summary?.revenue || 0)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Activity by Hour</p>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={selectedCustomerHourlyWithEvents.hourlyData || []} margin={{ bottom: 8, left: 0, right: 8, top: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="hourLabelCompact"
                            interval={1}
                            angle={-45}
                            textAnchor="end"
                            height={52}
                            tick={{ fontSize: 10 }}
                          />
                          <YAxis allowDecimals={false} />
                          <Tooltip content={<EventIntensityTooltip labelPrefix="Time: " />} />
                          <Bar dataKey="events" fill="#6366f1" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Activity by Weekday</p>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={selectedCustomerHourlyWithEvents.weekdayData || []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="dayLabel" />
                          <YAxis />
                          <Tooltip content={<EventIntensityTooltip labelPrefix="Day: " />} />
                          <Bar dataKey="events" fill="#10b981" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg border border-gray-100 p-4">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-sm font-semibold text-gray-700">Viewed But Not Purchased (Top Drop-offs)</p>
                      {(selectedCustomerBehavior.productBehavior?.viewedNotPurchased || []).length > 0 && (
                        <button
                          type="button"
                          onClick={openFullDropOffsModal}
                          className="text-xs font-semibold text-rose-600 hover:text-rose-800 whitespace-nowrap"
                        >
                          Full drop-offs
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {(selectedCustomerBehavior.productBehavior?.viewedNotPurchased || []).slice(0, 6).map((item) => (
                        <div key={item.productId} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700 line-clamp-1">{item.productName}</span>
                          <span className="text-rose-600 font-semibold">{item.nonConvertedViews || 0}</span>
                        </div>
                      ))}
                      {(selectedCustomerBehavior.productBehavior?.viewedNotPurchased || []).length === 0 && (
                        <p className="text-xs text-gray-500">No drop-off products in selected window.</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-100 p-4">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-sm font-semibold text-gray-700">Recent Event Timeline</p>
                      {(selectedCustomerBehavior.timeline || []).length > 0 && (
                        <button
                          type="button"
                          onClick={openFullTimelineModal}
                          className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
                        >
                          View full timeline
                        </button>
                      )}
                    </div>
                    <div className="space-y-2 max-h-52 overflow-auto pr-1">
                      {(selectedCustomerBehavior.timeline || []).slice(0, 10).map((event, idx) => {
                        const presentation = getTimelineEventPresentation(event);
                        return (
                          <p key={`${event.timestamp}-${idx}`} className="text-sm text-gray-700">
                            <span className={`font-semibold ${presentation.titleClass}`}>{presentation.title}</span>
                            {presentation.detail ? ` · ${presentation.detail}` : ''}
                            <span className="text-xs text-gray-500"> · {formatDate(event.timestamp)}</span>
                          </p>
                        );
                      })}
                      {(selectedCustomerBehavior.timeline || []).length === 0 && (
                        <p className="text-xs text-gray-500">No events in selected window.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-100 p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Customer Journey Visualizer</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {customerJourneySteps.map((step, idx) => (
                      <React.Fragment key={`${step}-${idx}`}>
                        <span className="px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-semibold">{step}</span>
                        {idx < customerJourneySteps.length - 1 && <span className="text-gray-400">→</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg border border-gray-100 p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Search Analytics System</p>
                    <p className="text-sm text-gray-700">Search Events: <strong>{searchAnalytics.searchEvents}</strong></p>
                    <p className="text-sm text-gray-700">Search Share: <strong>{searchAnalytics.searchShare}%</strong></p>
                    <p className="text-sm text-gray-700">No-result Proxy: <strong>{searchAnalytics.noResultProxy}</strong></p>
                    <p className="text-xs text-gray-500 mt-2">Suggestion: improve tags/ranking for high-search low-conversion products.</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-100 p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Cohort & Loyalty Intelligence (Proxy)</p>
                    <p className="text-sm text-gray-700">High Value Active Customers: <strong>{behaviorInsights?.watchlists?.highValueActiveCustomers?.length || 0}</strong></p>
                    <p className="text-sm text-gray-700">Returning / Loyal Signal: <strong>{customerDna.retention.loyaltySignal}</strong></p>
                    <p className="text-sm text-gray-700">At-Risk Customers: <strong>{behaviorInsights?.watchlists?.churnRiskCustomers?.length || 0}</strong></p>
                    <p className="text-xs text-gray-500 mt-2">Weekly/monthly cohorts can be upgraded further once first-order timestamps are normalized per customer.</p>
                  </div>
                </div>

                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                  <p className="text-sm font-semibold text-indigo-800 mb-2">Growth Actions for Sales Boost</p>
                  <div className="space-y-1">
                    {(behaviorInsights?.monitoringRecommendations || []).slice(0, 4).map((recommendation, idx) => (
                      <p key={idx} className="text-sm text-indigo-900">- {recommendation}</p>
                    ))}
                    <p className="text-sm text-indigo-900">
                      - Run retargeting for this customer within the next 2 hours if open cart value is above {formatCurrency(500)}.
                    </p>
                    <p className="text-sm text-indigo-900">
                      - Trigger personalized offers on products with repeated views and zero purchases.
                    </p>
                    <p className="text-sm text-indigo-900">
                      - Auto-action candidate: trigger reminder/push/offer when customer stays in "high intent no purchase" watchlist.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 via-indigo-500 to-violet-500" />
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FaStore className="text-cyan-600" /> Branch Wise Performance Analytics
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Deep branch micro analysis — segments, products, risk, and revenue intelligence · {getWindowLabel(selectedWindow)}
                </p>
                {branchPerformanceInsights?.scopeNote ? (
                  <p className="text-[11px] text-indigo-600 mt-1">{branchPerformanceInsights.scopeNote}</p>
                ) : null}
              </div>
              <div className="w-full max-w-[360px] min-w-[260px]">
                <Select
                  value={selectedBranchPerformanceOption}
                  onChange={(option) => setSelectedBranchPerformanceId(option?.value ? String(option.value) : 'all')}
                  options={branchPerformanceOptions}
                  placeholder="Select branch..."
                  isSearchable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  styles={{
                    control: (base) => ({ ...base, borderRadius: '0.5rem', borderColor: '#d1d5db', minHeight: '42px' }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </div>

            {loadingBranchPerformance ? (
              <p className="text-sm text-gray-500 py-8 text-center">Loading branch performance analytics...</p>
            ) : !branchPerformanceInsights ? (
              <p className="text-sm text-gray-500 py-8 text-center">Select a branch to view performance analytics.</p>
            ) : (
              <div className="space-y-5">
                <div className="overflow-x-auto pb-1 scrollbar-thin">
                  <div className="flex flex-nowrap gap-2 min-w-full">
                  {branchPerformanceSummaryCards.map((card) => (
                    <div key={card.label} className="flex-1 min-w-[140px] shrink-0 rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50/80 px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{card.label}</p>
                      <p className="text-lg font-bold text-gray-900 mt-0.5">{card.value}</p>
                    </div>
                  ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                  <div className="xl:col-span-2 rounded-xl border border-gray-100 bg-gray-50/50 p-4">
                    <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <FaTrophy className="text-amber-500" /> Branch Revenue Leaderboard · {getWindowLabel(selectedWindow)}
                    </p>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={branchLeaderboardChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                          <YAxis />
                          <Tooltip formatter={(value, name) => [name === 'Revenue' ? formatCurrency(value) : value, name]} />
                          <Bar dataKey="Revenue" fill="#6366f1" radius={[6, 6, 0, 0]} />
                          <Bar dataKey="Orders" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-100 bg-white p-4">
                    <p className="text-sm font-semibold text-gray-800 mb-3">Customer Segments at Branch</p>
                    <div className="h-64">
                      {branchSegmentChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={branchSegmentChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={78} label>
                              {branchSegmentChartData.map((entry, index) => (
                                <Cell key={`${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-xs text-gray-500 text-center py-16">No segment signals for this branch in the selected window.</p>
                      )}
                    </div>
                    {branchPerformanceInsights?.peerComparison?.topSegment ? (
                      <p className="text-xs text-indigo-700 mt-2">
                        Top segment: <span className="font-semibold">{branchPerformanceInsights.peerComparison.topSegment.name}</span>
                        {' '}({branchPerformanceInsights.peerComparison.topSegment.count})
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4">
                    <p className="text-sm font-semibold text-gray-800 mb-3">Branch Activity by Hour (IST)</p>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={branchHourlyChartData} margin={{ bottom: 8, left: 0, right: 8, top: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="hourLabelCompact" interval={1} angle={-45} textAnchor="end" height={52} tick={{ fontSize: 10 }} />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Bar dataKey="events" fill="#6366f1" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4">
                    <p className="text-sm font-semibold text-gray-800 mb-3">Branch Activity by Weekday</p>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={branchWeekdayChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="dayLabel" />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Bar dataKey="events" fill="#10b981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-rose-100 bg-rose-50/30 p-4">
                    <p className="text-sm font-semibold text-rose-800 mb-2">Branch Drop-off Products (Views, No Sales)</p>
                    <div className="space-y-2 max-h-56 overflow-auto pr-1">
                      {(branchPerformanceInsights.topDropOffProducts || []).slice(0, 8).map((item) => (
                        <div key={item.productId} className="flex items-center justify-between text-sm bg-white/80 rounded-lg px-3 py-2 border border-rose-100">
                          <span className="text-gray-800 line-clamp-1">{item.productName}</span>
                          <span className="text-rose-700 font-semibold shrink-0 ml-2">{item.views || 0} views</span>
                        </div>
                      ))}
                      {(branchPerformanceInsights.topDropOffProducts || []).length === 0 && (
                        <p className="text-xs text-gray-500">No branch drop-off products in this window.</p>
                      )}
                    </div>
                  </div>
                  <div className="rounded-xl border border-amber-100 bg-amber-50/30 p-4">
                    <p className="text-sm font-semibold text-amber-800 mb-2">Underperforming at This Branch</p>
                    <p className="text-[11px] text-amber-700 mb-2">Flags products with views/cart activity but weak branch sales conversion</p>
                    <div className="space-y-2 max-h-56 overflow-auto pr-1">
                      {(branchPerformanceInsights.underperformingProducts || []).slice(0, 8).map((item) => (
                        <div key={`under-${item.productId}`} className="bg-white/80 rounded-lg px-3 py-2 border border-amber-100">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-semibold text-gray-800 line-clamp-1">{item.productName}</p>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded shrink-0">
                              Gap {Number(item.branchGap || 0)}
                            </span>
                          </div>
                          <p className="text-xs text-amber-800 mt-1">{item.underperformanceReason || 'Weak branch conversion'}</p>
                          <p className="text-[11px] text-gray-500 mt-1">
                            {Number(item.views || 0)} views · {Number(item.cartAdditions || 0)} cart · {Number(item.unitsSold || 0)} sold
                            {' · '}View→Sale {Number(item.viewToSaleRate || item.branchConversionRate || 0).toFixed(1)}%
                          </p>
                        </div>
                      ))}
                      {(branchPerformanceInsights.underperformingProducts || []).length === 0 && (
                        <p className="text-xs text-gray-500">No underperforming products flagged for this branch.</p>
                      )}
                    </div>
                  </div>
                </div>

                {branchProductHealthData.length > 0 ? (
                  <div className="overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
                    <div className="grid grid-rows-2 grid-flow-col auto-cols-[minmax(320px,340px)] gap-x-4 gap-y-3 w-max min-w-full">
                      {branchProductHealthData.slice(0, 12).map((product) => {
                        const tierMeta = PRODUCT_HEALTH_TIER_META[product.tier] || PRODUCT_HEALTH_TIER_META.needs_attention;
                        const TierIcon = tierMeta.icon;
                        return (
                          <button
                            key={`branch-health-${product.productId}`}
                            type="button"
                            onClick={() => setSelectedHealthProductDetail(product)}
                            className="text-left rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50/80 p-4 hover:border-cyan-200 hover:shadow-md transition group h-full"
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${tierMeta.chip}`}>
                                <TierIcon className="text-[9px]" />
                                {product.tierLabel}
                              </span>
                              <span className="text-lg font-bold text-cyan-700">{product.performanceScore}</span>
                            </div>
                            <p className="text-sm font-semibold text-gray-800 line-clamp-2 min-h-[2.5rem]">{product.name}</p>
                            <div className="mt-3 flex flex-wrap items-center gap-1 text-[10px] font-semibold text-gray-500">
                              <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{product.views} views</span>
                              <FaArrowRight className="text-[8px] text-gray-300 shrink-0" />
                              <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">{product.carts} cart</span>
                              <FaArrowRight className="text-[8px] text-gray-300 shrink-0" />
                              <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{product.orders} orders</span>
                            </div>
                            <p className="mt-3 text-xs text-gray-600 line-clamp-2 leading-relaxed">{product.action}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-gray-100 p-4">
                    <p className="text-sm font-semibold text-gray-800 mb-2">Top Branch Customers</p>
                    <div className="space-y-2 max-h-64 overflow-auto pr-1">
                      {(branchPerformanceInsights.topCustomers || []).slice(0, 8).map((customer) => (
                        <div key={customer.customerId} className="flex items-center justify-between gap-2 text-sm border-b border-gray-100 pb-2 last:border-b-0">
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-800 line-clamp-1">{customer.customerName}</p>
                            <p className="text-xs text-gray-500">{customer.segment} · Intent {customer.intentScore}</p>
                          </div>
                          <span className="text-emerald-700 font-semibold shrink-0">{formatCurrency(customer.revenue || 0)}</span>
                        </div>
                      ))}
                      {(branchPerformanceInsights.topCustomers || []).length === 0 && (
                        <p className="text-xs text-gray-500">No high-intent branch customers in this window.</p>
                      )}
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-100 p-4">
                    <p className="text-sm font-semibold text-gray-800 mb-2">Branch Pincode Performance</p>
                    <div className="space-y-2 max-h-64 overflow-auto pr-1">
                      {(branchPerformanceInsights.pincodeAnalytics?.topPincodes || []).slice(0, 8).map((row) => (
                        <div key={row.pincode} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-b-0">
                          <span className="font-semibold text-gray-800">{row.pincode}</span>
                          <span className="text-gray-600">{row.totalOrders || 0} orders · {Number(row.deliveredRate || 0).toFixed(1)}% delivered</span>
                        </div>
                      ))}
                      {(branchPerformanceInsights.pincodeAnalytics?.topPincodes || []).length === 0 && (
                        <p className="text-xs text-gray-500">No pincode delivery data for this branch window.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4">
                  <p className="text-sm font-semibold text-indigo-900 mb-2">Branch Action Playbook</p>
                  <div className="space-y-1">
                    {(branchPerformanceInsights.monitoringRecommendations || []).map((item, idx) => (
                      <p key={`branch-rec-${idx}`} className="text-sm text-indigo-900">- {item}</p>
                    ))}
                    {branchPerformanceInsights.peerComparison?.bestBranch ? (
                      <p className="text-sm text-indigo-900">
                        - Benchmark against top branch <span className="font-semibold">{branchPerformanceInsights.peerComparison.bestBranch.branchName}</span>
                        {' '}({formatCurrency(branchPerformanceInsights.peerComparison.bestBranch.revenue || 0)} revenue).
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50/80 via-white to-indigo-50/50 p-5 mt-2">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500" />
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                        <FaWallet className="text-violet-600" /> Free Cash Micro Analytics
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Wallet usage, adoption, expiry risk, notifications & customer-level tracking · {getWindowLabel(selectedWindow)}
                      </p>
                      {freeCashAnalytics?.scopeNote ? (
                        <p className="text-[11px] text-violet-700 mt-1">{freeCashAnalytics.scopeNote}</p>
                      ) : null}
                    </div>
                  </div>

                  {loadingFreeCashAnalytics ? (
                    <p className="text-sm text-gray-500 py-6 text-center">Loading Free Cash analytics...</p>
                  ) : !freeCashAnalytics?.enabled ? (
                    <div className="rounded-xl border border-dashed border-violet-200 bg-white/60 p-6 text-center">
                      <FaWallet className="mx-auto text-violet-300 text-2xl mb-2" />
                      <p className="text-sm text-gray-600">
                        {freeCashAnalytics?.scopeNote || 'Free Cash is not enabled. Turn it on in Admin Permissions to unlock wallet intelligence.'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-5">
                      <div className="overflow-x-auto pb-1 scrollbar-thin">
                        <div className="flex flex-nowrap gap-2 min-w-full">
                          {freeCashSummaryCards.map((card) => (
                            <div key={card.label} className="flex-1 min-w-[130px] shrink-0 rounded-xl border border-violet-100 bg-white/90 px-3 py-2.5 shadow-sm">
                              <p className="text-[10px] uppercase tracking-wider text-violet-600 font-bold">{card.label}</p>
                              <p className="text-lg font-bold text-gray-900 mt-0.5">{card.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                        {[
                          { label: 'Credited Customers', value: freeCashAnalytics.adoption?.totalCreditedCustomers, icon: FaUsers },
                          { label: 'Ever Redeemed', value: freeCashAnalytics.adoption?.activeRedeemers, icon: FaShoppingCart },
                          { label: 'Dormant Wallets', value: freeCashAnalytics.adoption?.dormantHolders, icon: FaHourglassHalf },
                          { label: 'Expiring 24h', value: formatCurrency(freeCashAnalytics.expiryRisk?.expiring24h?.amount || 0), icon: FaExclamationTriangle },
                          { label: 'Expired (Window)', value: formatCurrency(freeCashAnalytics.expiryRisk?.expiredInWindow?.amount || 0), icon: FaClock },
                          { label: 'FC Order Share', value: `${Number(freeCashAnalytics.windowActivity?.freeCashOrderShare || 0).toFixed(1)}%`, icon: FaChartLine },
                        ].map((item) => {
                          const Icon = item.icon;
                          return (
                            <div key={item.label} className="rounded-lg border border-violet-100 bg-white/80 px-3 py-2">
                              <p className="text-[10px] text-gray-500 flex items-center gap-1"><Icon className="text-violet-400" /> {item.label}</p>
                              <p className="text-sm font-bold text-gray-800 mt-0.5">{typeof item.value === 'number' ? item.value.toLocaleString('en-IN') : item.value}</p>
                            </div>
                          );
                        })}
                      </div>

                      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                        <div className="rounded-xl border border-violet-100 bg-white p-4">
                          <p className="text-sm font-semibold text-gray-800 mb-3">Adoption · Using vs Not Using</p>
                          <div className="h-56">
                            {freeCashAdoptionChartData.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie data={freeCashAdoptionChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={72} label>
                                    {freeCashAdoptionChartData.map((entry, index) => (
                                      <Cell key={`fc-adopt-${entry.name}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip />
                                </PieChart>
                              </ResponsiveContainer>
                            ) : (
                              <p className="text-xs text-gray-500 text-center py-16">No adoption data yet.</p>
                            )}
                          </div>
                          <p className="text-xs text-violet-700 mt-1">
                            {Number(freeCashAnalytics.adoption?.neverRedeemedRate || 0).toFixed(1)}% credited customers never redeemed
                          </p>
                        </div>

                        <div className="rounded-xl border border-violet-100 bg-white p-4">
                          <p className="text-sm font-semibold text-gray-800 mb-3">Credits by Source · {getWindowLabel(selectedWindow)}</p>
                          <div className="h-56">
                            {freeCashSourceChartData.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={freeCashSourceChartData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                                  <YAxis />
                                  <Tooltip formatter={(value, name) => [name === 'amount' ? formatCurrency(value) : value, name === 'amount' ? 'Amount' : 'Loads']} />
                                  <Bar dataKey="amount" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <p className="text-xs text-gray-500 text-center py-16">No credits issued in this window.</p>
                            )}
                          </div>
                        </div>

                        <div className="rounded-xl border border-violet-100 bg-white p-4">
                          <p className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2">
                            <FaBell className="text-violet-500" /> Notification Intelligence
                          </p>
                          {freeCashAnalytics.notificationInsights?.retention?.truncatedByRetention ? (
                            <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1 mb-2">
                              {freeCashAnalytics.notificationInsights.retention.note}
                            </p>
                          ) : (
                            <p className="text-[11px] text-gray-500 mb-2">Free Cash alerts (segment loads, custom loads, expiry reminders) for {getWindowLabel(selectedWindow)}</p>
                          )}
                          <div className="space-y-2">
                            {freeCashNotificationCards.map((card) => (
                              <div key={card.label} className="flex items-center justify-between text-sm border-b border-violet-50 pb-2 last:border-b-0">
                                <span className="text-gray-600">{card.label}</span>
                                <span className="font-semibold text-violet-800">{card.value}</span>
                              </div>
                            ))}
                          </div>
                          {Number(freeCashAnalytics.notificationInsights?.totalSent || 0) === 0 ? (
                            <p className="mt-3 text-[11px] text-gray-500">
                              No Free Cash alerts were sent in this window. Enable the Free Cash channel and its events in Notification settings to start nudging wallet holders.
                            </p>
                          ) : (
                            <div className="mt-3 grid grid-cols-3 gap-1 text-[10px]">
                              {['segment_load', 'custom_load', 'expiry_reminder'].map((evt) => {
                                const row = freeCashAnalytics.notificationInsights?.byEvent?.[evt] || {};
                                const labels = { segment_load: 'Segment', custom_load: 'Custom', expiry_reminder: 'Expiry' };
                                return (
                                  <div key={evt} className="rounded bg-violet-50 px-2 py-1.5 text-center">
                                    <p className="text-violet-600 font-bold">{labels[evt]}</p>
                                    <p className="text-gray-800 font-semibold">{Number(row.count || 0).toLocaleString('en-IN')}</p>
                                    <p className="text-[9px] text-gray-500">{Number(row.delivered || 0).toLocaleString('en-IN')} pushed</p>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {(freeCashAnalytics.notificationInsights?.allEvents || [])
                            .filter((row) => !['segment_load', 'custom_load', 'expiry_reminder'].includes(row.event))
                            .length > 0 && (
                            <p className="mt-2 text-[10px] text-gray-500">
                              Other Free Cash alerts:{' '}
                              {(freeCashAnalytics.notificationInsights.allEvents || [])
                                .filter((row) => !['segment_load', 'custom_load', 'expiry_reminder'].includes(row.event))
                                .map((row) => `${row.event} (${row.count})`)
                                .join(', ')}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-violet-100 bg-gray-50/50 p-4">
                          <p className="text-sm font-semibold text-gray-800 mb-3">Free Cash Activity by Hour (IST)</p>
                          <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={freeCashHourlyChartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="hourLabel" interval={2} tick={{ fontSize: 9 }} />
                                <YAxis />
                                <Tooltip formatter={(value, name) => [formatCurrency(value), name]} />
                                <Bar dataKey="Credits" fill="#a78bfa" radius={[3, 3, 0, 0]} />
                                <Bar dataKey="Redeemed" fill="#10b981" radius={[3, 3, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {selectedBranchPerformanceId === 'all' && freeCashBranchChartData.length > 0 ? (
                          <div className="rounded-xl border border-violet-100 bg-gray-50/50 p-4">
                            <p className="text-sm font-semibold text-gray-800 mb-3">Free Cash Discount by Branch</p>
                            <div className="h-56">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={freeCashBranchChartData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                                  <YAxis />
                                  <Tooltip formatter={(value, name) => [name === 'Discount' ? formatCurrency(value) : value, name]} />
                                  <Bar dataKey="Discount" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                                  <Bar dataKey="Orders" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        ) : (
                          <div className="rounded-xl border border-violet-100 bg-white p-4">
                            <p className="text-sm font-semibold text-gray-800 mb-3">Window Snapshot</p>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between"><span className="text-gray-600">Credits issued</span><span className="font-semibold">{formatCurrency(freeCashAnalytics.windowActivity?.creditsIssued || 0)}</span></div>
                              <div className="flex justify-between"><span className="text-gray-600">Redeemed</span><span className="font-semibold text-emerald-700">{formatCurrency(freeCashAnalytics.windowActivity?.redeemed || 0)}</span></div>
                              <div className="flex justify-between"><span className="text-gray-600">Discount on orders</span><span className="font-semibold">{formatCurrency(freeCashAnalytics.windowActivity?.freeCashDiscountTotal || 0)}</span></div>
                              <div className="flex justify-between"><span className="text-gray-600">Unique redeemers</span><span className="font-semibold">{freeCashAnalytics.windowActivity?.uniqueRedeemersInWindow || 0}</span></div>
                              <div className="flex justify-between"><span className="text-gray-600">Lifetime expired</span><span className="font-semibold text-rose-600">{formatCurrency(freeCashAnalytics.summary?.totalExpired || 0)}</span></div>
                              <div className="flex justify-between"><span className="text-gray-600">Redemption rate</span><span className="font-semibold">{Number(freeCashAnalytics.summary?.redemptionRate || 0).toFixed(1)}%</span></div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-emerald-100 bg-emerald-50/20 p-4">
                          <p className="text-sm font-semibold text-emerald-800 mb-2">Top Redeemers · {getWindowLabel(selectedWindow)}</p>
                          <div className="space-y-2 max-h-56 overflow-auto pr-1">
                            {(freeCashAnalytics.topRedeemers || []).map((c) => (
                              <div key={c.customerId} className="flex items-center justify-between gap-2 text-sm bg-white/80 rounded-lg px-3 py-2 border border-emerald-100">
                                <div className="min-w-0">
                                  <p className="font-semibold text-gray-800 line-clamp-1">{c.customerName}</p>
                                  <p className="text-xs text-gray-500">
                                    {c.mobile || c.email || '—'} · {c.redemptions || 0} orders
                                    {c.source ? ` · ${c.source}` : ''}
                                  </p>
                                </div>
                                <span className="text-emerald-700 font-semibold shrink-0">{formatCurrency(c.redeemed || 0)}</span>
                              </div>
                            ))}
                            {(freeCashAnalytics.topRedeemers || []).length === 0 && (
                              <p className="text-xs text-gray-500">No redemptions in this window.</p>
                            )}
                          </div>
                        </div>

                        <div className="rounded-xl border border-amber-100 bg-amber-50/20 p-4">
                          <p className="text-sm font-semibold text-amber-800 mb-2">High Balance · Not Redeeming (30d)</p>
                          <div className="space-y-2 max-h-56 overflow-auto pr-1">
                            {(freeCashAnalytics.highBalanceNoRedemption || []).map((c) => (
                              <div key={c.customerId} className="flex items-center justify-between gap-2 text-sm bg-white/80 rounded-lg px-3 py-2 border border-amber-100">
                                <div className="min-w-0">
                                  <p className="font-semibold text-gray-800 line-clamp-1">{c.customerName}</p>
                                  <p className="text-xs text-gray-500">{c.mobile || '—'}</p>
                                </div>
                                <span className="text-amber-700 font-semibold shrink-0">{formatCurrency(c.balance || 0)}</span>
                              </div>
                            ))}
                            {(freeCashAnalytics.highBalanceNoRedemption || []).length === 0 && (
                              <p className="text-xs text-gray-500">No dormant high-balance wallets.</p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-xl border border-sky-100 bg-sky-50/20 p-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div>
                            <p className="text-sm font-semibold text-sky-800">Orders Using Free Cash · {getWindowLabel(selectedWindow)}</p>
                            <p className="text-[11px] text-sky-600">Exact order, customer, branch and discount used for tracking redemption</p>
                          </div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-sky-700 bg-white border border-sky-100 rounded-full px-2 py-1">
                            {(freeCashAnalytics.ordersUsingFreeCash || []).length} recent
                          </span>
                        </div>
                        <div className="overflow-x-auto">
                          {(freeCashAnalytics.ordersUsingFreeCash || []).length > 0 ? (
                            <table className="min-w-full text-xs">
                              <thead>
                                <tr className="text-left text-sky-700 border-b border-sky-100">
                                  <th className="py-2 pr-3 font-semibold">Order</th>
                                  <th className="py-2 pr-3 font-semibold">Customer</th>
                                  <th className="py-2 pr-3 font-semibold">Branch</th>
                                  <th className="py-2 pr-3 font-semibold text-right">Free Cash</th>
                                  <th className="py-2 pr-3 font-semibold text-right">Payable</th>
                                  <th className="py-2 pr-3 font-semibold">Status</th>
                                  <th className="py-2 font-semibold">Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(freeCashAnalytics.ordersUsingFreeCash || []).map((order) => (
                                  <tr key={order.orderId} className="border-b border-sky-50 last:border-b-0 hover:bg-white/70">
                                    <td className="py-2 pr-3">
                                      <button
                                        type="button"
                                        onClick={() => handleNavigateOrderOverview(order.orderId)}
                                        className="font-semibold text-sky-700 hover:text-sky-900 hover:underline"
                                      >
                                        {order.saleId || order.orderId}
                                      </button>
                                    </td>
                                    <td className="py-2 pr-3">
                                      <p className="font-semibold text-gray-800 line-clamp-1">{order.customerName || 'Customer'}</p>
                                      <p className="text-[10px] text-gray-500">{order.mobile || '—'}</p>
                                    </td>
                                    <td className="py-2 pr-3 text-gray-600">{order.branchName || '—'}</td>
                                    <td className="py-2 pr-3 text-right font-semibold text-violet-700">{formatCurrency(order.freeCashDiscount || 0)}</td>
                                    <td className="py-2 pr-3 text-right font-semibold text-gray-800">{formatCurrency(order.finalAmount || 0)}</td>
                                    <td className="py-2 pr-3">
                                      <span className="inline-flex rounded-full bg-white border border-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                                        {order.status || '—'}
                                      </span>
                                    </td>
                                    <td className="py-2 text-gray-500 whitespace-nowrap">{formatDate(order.orderPlacedOn)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-xs text-gray-500 py-6 text-center">No orders used Free Cash in this window.</p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-rose-100 bg-rose-50/20 p-4">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div>
                              <p className="text-sm font-semibold text-rose-800">Notified but Didn&apos;t Use Free Cash</p>
                              <p className="text-[11px] text-rose-600">Customers who received a Free Cash push/in-app alert but did not redeem after it</p>
                            </div>
                            <span className="shrink-0 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
                              {Number(notifiedNoUsageMeta.totalCount || 0).toLocaleString('en-IN')} customers
                            </span>
                          </div>

                          {notifiedNoUsageMeta.summary ? (
                            <div className="grid grid-cols-3 gap-1 mb-2 text-[10px]">
                              <div className="rounded bg-white/80 border border-rose-100 px-2 py-1.5 text-center">
                                <p className="text-gray-500">Notified</p>
                                <p className="font-bold text-gray-800">{Number(notifiedNoUsageMeta.summary.notifiedCustomers || 0).toLocaleString('en-IN')}</p>
                              </div>
                              <div className="rounded bg-white/80 border border-rose-100 px-2 py-1.5 text-center">
                                <p className="text-gray-500">Redeemed after</p>
                                <p className="font-bold text-emerald-700">{Number(notifiedNoUsageMeta.summary.usedAfterNotify || 0).toLocaleString('en-IN')}</p>
                              </div>
                              <div className="rounded bg-white/80 border border-rose-100 px-2 py-1.5 text-center">
                                <p className="text-gray-500">Wallet at risk</p>
                                <p className="font-bold text-rose-700">{formatCurrency(notifiedNoUsageMeta.summary.walletAtRisk || 0)}</p>
                              </div>
                            </div>
                          ) : null}

                          <div className="relative mb-2">
                            <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                            <input
                              type="text"
                              value={notifiedNoUsageSearch}
                              onChange={(e) => setNotifiedNoUsageSearch(e.target.value)}
                              placeholder="Search by name, mobile or email..."
                              className="w-full rounded-lg border border-rose-100 bg-white pl-8 pr-8 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-rose-200"
                            />
                            {notifiedNoUsageSearch ? (
                              <button
                                type="button"
                                onClick={() => setNotifiedNoUsageSearch('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                title="Clear search"
                              >
                                <FaTimes className="text-xs" />
                              </button>
                            ) : null}
                          </div>

                          <div className="space-y-2 min-h-[9rem]">
                            {loadingNotifiedNoUsage ? (
                              <div className="flex items-center justify-center py-10">
                                <FaSyncAlt className="animate-spin text-rose-400" />
                              </div>
                            ) : notifiedNoUsageRows.length > 0 ? (
                              notifiedNoUsageRows.map((c) => (
                                <button
                                  key={c.customerId}
                                  type="button"
                                  onClick={() => setNotifiedDetailCustomer(c)}
                                  title="View full notification details"
                                  className="w-full flex items-center justify-between gap-2 text-sm bg-white/80 rounded-lg px-3 py-2 border border-rose-100 text-left hover:bg-white hover:border-rose-300 hover:shadow-sm transition-all"
                                >
                                  <div className="min-w-0">
                                    <p className="font-semibold text-gray-800 line-clamp-1">{c.customerName}</p>
                                    <p className="text-xs text-gray-500">{c.mobile || c.email || '—'}</p>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <div className="text-right">
                                      <p className="font-semibold text-rose-700">{formatCurrency(c.walletBalance || 0)}</p>
                                      <p className="text-[9px] text-gray-500">wallet balance</p>
                                    </div>
                                    <FaChevronRight className="text-rose-300 text-xs" />
                                  </div>
                                </button>
                              ))
                            ) : (
                              <p className="text-xs text-gray-500 py-6 text-center">
                                {debouncedNotifiedSearch
                                  ? `No notified customer matches "${debouncedNotifiedSearch}".`
                                  : 'No notification ghost users in this window — everyone who got an alert redeemed after it.'}
                              </p>
                            )}
                          </div>

                          {Number(notifiedNoUsageMeta.totalCount || 0) > NOTIFIED_NO_USAGE_PAGE_SIZE ? (
                            <div className="mt-3 flex items-center justify-between text-[11px]">
                              <span className="text-gray-500">
                                Showing {notifiedNoUsagePage * NOTIFIED_NO_USAGE_PAGE_SIZE + 1}–{notifiedNoUsagePage * NOTIFIED_NO_USAGE_PAGE_SIZE + notifiedNoUsageRows.length} of {Number(notifiedNoUsageMeta.totalCount || 0).toLocaleString('en-IN')} · page {notifiedNoUsagePage + 1}/{notifiedNoUsageTotalPages}
                              </span>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => setNotifiedNoUsagePage((p) => Math.max(0, p - 1))}
                                  disabled={notifiedNoUsagePage === 0 || loadingNotifiedNoUsage}
                                  className="rounded-lg border border-rose-200 bg-white px-2 py-1 font-semibold text-rose-700 disabled:opacity-40"
                                >
                                  Prev
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setNotifiedNoUsagePage((p) => Math.min(notifiedNoUsageTotalPages - 1, p + 1))}
                                  disabled={notifiedNoUsagePage >= notifiedNoUsageTotalPages - 1 || loadingNotifiedNoUsage}
                                  className="rounded-lg border border-rose-200 bg-white px-2 py-1 font-semibold text-rose-700 disabled:opacity-40"
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          ) : null}

                          {notifiedNoUsageMeta.notificationWindow?.truncatedByRetention ? (
                            <p className="mt-2 text-[10px] text-amber-700">
                              Alert history is kept for {notifiedNoUsageMeta.notificationWindow.retentionDays} days, so this list covers the last {notifiedNoUsageMeta.notificationWindow.retentionDays} days of notifications.
                            </p>
                          ) : null}
                        </div>

                        <div className="rounded-xl border border-orange-100 bg-orange-50/20 p-4">
                          <p className="text-sm font-semibold text-orange-800 mb-2">Expiring Soon (≤ 3 days)</p>
                          <div className="space-y-2 max-h-56 overflow-auto pr-1">
                            {(freeCashAnalytics.expiringSoon || []).map((c) => (
                              <div key={c.customerId} className="flex items-center justify-between gap-2 text-sm bg-white/80 rounded-lg px-3 py-2 border border-orange-100">
                                <div className="min-w-0">
                                  <p className="font-semibold text-gray-800 line-clamp-1">{c.customerName}</p>
                                  <p className="text-xs text-gray-500">{c.mobile || '—'}</p>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-orange-700 font-semibold">{formatCurrency(c.balance || 0)}</p>
                                  {c.expiresAt ? <p className="text-[10px] text-gray-400">{formatDate(c.expiresAt)}</p> : null}
                                </div>
                              </div>
                            ))}
                            {(freeCashAnalytics.expiringSoon || []).length === 0 && (
                              <p className="text-xs text-gray-500">No wallets expiring in the next 3 days.</p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-4">
                        <p className="text-sm font-semibold text-violet-900 mb-2">Free Cash Action Playbook</p>
                        <div className="space-y-1">
                          {(freeCashAnalytics.monitoringRecommendations || []).map((item, idx) => (
                            <p key={`fc-rec-${idx}`} className="text-sm text-violet-900">- {item}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Instore Sales Micro Analytics — POS / walk-in deep dive (sibling to Free Cash) */}
                <div className="relative overflow-hidden rounded-2xl border border-teal-200 bg-gradient-to-br from-teal-50/80 via-white to-cyan-50/50 p-5 mt-2">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-cyan-500 to-emerald-500" />
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                        <FaStore className="text-teal-600" /> Instore Sales Micro Analytics
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        POS stock, golden hours, returns, loyalty &amp; offers · revenue net of returns · {getWindowLabel(selectedWindow)}
                      </p>
                      {instoreSalesAnalytics?.scopeNote ? (
                        <p className="text-[11px] text-teal-700 mt-1">{instoreSalesAnalytics.scopeNote}</p>
                      ) : null}
                    </div>
                    <div className="min-w-[220px]">
                      <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">POS Branch</label>
                      <Select
                        value={selectedInstoreBranchOption}
                        onChange={(opt) => setSelectedInstoreBranchId(opt ? opt.value : 'all')}
                        options={instoreBranchOptions}
                        isSearchable
                        placeholder="Select POS branch"
                        classNamePrefix="instore-branch-select"
                        styles={{
                          control: (base) => ({ ...base, borderRadius: 12, borderColor: '#99f6e4', minHeight: 38, fontSize: 13 }),
                          menu: (base) => ({ ...base, fontSize: 13, zIndex: 30 }),
                        }}
                      />
                    </div>
                  </div>

                  {loadingInstoreSalesAnalytics ? (
                    <p className="text-sm text-gray-500 py-6 text-center">Loading Instore Sales analytics...</p>
                  ) : !instoreSalesAnalytics?.enabled ? (
                    <div className="rounded-xl border border-dashed border-teal-200 bg-white/60 p-6 text-center">
                      <FaStore className="mx-auto text-teal-300 text-2xl mb-2" />
                      <p className="text-sm text-gray-600">
                        {instoreSalesAnalytics?.scopeNote || 'Instore (POS) analytics are unavailable right now.'}
                      </p>
                    </div>
                  ) : (instoreSalesAnalytics.summary?.totalOrders || 0) === 0 ? (
                    <div className="rounded-xl border border-dashed border-teal-200 bg-white/60 p-8 text-center">
                      <FaStore className="mx-auto text-teal-300 text-3xl mb-2" />
                      <p className="text-sm font-semibold text-gray-700">No in-store (POS) sales in this window</p>
                      <p className="text-xs text-gray-500 mt-1">Bill walk-in customers from Merchant Billing to unlock movement analytics.</p>
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {/* Summary strip */}
                      <div className="flex flex-nowrap gap-2 overflow-x-auto pb-1">
                        {instoreSummaryCards.map((card) => (
                          <div
                            key={card.label}
                            className={`min-w-[130px] shrink-0 rounded-xl border px-3 py-2.5 ${
                              card.tone === 'rose'
                                ? 'border-rose-100 bg-rose-50/70'
                                : card.tone === 'emerald'
                                  ? 'border-emerald-100 bg-emerald-50/70'
                                  : card.tone === 'amber'
                                    ? 'border-amber-100 bg-amber-50/70'
                                    : 'border-teal-100 bg-white/80'
                            }`}
                          >
                            <p className="text-[11px] text-gray-500">{card.label}</p>
                            <p className={`text-sm font-bold mt-0.5 ${
                              card.tone === 'rose'
                                ? 'text-rose-800'
                                : card.tone === 'emerald'
                                  ? 'text-emerald-800'
                                  : card.tone === 'amber'
                                    ? 'text-amber-800'
                                    : 'text-gray-800'
                            }`}
                            >
                              {card.value}
                            </p>
                            {card.hint ? (
                              <p className="text-[10px] text-gray-400 mt-0.5">{card.hint}</p>
                            ) : null}
                          </div>
                        ))}
                      </div>

                      {/* Returned items */}
                      <div className="rounded-xl border border-rose-100 bg-white/90 overflow-hidden">
                        <div className="px-4 py-3 border-b border-rose-50 flex flex-wrap items-center justify-between gap-2 bg-gradient-to-r from-rose-50/80 to-white">
                          <div>
                            <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                              <FaUndo className="text-rose-600" /> Returned items
                            </p>
                            <p className="text-[11px] text-gray-500 mt-0.5">
                              Same window &amp; branch · Just return restocks · Damaged does not · Revenue KPIs are net
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-1.5 text-[10px] font-semibold">
                            <span className="px-2 py-1 rounded-md bg-rose-100 text-rose-800 inline-flex items-center gap-1">
                              <FaUndo /> {formatCurrency(instoreSalesAnalytics?.returns?.summary?.totalRefund || 0)}
                            </span>
                            <span className="px-2 py-1 rounded-md bg-emerald-100 text-emerald-800 inline-flex items-center gap-1">
                              <FaBoxOpen /> Restocked {Number(instoreSalesAnalytics?.returns?.summary?.restockedQty || 0).toLocaleString('en-IN')}
                            </span>
                            <span className="px-2 py-1 rounded-md bg-amber-100 text-amber-800">
                              Damaged {Number(instoreSalesAnalytics?.returns?.summary?.damagedQty || 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                        </div>
                        {(instoreSalesAnalytics?.returns?.items || []).length === 0 ? (
                          <p className="text-xs text-gray-500 text-center py-8 px-4">No returned items in this window.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                                <tr>
                                  <th className="px-3 py-2 text-left font-medium">Invoice</th>
                                  <th className="px-3 py-2 text-left font-medium">Product</th>
                                  <th className="px-3 py-2 text-left font-medium">Option</th>
                                  <th className="px-3 py-2 text-right font-medium">Qty</th>
                                  <th className="px-3 py-2 text-right font-medium">Refund</th>
                                  <th className="px-3 py-2 text-left font-medium">Reason</th>
                                  <th className="px-3 py-2 text-left font-medium">Stock</th>
                                  <th className="px-3 py-2 text-left font-medium">When</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {(instoreSalesAnalytics.returns.items || []).map((r) => (
                                  <tr key={String(r._id)} className="hover:bg-rose-50/40">
                                    <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{r.walkInOrderId || '—'}</td>
                                    <td className="px-3 py-2 text-slate-800">
                                      {r.productName}
                                      {r.isFreeOfferItem ? (
                                        <span className="ml-1 text-[9px] uppercase tracking-wide px-1 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">Free</span>
                                      ) : null}
                                    </td>
                                    <td className="px-3 py-2 text-slate-500">{r.optionLabel || '—'}</td>
                                    <td className="px-3 py-2 text-right font-medium">{r.quantity}</td>
                                    <td className="px-3 py-2 text-right font-semibold text-rose-700">{formatCurrency(r.refundAmount)}</td>
                                    <td className="px-3 py-2">
                                      <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                                        r.reason === 'damaged' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                                      }`}
                                      >
                                        {r.reasonLabel || (r.reason === 'damaged' ? 'Damaged' : 'Just return')}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-slate-600">{r.stockImpact || '—'}</td>
                                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                                      {r.returnedAt
                                        ? new Date(r.returnedAt).toLocaleString('en-IN', {
                                          day: '2-digit',
                                          month: 'short',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })
                                        : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {(instoreSalesAnalytics?.returns?.summary?.totalLines || 0) > (instoreSalesAnalytics?.returns?.items || []).length && (
                              <p className="px-3 py-2 text-[11px] text-slate-500 border-t border-slate-100">
                                Showing latest {(instoreSalesAnalytics.returns.items || []).length} of {instoreSalesAnalytics.returns.summary.totalLines} return lines
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Sales by hour + Payment mix */}
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="lg:col-span-2 rounded-xl border border-teal-100 bg-white/80 p-4">
                          <p className="text-sm font-semibold text-gray-800 mb-1">Sales by Hour of Day</p>
                          <p className="text-[11px] text-gray-500 mb-2">Find your golden hours — when revenue &amp; footfall peak in-store.</p>
                          <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={instoreHourlyChartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value, name) => (name === 'Revenue' ? formatCurrency(value) : value)} />
                                <Bar dataKey="Revenue" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                        <div className="rounded-xl border border-teal-100 bg-white/80 p-4">
                          <p className="text-sm font-semibold text-gray-800 mb-2">Payment Mix</p>
                          {instorePaymentChartData.length > 0 ? (
                            <div className="h-56">
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie data={instorePaymentChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={(e) => `${e.name} ${e.share}%`} labelLine={false}>
                                    {instorePaymentChartData.map((entry, idx) => (
                                      <Cell key={`pay-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value) => formatCurrency(value)} />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 text-center py-16">No payment data yet.</p>
                          )}
                        </div>
                      </div>

                      {/* Daily timeline (multi-day) + Day of week */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {instoreDailyChartData.length > 1 && (
                          <div className="rounded-xl border border-teal-100 bg-white/80 p-4">
                            <p className="text-sm font-semibold text-gray-800 mb-2">Revenue Timeline</p>
                            <div className="h-56">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={instoreDailyChartData}>
                                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                                  <YAxis tick={{ fontSize: 10 }} />
                                  <Tooltip formatter={(value, name) => (name === 'Revenue' ? formatCurrency(value) : value)} />
                                  <Line type="monotone" dataKey="Revenue" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                                  <Line type="monotone" dataKey="Bills" stroke="#10b981" strokeWidth={2} dot={false} />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        )}
                        <div className={`rounded-xl border border-teal-100 bg-white/80 p-4 ${instoreDailyChartData.length > 1 ? '' : 'lg:col-span-2'}`}>
                          <p className="text-sm font-semibold text-gray-800 mb-2">Best Days of the Week</p>
                          <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={instoreDowChartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value, name) => (name === 'Revenue' ? formatCurrency(value) : value)} />
                                <Bar dataKey="Revenue" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>

                      {/* Fast movers + Dead stock */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-emerald-100 bg-emerald-50/30 p-4">
                          <p className="text-sm font-semibold text-emerald-900 mb-1 flex items-center gap-2"><FaBolt className="text-emerald-600" /> Fast Movers</p>
                          <p className="text-[11px] text-gray-500 mb-2">Highest velocity — keep these always in stock.</p>
                          <div className="space-y-2 max-h-72 overflow-auto pr-1">
                            {(instoreSalesAnalytics.topMovers || []).map((p, idx) => (
                              <div key={p.productId || idx} className="flex items-center justify-between gap-2 text-sm bg-white/80 rounded-lg px-3 py-2 border border-emerald-100">
                                <div className="min-w-0 flex items-center gap-2">
                                  <span className="text-xs font-bold text-emerald-700 w-5 shrink-0">#{idx + 1}</span>
                                  <div className="min-w-0">
                                    <p className="font-semibold text-gray-800 line-clamp-1">{p.productName}</p>
                                    <p className="text-[11px] text-gray-500">{p.velocityPerDay}/day · {p.orders} bills</p>
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-emerald-700 font-semibold">{p.units} units</p>
                                  <p className="text-[10px] text-gray-400">{formatCurrency(p.revenue)}</p>
                                </div>
                              </div>
                            ))}
                            {(instoreSalesAnalytics.topMovers || []).length === 0 && (
                              <p className="text-xs text-gray-500">No product movement in this window.</p>
                            )}
                          </div>
                        </div>
                        <div className="rounded-xl border border-rose-100 bg-rose-50/30 p-4">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="text-sm font-semibold text-rose-900 flex items-center gap-2"><FaExclamationTriangle className="text-rose-500" /> Dead Stock (no in-store sales)</p>
                            {(instoreSalesAnalytics.deadStockCount || 0) > 0 && (
                              <button
                                type="button"
                                onClick={() => { setDeadStockSearch(''); setDeadStockModalOpen(true); }}
                                className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-rose-700 bg-white border border-rose-200 rounded-lg px-2.5 py-1 hover:bg-rose-50 transition-colors"
                              >
                                View All <FaArrowRight className="text-[10px]" />
                              </button>
                            )}
                          </div>
                          <p className="text-[11px] text-gray-500 mb-2">
                            {instoreSalesAnalytics.deadStockCount || 0} product(s) · {Number(instoreSalesAnalytics.deadStockUnits || 0).toLocaleString('en-IN')} units · ~{formatCurrency(instoreSalesAnalytics.deadStockValue || 0)} stuck.
                          </p>
                          <div className="space-y-2 max-h-72 overflow-auto pr-1">
                            {(instoreSalesAnalytics.deadStock || []).map((p, idx) => (
                              <div key={p.productId || idx} className="flex items-center justify-between gap-2 text-sm bg-white/80 rounded-lg px-3 py-2 border border-rose-100">
                                <div className="min-w-0">
                                  <p className="font-semibold text-gray-800 line-clamp-1">{p.productName}</p>
                                  <p className="text-[11px] text-gray-500">{p.sku ? `${p.sku} · ` : ''}{p.category}</p>
                                </div>
                                <span className="text-rose-700 font-semibold shrink-0">{p.availableQuantity} in stock</span>
                              </div>
                            ))}
                            {(instoreSalesAnalytics.deadStock || []).length === 0 && (
                              <p className="text-xs text-gray-500">No dead stock — everything moved at least once.</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Best product by hour + Category performance */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-teal-100 bg-white/80 p-4">
                          <p className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2"><FaClock className="text-teal-600" /> Best-Selling Product by Hour</p>
                          <p className="text-[11px] text-gray-500 mb-2">What sells most at each time of day — plan stock &amp; counter display.</p>
                          <div className="space-y-1.5 max-h-72 overflow-auto pr-1">
                            {(instoreSalesAnalytics.bestProductByHour || []).map((row) => (
                              <div key={row.hour} className="flex items-center justify-between gap-2 text-sm bg-teal-50/40 rounded-lg px-3 py-1.5 border border-teal-100">
                                <span className="text-xs font-semibold text-teal-700 w-14 shrink-0">{row.hourLabel}</span>
                                <span className="font-medium text-gray-800 line-clamp-1 flex-1">{row.productName}</span>
                                <span className="text-[11px] text-gray-500 shrink-0">{row.units} units</span>
                              </div>
                            ))}
                            {(instoreSalesAnalytics.bestProductByHour || []).length === 0 && (
                              <p className="text-xs text-gray-500">No hourly product data yet.</p>
                            )}
                          </div>
                        </div>
                        <div className="rounded-xl border border-teal-100 bg-white/80 p-4">
                          <p className="text-sm font-semibold text-gray-800 mb-2">Category Performance</p>
                          {instoreCategoryChartData.length > 0 ? (
                            <div className="h-72">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={instoreCategoryChartData} layout="vertical" margin={{ left: 12 }}>
                                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                  <XAxis type="number" tick={{ fontSize: 10 }} />
                                  <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={90} />
                                  <Tooltip formatter={(value, name) => (name === 'Revenue' ? formatCurrency(value) : value)} />
                                  <Bar dataKey="Revenue" fill="#0d9488" radius={[0, 4, 4, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 text-center py-16">No category data yet.</p>
                          )}
                        </div>
                      </div>

                      {/* Sub-Category performance */}
                      {instoreSubCategoryChartData.length > 0 && (
                        <div className="rounded-xl border border-teal-100 bg-white/80 p-4">
                          <p className="text-sm font-semibold text-gray-800 mb-1">Sub-Category Performance</p>
                          <p className="text-[11px] text-gray-500 mb-2">Revenue by sub-category (parent › sub) — drill into what's really driving each category.</p>
                          <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={instoreSubCategoryChartData} layout="vertical" margin={{ left: 12 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 10 }} />
                                <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={140} />
                                <Tooltip formatter={(value, name) => (name === 'Revenue' ? formatCurrency(value) : value)} />
                                <Bar dataKey="Revenue" fill="#0891b2" radius={[0, 4, 4, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}

                      {/* Reorder radar + Frequently bought together */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-orange-100 bg-orange-50/30 p-4">
                          <p className="text-sm font-semibold text-orange-900 mb-1 flex items-center gap-2"><FaBolt className="text-orange-500" /> Reorder Radar</p>
                          <p className="text-[11px] text-gray-500 mb-2">Fast-movers running low — days of cover at current in-store velocity.</p>
                          <div className="space-y-2 max-h-72 overflow-auto pr-1">
                            {(instoreSalesAnalytics.reorderRadar || []).map((p, idx) => (
                              <div key={p.productId || idx} className="flex items-center justify-between gap-2 text-sm bg-white/80 rounded-lg px-3 py-2 border border-orange-100">
                                <div className="min-w-0">
                                  <p className="font-semibold text-gray-800 line-clamp-1">{p.productName}</p>
                                  <p className="text-[11px] text-gray-500">{p.velocityPerDay}/day · {p.stock} in stock</p>
                                </div>
                                <span className={`text-xs font-bold shrink-0 px-2 py-1 rounded-lg ${p.daysCover <= 3 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {p.daysCover}d cover
                                </span>
                              </div>
                            ))}
                            {(instoreSalesAnalytics.reorderRadar || []).length === 0 && (
                              <p className="text-xs text-gray-500">No fast-movers running low. Stock levels look healthy.</p>
                            )}
                          </div>
                        </div>
                        <div className="rounded-xl border border-indigo-100 bg-indigo-50/30 p-4">
                          <p className="text-sm font-semibold text-indigo-900 mb-1 flex items-center gap-2"><FaShoppingCart className="text-indigo-500" /> Frequently Bought Together</p>
                          <p className="text-[11px] text-gray-500 mb-2">Product pairs that show up on the same bill — perfect for combos &amp; counter placement.</p>
                          <div className="space-y-2 max-h-72 overflow-auto pr-1">
                            {(instoreSalesAnalytics.frequentlyBoughtTogether || []).map((p, idx) => (
                              <div key={idx} className="flex items-center justify-between gap-2 text-sm bg-white/80 rounded-lg px-3 py-2 border border-indigo-100">
                                <p className="font-medium text-gray-800 line-clamp-2 min-w-0">
                                  {p.productA} <span className="text-indigo-400">+</span> {p.productB}
                                </p>
                                <span className="text-xs font-bold text-indigo-700 shrink-0">{p.count}×</span>
                              </div>
                            ))}
                            {(instoreSalesAnalytics.frequentlyBoughtTogether || []).length === 0 && (
                              <p className="text-xs text-gray-500">Not enough multi-item bills yet to detect combos.</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Day x Hour heatmap */}
                      {(instoreSalesAnalytics.heatmapMax || 0) > 0 && (
                        <div className="rounded-xl border border-teal-100 bg-white/80 p-4">
                          <p className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2"><FaFire className="text-teal-600" /> Best-of-Best Timing Grid</p>
                          <p className="text-[11px] text-gray-500 mb-3">Revenue intensity by day &amp; hour — the darker the cell, the hotter the slot.</p>
                          <div className="overflow-x-auto">
                            <div className="min-w-[640px]">
                              <div className="flex items-center gap-1 mb-1 pl-10">
                                {Array.from({ length: 24 }, (_, h) => (
                                  <div key={h} className="flex-1 text-center text-[8px] text-gray-400">
                                    {h % 3 === 0 ? h : ''}
                                  </div>
                                ))}
                              </div>
                              {(instoreSalesAnalytics.heatmap || []).map((row) => (
                                <div key={row.dow} className="flex items-center gap-1 mb-1">
                                  <div className="w-9 text-[10px] font-semibold text-gray-500 shrink-0">{row.label}</div>
                                  {row.hours.map((cell) => {
                                    const intensity = instoreSalesAnalytics.heatmapMax > 0 ? cell.revenue / instoreSalesAnalytics.heatmapMax : 0;
                                    const bg = cell.revenue > 0 ? `rgba(13, 148, 136, ${0.12 + intensity * 0.85})` : '#f1f5f9';
                                    return (
                                      <div
                                        key={cell.hour}
                                        className="flex-1 rounded-sm"
                                        style={{ height: 18, backgroundColor: bg }}
                                        title={`${row.label} ${formatHourLabel(cell.hour)} · ${formatCurrency(cell.revenue)} · ${cell.orders} bills`}
                                      />
                                    );
                                  })}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Slow movers table */}
                      {(instoreSalesAnalytics.slowMovers || []).length > 0 && (
                        <div className="rounded-xl border border-amber-100 bg-amber-50/20 p-4">
                          <p className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2"><FaHourglassHalf className="text-amber-600" /> Slow Movers (sold, but barely)</p>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="text-left text-gray-500">
                                <tr>
                                  <th className="py-1.5 pr-3 font-medium">Product</th>
                                  <th className="py-1.5 px-3 font-medium text-right">Units</th>
                                  <th className="py-1.5 px-3 font-medium text-right">Bills</th>
                                  <th className="py-1.5 pl-3 font-medium text-right">Revenue</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-amber-100">
                                {instoreSalesAnalytics.slowMovers.map((p, idx) => (
                                  <tr key={p.productId || idx}>
                                    <td className="py-1.5 pr-3 text-gray-800 line-clamp-1">{p.productName}</td>
                                    <td className="py-1.5 px-3 text-right text-gray-700">{p.units}</td>
                                    <td className="py-1.5 px-3 text-right text-gray-700">{p.orders}</td>
                                    <td className="py-1.5 pl-3 text-right text-gray-700">{formatCurrency(p.revenue)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Action playbook */}
                      {(instoreSalesAnalytics.insights || []).length > 0 && (
                        <div className="rounded-xl border border-teal-200 bg-teal-50/40 p-4">
                          <p className="text-sm font-semibold text-teal-900 mb-2">Instore Sales Action Playbook</p>
                          <div className="space-y-1">
                            {instoreSalesAnalytics.insights.map((item, idx) => (
                              <p key={`is-rec-${idx}`} className="text-sm text-teal-900">- {item}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* POS Customers & Loyalty — micro analysis */}
                  <div className="mt-5 rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50/70 via-white to-violet-50/40 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                      <div>
                        <p className="text-sm font-semibold text-indigo-950 flex items-center gap-2">
                          <FaUserFriends className="text-indigo-600" /> POS Customers &amp; Loyalty
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Mobile-captured loyalty vs classic walk-ins · offer investment · search any POS customer
                        </p>
                        {instoreCustomerAnalytics?.scopeNote ? (
                          <p className="text-[11px] text-indigo-700 mt-1">{instoreCustomerAnalytics.scopeNote}</p>
                        ) : null}
                      </div>
                      <div className="relative w-full sm:w-[280px]">
                        <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Search POS customer</label>
                        <div className="relative">
                          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                          <input
                            type="text"
                            value={instorePosSearch}
                            onChange={(e) => setInstorePosSearch(e.target.value)}
                            placeholder="Mobile or name…"
                            className="w-full rounded-xl border border-indigo-200 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                          />
                        </div>
                        {(loadingInstorePosSearch || instorePosSearchResults.length > 0 || (debouncedInstorePosSearch.trim().length >= 2)) && (
                          <div className="absolute z-20 mt-1 w-full rounded-xl border border-indigo-100 bg-white shadow-lg max-h-64 overflow-auto">
                            {loadingInstorePosSearch ? (
                              <p className="text-xs text-gray-500 px-3 py-2">Searching…</p>
                            ) : instorePosSearchResults.length === 0 ? (
                              <p className="text-xs text-gray-500 px-3 py-2">No POS customers matched.</p>
                            ) : (
                              instorePosSearchResults.map((row) => (
                                <button
                                  key={row.mobile}
                                  type="button"
                                  onClick={() => {
                                    setInstorePosSearch('');
                                    setInstorePosSearchResults([]);
                                    openPosCustomerProfile(row.mobile);
                                  }}
                                  className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-gray-50 last:border-0"
                                >
                                  <p className="text-sm font-semibold text-gray-800">{row.customerName || 'Walk-in'}</p>
                                  <p className="text-[11px] text-gray-500">
                                    {row.mobile} · {row.orderCount} bills · {formatCurrency(row.totalSpend)}
                                    {row.offerRedemptions > 0 ? ` · ${row.offerRedemptions} offers` : ''}
                                  </p>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {loadingInstoreCustomerAnalytics ? (
                      <p className="text-sm text-gray-500 py-4 text-center">Loading loyalty &amp; offer analytics…</p>
                    ) : !instoreCustomerAnalytics?.enabled ? (
                      <p className="text-sm text-gray-500 py-4 text-center">Loyalty analytics unavailable right now.</p>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex flex-nowrap gap-2 overflow-x-auto pb-1">
                          {instoreLoyaltySummaryCards.map((card) => (
                            <div key={card.label} className="min-w-[125px] shrink-0 rounded-xl border border-indigo-100 bg-white/90 px-3 py-2.5">
                              <p className="text-[11px] text-gray-500">{card.label}</p>
                              <p className="text-sm font-bold text-gray-800 mt-0.5">{card.value}</p>
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          <div className="rounded-xl border border-indigo-100 bg-white/90 p-4">
                            <p className="text-sm font-semibold text-gray-800 mb-1">Loyalty vs Classic</p>
                            <p className="text-[11px] text-gray-500 mb-2">Revenue split — classic bills cannot unlock streak/spend loyalty rules.</p>
                            {instoreLoyaltySplitChart.length > 0 ? (
                              <div className="h-48">
                                <ResponsiveContainer width="100%" height="100%">
                                  <PieChart>
                                    <Pie data={instoreLoyaltySplitChart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={(e) => e.name}>
                                      {instoreLoyaltySplitChart.map((entry, idx) => (
                                        <Cell key={`loy-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                      ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500 text-center py-12">No bills in this window.</p>
                            )}
                          </div>

                          <div className="lg:col-span-2 rounded-xl border border-indigo-100 bg-white/90 p-4">
                            <p className="text-sm font-semibold text-gray-800 mb-1">Offer rule mix</p>
                            <p className="text-[11px] text-gray-500 mb-2">Which POS offer types retailers actually redeem.</p>
                            {instoreOfferRuleChart.length > 0 ? (
                              <div className="h-48">
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart data={instoreOfferRuleChart} layout="vertical" margin={{ left: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 10 }} />
                                    <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={110} />
                                    <Tooltip />
                                    <Bar dataKey="Redemptions" fill="#6366f1" radius={[0, 4, 4, 0]} />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500 text-center py-12">No offer redemptions in this window.</p>
                            )}
                          </div>
                        </div>

                        {/* Segments */}
                        <div className="rounded-xl border border-indigo-100 bg-white/90 p-4">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                            <div>
                              <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                <FaHeart className="text-rose-500" /> Loyalty segments
                              </p>
                              <p className="text-[11px] text-gray-500">Click a chip, then open any customer for a 360° bill &amp; offer history.</p>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {Object.entries(POS_LOYALTY_SEGMENT_META).map(([key, meta]) => {
                                const count = instoreCustomerAnalytics?.segments?.counts?.[key] || 0;
                                const active = selectedPosLoyaltySegment === key;
                                const Icon = meta.icon;
                                return (
                                  <button
                                    key={key}
                                    type="button"
                                    title={meta.hint}
                                    onClick={() => setSelectedPosLoyaltySegment(key)}
                                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-full border transition ${
                                      active ? `${meta.chip} ring-2 ring-offset-1 ring-indigo-300` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                    }`}
                                  >
                                    <Icon className="text-[10px]" /> {meta.label}
                                    <span className="opacity-70">{count}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div className="space-y-2 max-h-72 overflow-auto pr-1">
                            {selectedPosSegmentRows.length === 0 ? (
                              <p className="text-xs text-gray-500 py-4 text-center">No customers in this segment for the selected window.</p>
                            ) : (
                              selectedPosSegmentRows.map((row) => (
                                <button
                                  key={row.mobile}
                                  type="button"
                                  onClick={() => openPosCustomerProfile(row.mobile)}
                                  className="w-full flex items-center justify-between gap-2 text-left text-sm bg-indigo-50/40 hover:bg-indigo-50 rounded-lg px-3 py-2 border border-indigo-100 transition"
                                >
                                  <div className="min-w-0">
                                    <p className="font-semibold text-gray-800 line-clamp-1">{row.customerName || 'Walk-in'}</p>
                                    <p className="text-[11px] text-gray-500">
                                      {row.mobile} · {row.orderCount} bills · last {row.daysSinceLastVisit != null ? `${row.daysSinceLastVisit}d` : '—'}
                                      {row.offerRedemptions > 0 ? ` · ${row.offerRedemptions} offers` : ''}
                                    </p>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <p className="font-semibold text-indigo-700">{formatCurrency(row.totalSpend)}</p>
                                    <p className="text-[10px] text-gray-400">avg {formatCurrency(row.avgBill)}</p>
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                        </div>

                        {/* Top customers + Offer performance */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="rounded-xl border border-amber-100 bg-amber-50/30 p-4">
                            <p className="text-sm font-semibold text-amber-900 mb-1 flex items-center gap-2">
                              <FaCrown className="text-amber-500" /> Top loyalty spenders
                            </p>
                            <p className="text-[11px] text-gray-500 mb-2">Highest POS spend this window (mobile-captured only).</p>
                            <div className="space-y-2 max-h-72 overflow-auto pr-1">
                              {(instoreCustomerAnalytics.topCustomers || []).map((row, idx) => (
                                <button
                                  key={row.mobile}
                                  type="button"
                                  onClick={() => openPosCustomerProfile(row.mobile)}
                                  className="w-full flex items-center justify-between gap-2 text-left text-sm bg-white/80 hover:bg-white rounded-lg px-3 py-2 border border-amber-100 transition"
                                >
                                  <div className="min-w-0 flex items-center gap-2">
                                    <span className="text-xs font-bold text-amber-700 w-5 shrink-0">#{idx + 1}</span>
                                    <div className="min-w-0">
                                      <p className="font-semibold text-gray-800 line-clamp-1">{row.customerName}</p>
                                      <p className="text-[11px] text-gray-500">{row.mobile} · {row.orderCount} bills</p>
                                    </div>
                                  </div>
                                  <span className="font-semibold text-amber-800 shrink-0">{formatCurrency(row.totalSpend)}</span>
                                </button>
                              ))}
                              {(instoreCustomerAnalytics.topCustomers || []).length === 0 && (
                                <p className="text-xs text-gray-500">No loyalty customers yet — collect mobiles on Behaviour Payment.</p>
                              )}
                            </div>
                          </div>

                          <div className="rounded-xl border border-violet-100 bg-violet-50/30 p-4">
                            <p className="text-sm font-semibold text-violet-900 mb-1 flex items-center gap-2">
                              <FaGift className="text-violet-600" /> Offer performance
                            </p>
                            <p className="text-[11px] text-gray-500 mb-2">Cash discounts + free-gift worth redeemed per offer.</p>
                            <div className="space-y-2 max-h-72 overflow-auto pr-1">
                              {(instoreCustomerAnalytics.offerPerformance || []).map((row) => (
                                <div key={row.offerId || row.offerName} className="flex items-center justify-between gap-2 text-sm bg-white/80 rounded-lg px-3 py-2 border border-violet-100">
                                  <div className="min-w-0">
                                    <p className="font-semibold text-gray-800 line-clamp-1">{row.offerName}</p>
                                    <p className="text-[11px] text-gray-500">
                                      {row.ruleLabel} · {row.redemptions}× · {row.uniqueCustomers} customers
                                      {row.isActive === false ? ' · inactive' : ''}
                                    </p>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <p className="font-semibold text-violet-800">{formatCurrency(row.totalInvestment)}</p>
                                    <p className="text-[10px] text-gray-400">on {formatCurrency(row.revenueOnOfferBills)} bills</p>
                                  </div>
                                </div>
                              ))}
                              {(instoreCustomerAnalytics.offerPerformance || []).length === 0 && (
                                <p className="text-xs text-gray-500">No POS offers redeemed in this window.</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Free gifts + unused offers */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="rounded-xl border border-emerald-100 bg-emerald-50/30 p-4">
                            <p className="text-sm font-semibold text-emerald-900 mb-1 flex items-center gap-2">
                              <FaGift className="text-emerald-600" /> Free gifts given
                            </p>
                            <p className="text-[11px] text-gray-500 mb-2">Auto-added ₹0 items from Free gift offers — catalog worth gifted away.</p>
                            <div className="space-y-2 max-h-56 overflow-auto pr-1">
                              {(instoreCustomerAnalytics.freeGiftProducts || []).map((row, idx) => (
                                <div key={`${row.productName}-${idx}`} className="flex items-center justify-between gap-2 text-sm bg-white/80 rounded-lg px-3 py-2 border border-emerald-100">
                                  <div className="min-w-0">
                                    <p className="font-semibold text-gray-800 line-clamp-1">{row.productName}</p>
                                    <p className="text-[11px] text-gray-500">{safeOptionLabel(row.optionLabel) || '—'} · {row.bills} bills</p>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <p className="font-semibold text-emerald-800">{row.units} free</p>
                                    <p className="text-[10px] text-gray-400">worth {formatCurrency(row.catalogWorth)}</p>
                                  </div>
                                </div>
                              ))}
                              {(instoreCustomerAnalytics.freeGiftProducts || []).length === 0 && (
                                <p className="text-xs text-gray-500">No free gift items given this window.</p>
                              )}
                            </div>
                          </div>

                          <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-4">
                            <p className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-2">
                              <FaBell className="text-slate-500" /> Active offers with zero redemptions
                            </p>
                            <p className="text-[11px] text-gray-500 mb-2">Configured but unused — review thresholds or cashier mobile capture.</p>
                            <div className="space-y-2 max-h-56 overflow-auto pr-1">
                              {(instoreCustomerAnalytics.unusedActiveOffers || []).map((row) => (
                                <div key={row.offerId} className="flex items-center justify-between gap-2 text-sm bg-white/80 rounded-lg px-3 py-2 border border-slate-200">
                                  <div className="min-w-0">
                                    <p className="font-semibold text-gray-800 line-clamp-1">{row.offerName}</p>
                                    <p className="text-[11px] text-gray-500">{row.ruleLabel} · priority {row.priority}</p>
                                  </div>
                                  <span className="text-[10px] font-bold uppercase text-slate-500 shrink-0">0 uses</span>
                                </div>
                              ))}
                              {(instoreCustomerAnalytics.unusedActiveOffers || []).length === 0 && (
                                <p className="text-xs text-gray-500">Every active offer got at least one redemption — nice.</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {(instoreCustomerAnalytics.insights || []).length > 0 && (
                          <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4">
                            <p className="text-sm font-semibold text-indigo-950 mb-2">Loyalty &amp; Offers Playbook</p>
                            <div className="space-y-1">
                              {instoreCustomerAnalytics.insights.map((item, idx) => (
                                <p key={`loy-rec-${idx}`} className="text-sm text-indigo-950">- {item}</p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {notifiedDetailCustomer && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
          onClick={() => setNotifiedDetailCustomer(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-rose-600 to-orange-500">
              <div className="flex items-start justify-between gap-3">
                <div className="text-white min-w-0">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <FaBell /> Notified · Not Redeemed
                  </h3>
                  <p className="text-sm text-white/95 mt-1 font-semibold truncate">
                    {notifiedDetailCustomer.customerName || 'Customer'}
                  </p>
                  <p className="text-xs text-white/80 mt-0.5">
                    {notifiedDetailCustomer.mobile || notifiedDetailCustomer.email || 'No contact on file'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setNotifiedDetailCustomer(null)}
                  className="text-white/90 hover:text-white shrink-0"
                  title="Close"
                >
                  <FaTimes size={16} />
                </button>
              </div>
            </div>

            <div className="p-5 overflow-auto space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-rose-100 bg-rose-50/40 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-rose-500 font-semibold">Wallet Balance</p>
                  <p className="text-xl font-bold text-rose-700 mt-1">{formatCurrency(notifiedDetailCustomer.walletBalance || 0)}</p>
                  {notifiedDetailCustomer.nearestExpiry ? (
                    <p className="text-[10px] text-orange-600 mt-0.5">Expires {formatDate(notifiedDetailCustomer.nearestExpiry)}</p>
                  ) : (
                    <p className="text-[10px] text-gray-500 mt-0.5">No expiry set</p>
                  )}
                </div>
                <div className="rounded-xl border border-violet-100 bg-violet-50/40 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-violet-500 font-semibold">Alerts Sent</p>
                  <p className="text-xl font-bold text-violet-700 mt-1">{Number(notifiedDetailCustomer.notificationsSent || 0).toLocaleString('en-IN')}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {Number(notifiedDetailCustomer.pushDelivered || 0).toLocaleString('en-IN')} pushed ·{' '}
                    {Math.max(0, Number(notifiedDetailCustomer.notificationsSent || 0) - Number(notifiedDetailCustomer.pushDelivered || 0)).toLocaleString('en-IN')} failed
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 space-y-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-600">Mobile</span>
                  <span className="font-semibold text-gray-800 flex items-center gap-2">
                    {notifiedDetailCustomer.mobile || '—'}
                    {notifiedDetailCustomer.mobile ? (
                      <button
                        type="button"
                        onClick={() => copyToClipboard(notifiedDetailCustomer.mobile)}
                        className="text-gray-400 hover:text-indigo-600"
                        title="Copy mobile number"
                      >
                        <FaCopy size={12} />
                      </button>
                    ) : null}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-600">Email</span>
                  <span className="font-semibold text-gray-800 truncate max-w-[60%]">{notifiedDetailCustomer.email || '—'}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-600">In-app reads</span>
                  <span className="font-semibold text-gray-800">
                    {Number(notifiedDetailCustomer.readCount || 0) > 0
                      ? `${Number(notifiedDetailCustomer.readCount).toLocaleString('en-IN')} of ${Number(notifiedDetailCustomer.notificationsSent || 0).toLocaleString('en-IN')} opened`
                      : 'Never opened'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-600">First alert</span>
                  <span className="font-semibold text-gray-800">{notifiedDetailCustomer.firstNotifiedAt ? formatDate(notifiedDetailCustomer.firstNotifiedAt) : '—'}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-600">Last alert</span>
                  <span className="font-semibold text-gray-800">
                    {notifiedDetailCustomer.lastNotifiedAt ? formatDate(notifiedDetailCustomer.lastNotifiedAt) : '—'}
                    {notifiedDetailCustomer.daysSinceNotified != null ? (
                      <span className="text-[10px] text-gray-500 font-normal">
                        {' '}({Number(notifiedDetailCustomer.daysSinceNotified) === 0 ? 'today' : `${notifiedDetailCustomer.daysSinceNotified}d ago`})
                      </span>
                    ) : null}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-600">Last Free Cash use</span>
                  <span className="font-semibold text-gray-800">
                    {notifiedDetailCustomer.lastRedeemedAt ? formatDate(notifiedDetailCustomer.lastRedeemedAt) : 'Never redeemed'}
                  </span>
                </div>
              </div>

              {(notifiedDetailCustomer.events || []).length > 0 ? (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1.5">Alert types received</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(notifiedDetailCustomer.events || []).map((evt) => (
                      <span key={evt} className="rounded-full bg-violet-50 border border-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 capitalize">
                        {String(evt).replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className={`rounded-xl border p-3 text-xs ${notifiedDetailCustomer.redeemedBeforeNotify ? 'border-amber-100 bg-amber-50/60 text-amber-800' : 'border-rose-100 bg-rose-50/50 text-rose-700'}`}>
                {notifiedDetailCustomer.redeemedBeforeNotify
                  ? 'This customer used Free Cash earlier in the window, but not after the latest alert — the reminder itself did not convert.'
                  : 'This customer has not redeemed any Free Cash after receiving the alert.'}
                {Number(notifiedDetailCustomer.walletBalance || 0) > 0
                  ? ` ${formatCurrency(notifiedDetailCustomer.walletBalance)} is still sitting in their wallet, so a follow-up nudge or a lower min-cart rule is worth trying.`
                  : ' Their wallet is empty right now, so load fresh Free Cash before nudging again.'}
              </div>
            </div>

            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setNotifiedDetailCustomer(null)}
                className="px-4 py-2 rounded-lg bg-gray-800 text-white text-sm font-semibold hover:bg-gray-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {posCustomerProfileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
          onClick={() => setPosCustomerProfileOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500">
              <div className="flex items-start justify-between gap-3">
                <div className="text-white min-w-0">
                  <h3 className="text-lg font-bold flex items-center gap-2 text-gray-100">
                    <FaMobileAlt /> POS Customer 360
                  </h3>
                  {posCustomerProfile ? (
                    <>
                      <p className="text-sm text-white/95 mt-1 font-semibold truncate">
                        {posCustomerProfile.customerName || 'Walk-in'} · {posCustomerProfile.mobile}
                      </p>
                      <p className="text-xs text-white/75 mt-0.5 capitalize">
                        Segment: {(posCustomerProfile.segment || '').replace(/_/g, ' ')} · all-time POS history
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-white/80 mt-1">Loading profile…</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setPosCustomerProfileOpen(false)}
                  className="text-white/90 hover:text-white p-2 rounded-lg hover:bg-white/10"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-5 space-y-4">
              {loadingPosCustomerProfile ? (
                <p className="text-sm text-gray-500 text-center py-10">Loading POS customer profile…</p>
              ) : !posCustomerProfile ? (
                <p className="text-sm text-gray-500 text-center py-10">No profile data found.</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { label: 'Bills', value: posCustomerProfile.summary?.orderCount },
                      { label: 'Total spend', value: formatCurrency(posCustomerProfile.summary?.totalSpend || 0) },
                      { label: 'Avg bill', value: formatCurrency(posCustomerProfile.summary?.avgBill || 0) },
                      { label: 'Visit days', value: posCustomerProfile.summary?.distinctVisitDays },
                      { label: 'Offers used', value: posCustomerProfile.summary?.offerRedemptions },
                      { label: 'Cash discount', value: formatCurrency(posCustomerProfile.summary?.offerDiscount || 0) },
                      { label: 'Free gifts', value: formatCurrency(posCustomerProfile.summary?.freeItemsWorth || 0) },
                      { label: 'Preferred pay', value: posCustomerProfile.summary?.preferredMethod || '—' },
                    ].map((card) => (
                      <div key={card.label} className="rounded-xl border border-indigo-100 bg-indigo-50/40 px-3 py-2">
                        <p className="text-[11px] text-gray-500">{card.label}</p>
                        <p className="text-sm font-bold text-gray-800 mt-0.5">{card.value ?? '—'}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-500">
                    First visit {formatDate(posCustomerProfile.summary?.firstOrderAt)} · Last visit {formatDate(posCustomerProfile.summary?.lastOrderAt)}
                    {posCustomerProfile.summary?.daysSinceLastVisit != null
                      ? ` · ${posCustomerProfile.summary.daysSinceLastVisit}d ago`
                      : ''}
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-gray-100 p-3">
                      <p className="text-sm font-semibold text-gray-800 mb-2">Favourite products</p>
                      <div className="space-y-1.5 max-h-48 overflow-auto">
                        {(posCustomerProfile.favouriteProducts || []).map((p) => (
                          <div key={p.productId} className="flex justify-between gap-2 text-sm">
                            <span className="text-gray-800 line-clamp-1">{p.productName}</span>
                            <span className="text-gray-500 shrink-0">{p.units}u · {formatCurrency(p.revenue)}</span>
                          </div>
                        ))}
                        {(posCustomerProfile.favouriteProducts || []).length === 0 && (
                          <p className="text-xs text-gray-500">No product history.</p>
                        )}
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-100 p-3">
                      <p className="text-sm font-semibold text-gray-800 mb-2">Offers redeemed</p>
                      <div className="space-y-1.5 max-h-48 overflow-auto">
                        {(posCustomerProfile.offerHistory || []).map((o) => (
                          <div key={o.offerId || o.offerName} className="flex justify-between gap-2 text-sm">
                            <div className="min-w-0">
                              <p className="text-gray-800 line-clamp-1 font-medium">{o.offerName}</p>
                              <p className="text-[11px] text-gray-500">{o.ruleLabel || 'Offer'} · {o.redemptions}×</p>
                            </div>
                            <span className="text-violet-700 shrink-0 font-semibold">
                              {formatCurrency((o.cashDiscount || 0) + (o.freeItemsWorth || 0))}
                            </span>
                          </div>
                        ))}
                        {(posCustomerProfile.offerHistory || []).length === 0 && (
                          <p className="text-xs text-gray-500">No offers redeemed yet.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-100 p-3">
                    <p className="text-sm font-semibold text-gray-800 mb-2">Recent POS bills</p>
                    <div className="space-y-2 max-h-80 overflow-auto">
                      {(posCustomerProfile.recentBills || []).map((bill) => (
                        <div key={bill.walkInOrderId || bill.orderPlacedOn} className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold text-gray-800">{bill.walkInOrderId || 'Bill'}</p>
                              <p className="text-[11px] text-gray-500">
                                {formatDate(bill.orderPlacedOn)} · {bill.method}
                                {bill.branchName ? ` · ${bill.branchName}` : ''} · {bill.itemCount} items
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-gray-800">{formatCurrency(bill.amount)}</p>
                              {(bill.offerDiscount > 0 || bill.freeItemsWorth > 0) && (
                                <p className="text-[11px] text-violet-700">
                                  {bill.offerName || 'Offer'}
                                  {bill.offerDiscount > 0 ? ` · −${formatCurrency(bill.offerDiscount)}` : ''}
                                  {bill.freeItemsWorth > 0 ? ` · free ${formatCurrency(bill.freeItemsWorth)}` : ''}
                                </p>
                              )}
                            </div>
                          </div>
                          {(bill.freeItems || []).length > 0 && (
                            <p className="text-[11px] text-emerald-700 mt-1">
                              Free: {bill.freeItems.map((fi) => {
                                const label = safeOptionLabel(fi.optionLabel);
                                return `${fi.productName}${label ? ` (${label})` : ''} ×${fi.quantity}`;
                              }).join(', ')}
                            </p>
                          )}
                        </div>
                      ))}
                      {(posCustomerProfile.recentBills || []).length === 0 && (
                        <p className="text-xs text-gray-500">No bills found.</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {deadStockModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
          onClick={() => setDeadStockModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-[90%] max-h-[88vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-rose-500 via-rose-500 to-orange-500">
              <div className="flex items-start justify-between gap-3">
                <div className="text-white">
                  <h3 className="text-lg font-bold flex items-center gap-2"><FaExclamationTriangle /> Dead Stock — Detailed View</h3>
                  <p className="text-xs text-white/80 mt-0.5">
                    Products sitting in stock with zero in-store (POS) sales · {instoreSalesAnalytics?.branchScope || 'All POS Branches'} · {getWindowLabel(selectedWindow)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setDeadStockModalOpen(false)}
                  className="shrink-0 text-white/80 hover:text-white bg-white/15 hover:bg-white/25 rounded-lg p-2 transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
              {/* Summary chips */}
              <div className="flex flex-wrap gap-2 mt-3">
                <div className="bg-white/15 backdrop-blur rounded-lg px-3 py-1.5">
                  <p className="text-[10px] text-white/70 uppercase tracking-wide">Products</p>
                  <p className="text-sm font-bold text-white">{Number(instoreSalesAnalytics?.deadStockCount || 0).toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-white/15 backdrop-blur rounded-lg px-3 py-1.5">
                  <p className="text-[10px] text-white/70 uppercase tracking-wide">Units Stuck</p>
                  <p className="text-sm font-bold text-white">{Number(instoreSalesAnalytics?.deadStockUnits || 0).toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-white/15 backdrop-blur rounded-lg px-3 py-1.5">
                  <p className="text-[10px] text-white/70 uppercase tracking-wide">Est. Capital Stuck</p>
                  <p className="text-sm font-bold text-white">{formatCurrency(instoreSalesAnalytics?.deadStockValue || 0)}</p>
                </div>
              </div>
            </div>

            {/* Search */}
            <div className="px-6 py-3 border-b border-gray-100 bg-gray-50/50">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  value={deadStockSearch}
                  onChange={(e) => setDeadStockSearch(e.target.value)}
                  placeholder="Search by product, SKU or category..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-300"
                />
              </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto px-6 py-3">
              {deadStockFiltered.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-16">No dead stock matches your search.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="text-left text-gray-500 sticky -top-4 bg-white">
                    <tr className="border-b border-gray-100">
                      <th className="py-2 pr-3 font-semibold">#</th>
                      <th className="py-2 pr-3 font-semibold">Product</th>
                      <th className="py-2 px-3 font-semibold">Category</th>
                      <th className="py-2 px-3 font-semibold text-right">In Stock</th>
                      <th className="py-2 px-3 font-semibold text-right">Est. Value</th>
                      <th className="py-2 pl-3 font-semibold text-right">POS History</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {deadStockFiltered.map((p, idx) => (
                      <tr key={p.productId || idx} className="hover:bg-rose-50/30">
                        <td className="py-2 pr-3 text-gray-400 font-medium">{idx + 1}</td>
                        <td className="py-2 pr-3">
                          <p className="font-semibold text-gray-800 line-clamp-1">{p.productName}</p>
                          {p.sku ? <p className="text-[11px] text-gray-400">{p.sku}</p> : null}
                        </td>
                        <td className="py-2 px-3 text-gray-600">{p.category}</td>
                        <td className="py-2 px-3 text-right font-semibold text-rose-700">{Number(p.availableQuantity || 0).toLocaleString('en-IN')}</td>
                        <td className="py-2 px-3 text-right text-gray-700">{formatCurrency(p.stockValue || 0)}</td>
                        <td className="py-2 pl-3 text-right">
                          {p.neverSoldInStore ? (
                            <span className="inline-block text-[10px] font-semibold text-rose-700 bg-rose-100 rounded-full px-2 py-0.5">Never sold</span>
                          ) : (
                            <span className="text-[11px] text-gray-500">{Number(p.lifetimePosSold || 0).toLocaleString('en-IN')} sold (lifetime)</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                Showing {deadStockFiltered.length.toLocaleString('en-IN')} of {Number(instoreSalesAnalytics?.deadStockCount || 0).toLocaleString('en-IN')} dead-stock products
              </p>
              <button
                type="button"
                onClick={() => setDeadStockModalOpen(false)}
                className="text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-xl px-4 py-2 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {fullDropOffsModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
          onClick={() => setFullDropOffsModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-rose-50 to-orange-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Full Drop-offs</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  {selectedCustomerBehavior?.customer?.name || 'Customer'}
                  {(selectedCustomerBehavior?.customer?.mobile || selectedCustomerBehavior?.customer?.email)
                    ? ` · ${selectedCustomerBehavior.customer.mobile || selectedCustomerBehavior.customer.email}`
                    : ''}
                  {' · '}{getWindowLabel(selectedWindow)}
                </p>
                <p className="text-xs text-rose-700 mt-1">Products viewed but not purchased in this window</p>
              </div>
              <button
                type="button"
                onClick={() => setFullDropOffsModalOpen(false)}
                className="w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-gray-800 flex items-center justify-center shrink-0"
                aria-label="Close full drop-offs"
              >
                <FaTimes size={14} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loadingFullDropOffs ? (
                <p className="text-sm text-gray-500 text-center py-10">Loading full drop-offs...</p>
              ) : fullDropOffItems.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-10">No drop-off products found in the selected window.</p>
              ) : (
                <div className="space-y-3">
                  {fullDropOffItems.map((item, idx) => (
                    <div
                      key={`${item.productId || idx}`}
                      className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100 last:border-b-0 last:pb-0"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-800 line-clamp-2">{item.productName || 'Unknown Product'}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Views: <span className="font-medium text-gray-700">{item.views || 0}</span>
                          {' · '}Cart adds: <span className="font-medium text-gray-700">{item.cartAdditions || 0}</span>
                          {' · '}Purchases: <span className="font-medium text-gray-700">{item.purchases || 0}</span>
                        </p>
                        {item.lastSeenAt ? (
                          <p className="text-[10px] text-gray-400 mt-0.5">Last viewed: {formatDate(item.lastSeenAt)}</p>
                        ) : null}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-bold text-rose-600">{item.nonConvertedViews || 0}</p>
                        <p className="text-[10px] uppercase tracking-wider text-rose-500 font-semibold">Drop-offs</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
              <span>
                {loadingFullDropOffs
                  ? 'Fetching drop-offs...'
                  : `Showing ${fullDropOffItems.length} product${fullDropOffItems.length === 1 ? '' : 's'}`}
              </span>
              <span>Up to 200 products in window</span>
            </div>
          </div>
        </div>
      )}

      <HourlyPulseDetailModal
        open={selectedHourlyPulseHour != null}
        detail={selectedHourlyPulseDetail}
        windowLabel={getWindowLabel(selectedWindow)}
        onClose={() => setSelectedHourlyPulseHour(null)}
        onCopyMobile={copyToClipboard}
        onCopyAllMobiles={copyHourlyPulseMobiles}
      />

      <ProductHealthDetailModal
        product={selectedHealthProductDetail}
        windowLabel={getWindowLabel(selectedWindow)}
        onClose={() => setSelectedHealthProductDetail(null)}
      />

      {fullTimelineModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
          onClick={() => setFullTimelineModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-violet-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Full Event Timeline</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  {selectedCustomerBehavior?.customer?.name || 'Customer'}
                  {(selectedCustomerBehavior?.customer?.mobile || selectedCustomerBehavior?.customer?.email)
                    ? ` · ${selectedCustomerBehavior.customer.mobile || selectedCustomerBehavior.customer.email}`
                    : ''}
                  {' · '}{getWindowLabel(selectedWindow)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFullTimelineModalOpen(false)}
                className="w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-gray-800 flex items-center justify-center shrink-0"
                aria-label="Close full timeline"
              >
                <FaTimes size={14} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loadingFullTimeline ? (
                <p className="text-sm text-gray-500 text-center py-10">Loading full timeline...</p>
              ) : fullTimelineEvents.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-10">No events found in the selected window.</p>
              ) : (
                <div className="space-y-3">
                  {fullTimelineEvents.map((event, idx) => {
                    const presentation = getTimelineEventPresentation(event);
                    return (
                      <div
                        key={`${event.timestamp}-${event.eventType}-${idx}`}
                        className="flex gap-3 pb-3 border-b border-gray-100 last:border-b-0 last:pb-0"
                      >
                        <div className={`w-2 h-2 rounded-full ${presentation.dotClass} mt-2 shrink-0`} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-800">
                            <span className={`font-semibold ${presentation.titleClass}`}>{presentation.title}</span>
                            {presentation.detail ? (
                              <span className="text-gray-700"> · {presentation.detail}</span>
                            ) : null}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">{formatDate(event.timestamp)}</p>
                          {event.sessionId ? (
                            <p className="text-[10px] text-gray-400 mt-0.5 truncate">Session: {event.sessionId}</p>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
              <span>
                {loadingFullTimeline
                  ? 'Fetching events...'
                  : `Showing ${fullTimelineEvents.length} event${fullTimelineEvents.length === 1 ? '' : 's'}`}
              </span>
              <span>Up to 200 events in window</span>
            </div>
          </div>
        </div>
      )}

      <LiveActiveSessionsModal
        open={livePresenceModalOpen}
        sessions={modalPresenceSessions}
        onClose={() => setLivePresenceModalOpen(false)}
        onCopyMobile={(mobile) => copyToClipboard(mobile)}
        onRefresh={fetchModalPresence}
        loading={loadingLivePresence}
        historyLoading={loadingTodaySessionHistory}
        historySessions={todaySessionHistory}
        historyLastUpdatedAt={todaySessionHistoryUpdatedAt}
        onHistoryRefresh={fetchTodaySessionHistory}
      />

      {toast.visible && (
        <div className="fixed bottom-6 right-6 bg-black text-white px-4 py-2 rounded shadow-lg z-50">
          {toast.message}
        </div>
      )}

    </div>
  );
};

export default Customers;
